/**
 * Bộ quy tắc ẩn / hiện quy trình thực chiến theo đặc thù cơ sở (Sheet 1 Chẩn đoán)
 * Giúp quầy chỉ hiển thị đúng những quy trình thực tế họ cần, loại bỏ quy trình rườm rà không áp dụng.
 * @param {string} code - Mã quy trình (QT-01 -> QT-26)
 * @param {object} config - Cấu hình chẩn đoán cơ sở
 * @returns {boolean} true nếu quy trình nên ẩn
 */
export const isSopHiddenByConfig = (code, config = {}) => {
  if (!config) return false;

  const { staffCount, ownerPresence, shiftCount, hasInternalSystem } = config;

  // 1. Quầy không có hệ thống quản lý nội bộ / 1 người / chủ tự bán một mình
  // Cơ sở có từ 3 người trở lên (3-5, 6-10, >10) luôn kích hoạt hệ thống nhân sự nội bộ
  const hasMultipleStaff = ['3-5', '6-10', '>10'].includes(staffCount);
  const isSoloOrNoInternal =
    hasInternalSystem === false ||
    hasInternalSystem === 'no' ||
    staffCount === '1' ||
    (!hasMultipleStaff && ownerPresence === 'chu-tu-ban');

  if (isSoloOrNoInternal) {
    // Ẩn toàn bộ Dòng Người: QT-20 (Tuyển dụng), QT-21 (Kèm việc), QT-22 (Giao việc), QT-23 (Xử lý vi phạm/nghỉ việc)
    if (['QT-20', 'QT-21', 'QT-22', 'QT-23'].includes(code)) return true;
    // Ẩn Bàn giao ca (QT-10) vì không có người/ca khác để giao nhận
    if (code === 'QT-10') return true;
    // Ẩn Kiểm quỹ & chống thất thoát nội bộ (QT-11) vì chủ tự thu chi
    if (code === 'QT-11') return true;
  }

  // 2. Quầy chỉ hoạt động 1 ca / ngày -> Ẩn Bàn giao ca (QT-10)
  if (shiftCount === '1' && code === 'QT-10') {
    return true;
  }

  // 3. Quầy nhỏ 2 người (chủ + 1 nhân viên): chưa cần quy trình tuyển dụng quy mô 8 câu (QT-20)
  if (staffCount === '2') {
    if (code === 'QT-20') return true;
    if (ownerPresence === 'chu-cung-nhan-vien' && code === 'QT-22') return true;
  }

  // 4. Chủ luôn có mặt bán cùng nhân viên hàng ngày -> Ẩn QT-22 (Giao việc từ xa khi chủ vắng mặt)
  if (ownerPresence === 'chu-cung-nhan-vien' && code === 'QT-22') {
    return true;
  }

  return false;
};

export const PHARMACY_DIAGNOSTIC_QUESTIONS = [
  {
    id: 'pharmacyName',
    number: 1,
    title: 'Tên quầy / Nhà thuốc của bạn là gì?',
    type: 'text',
    placeholder: 'Nhập tên nhà thuốc / quầy thuốc của bạn (Ví dụ: Nhà Thuốc An Tâm, Quầy Thuốc Số 12...)',
    impact: 'Tự động in trang trọng lên băng tiêu đề và chân trang của mọi quy trình xuất bản & bảng dán tường.',
  },
  {
    id: 'pharmacyType',
    number: 2,
    title: 'Loại hình cơ sở kinh doanh',
    type: 'select',
    impact: 'Kích hoạt các điều khoản cảnh báo phạm vi kinh doanh thuốc kê đơn & danh mục giới hạn trong QT-02 và QT-14.',
    options: [
      { label: 'Nhà thuốc (Phạm vi kinh doanh đầy đủ)', value: 'nha-thuoc' },
      { label: 'Quầy thuốc (Giới hạn một số nhóm thuốc kê đơn đặc thù)', value: 'quay-thuoc' },
    ],
  },
  {
    id: 'staffCount',
    number: 3,
    title: 'Cơ sở của bạn có bao nhiêu người làm việc?',
    type: 'select',
    impact: 'Quầy 1 người: tự động ẩn QT-10 (Bàn giao ca), QT-11 (Kiểm quỹ nội bộ) và QT-20 đến QT-23 (Dòng người); mỗi bước chỉ hiển thị một vai duy nhất. Từ 3 người trở lên: mở rộng toàn diện nhóm quản trị nhân sự.',
    options: [
      { label: '1 người (Chủ tự làm một mình - Không có hệ thống nội bộ)', value: '1' },
      { label: '2 người (Chủ + 1 nhân viên - Chưa cần hệ thống nội bộ phức tạp)', value: '2' },
      { label: '3 - 5 người (Có nhân sự & hệ thống quản lý nội bộ)', value: '3-5' },
      { label: '6 - 10 người (Quy mô vừa, có phân quyền)', value: '6-10' },
      { label: 'Trên 10 người (Chuỗi / Quy mô lớn)', value: '>10' },
    ],
  },
  {
    id: 'ownerPresence',
    number: 4,
    title: 'Chủ quầy có thường xuyên đứng bán không?',
    type: 'select',
    impact: 'Quyết định giọng văn (tự nhắc mình hay giao việc cho nhân viên), sinh/ẩn QT-11 & QT-22, và xác định đối tượng nhận báo cáo khi có sự cố.',
    options: [
      { label: 'Chủ tự bán một mình toàn thời gian (Không có nhân viên)', value: 'chu-tu-ban' },
      { label: 'Chủ bán cùng nhân viên hàng ngày', value: 'chu-cung-nhan-vien' },
      { label: 'Chủ thỉnh thoảng có mặt (vài buổi/tuần)', value: 'chu-thinh-thoang' },
      { label: 'Chủ không đứng quầy, có quản lý điều hành', value: 'chu-vang-mat' },
    ],
  },
  {
    id: 'shiftCount',
    number: 5,
    title: 'Cơ sở chia làm mấy ca mỗi ngày?',
    type: 'select',
    impact: 'Xác định việc sinh/ẩn QT-10 (Bàn giao ca). Phân tách rõ checklist mở ca đầu ngày và nhận ca giữa ngày trong QT-08.',
    options: [
      { label: '1 ca liên tục (Sáng đến tối)', value: '1' },
      { label: '2 ca (Ca sáng & Ca chiều/tối)', value: '2' },
      { label: '3 ca hoặc trực 24/24', value: '3' },
    ],
  },
  {
    id: 'hasSoftware',
    number: 6,
    title: 'Cơ sở có đang dùng phần mềm quản lý nhà thuốc không?',
    type: 'select',
    impact: 'Chuyển đổi các bước nhập máy của QT-01, QT-03, QT-04, QT-12 sang phương thức ghi chép sổ tay tối thiểu mà vẫn kiểm tra & truy xuất số lô được.',
    options: [
      { label: 'Đã có phần mềm quản lý nhà thuốc', value: 'co' },
      { label: 'Chưa có phần mềm (Đang ghi sổ / Excel thủ công)', value: 'chua' },
    ],
  },
];

export const PHARMACY_FLOWS = [
  {
    id: 'flow-hang',
    name: 'Dòng Hàng',
    icon: 'InboxOutlined',
    color: '#0284c7',
    badgeColor: 'blue',
    desc: 'Hàng vào cửa, lên kệ, ra khỏi quầy',
    qtCodes: ['QT-01', 'QT-02', 'QT-03', 'QT-04', 'QT-05', 'QT-06', 'QT-07'],
  },
  {
    id: 'flow-tien',
    name: 'Dòng Tiền',
    icon: 'DollarCircleOutlined',
    color: '#16a34a',
    badgeColor: 'green',
    desc: 'Mở ca, đóng ca, kiểm quỹ, chống thất thoát',
    qtCodes: ['QT-08', 'QT-09', 'QT-10', 'QT-11', 'QT-12'],
  },
  {
    id: 'flow-khach',
    name: 'Dòng Khách',
    icon: 'SmileOutlined',
    color: '#d97706',
    badgeColor: 'gold',
    desc: 'Từ lúc khách bước vào đến lúc quay lại lần sau',
    qtCodes: ['QT-13', 'QT-14', 'QT-15', 'QT-16', 'QT-17', 'QT-18', 'QT-19'],
  },
  {
    id: 'flow-nguoi',
    name: 'Dòng Người',
    icon: 'TeamOutlined',
    color: '#7c3aed',
    badgeColor: 'purple',
    desc: 'Tuyển, kèm cặp, giao việc, xử lý sai phạm',
    qtCodes: ['QT-20', 'QT-21', 'QT-22', 'QT-23'],
  },
  {
    id: 'flow-tinhhuong',
    name: 'Tình Huống Bất Ngờ',
    icon: 'ThunderboltOutlined',
    color: '#dc2626',
    badgeColor: 'red',
    desc: 'Lúc cần nhất thì không ai nhớ phải làm gì',
    qtCodes: ['QT-24', 'QT-25', 'QT-26'],
  },
];

export const PHARMACY_ROLLOUT_PHASES = [
  {
    phase: 1,
    name: 'Đợt 1 - Bản chạy thử cốt lõi',
    tagColor: 'green',
    desc: 'Sáu quy trình phủ đúng những gì quầy đụng vào MỖI NGÀY + 2 tình huống lo nhất (tình huống khó & đoàn kiểm tra). Dùng chạy thử tại 20-30 nhà thuốc.',
    qtCodes: ['QT-01', 'QT-08', 'QT-09', 'QT-13', 'QT-16', 'QT-24'],
  },
  {
    phase: 2,
    name: 'Đợt 2 - Trọn Dòng Hàng & Dòng Tiền',
    tagColor: 'blue',
    desc: 'Nhóm liên quan trực tiếp tới TIỀN. Gắn chặt với việc nhà thuốc nhập hàng - đòn bẩy thương mại cùng Thế Thảo Pharma.',
    qtCodes: ['QT-02', 'QT-03', 'QT-04', 'QT-05', 'QT-10', 'QT-11'],
  },
  {
    phase: 3,
    name: 'Đợt 3 - Mở rộng toàn diện',
    tagColor: 'purple',
    desc: 'Bổ sung đầy đủ mảng quản trị nhân sự, bán kèm, chăm sóc khách hàng dài ngày, giao thuốc Zalo và các sự cố bất ngờ.',
    qtCodes: ['QT-06', 'QT-07', 'QT-12', 'QT-14', 'QT-15', 'QT-17', 'QT-18', 'QT-19', 'QT-20', 'QT-21', 'QT-22', 'QT-23', 'QT-25', 'QT-26'],
  },
];

export const PHARMACY_SELF_CHECK_QUESTIONS = [
  {
    num: 1,
    question: 'Một nhân viên mới vào làm ba ngày, đọc xong có làm được ngay không? Nếu phải hỏi lại thì bước nào còn thiếu?',
  },
  {
    num: 2,
    question: 'Có đủ ít nhất 3 chi tiết mà chỉ người làm nghề mới biết không? Không đủ thì quy trình này còn đang là lý thuyết.',
  },
  {
    num: 3,
    question: 'Bảng dán tường in ra, đứng cách 1 mét có đọc được không? Có tick được trong lúc tay đang bận không?',
  },
];

export const PHARMACY_SOPS_DATA = [
  {
    "code": "QT-01",
    "title": "NHẬN HÀNG VÀ NHẬP KHO",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 1,
    "phaseName": "Đợt 1 - Bản chạy thử",
    "pages": 5,
    "targetRole": "Người nhận hàng",
    "summary": "24 bước theo 7 mục: trước khi xe tới · khi xe tới chưa ký gì · mở thùng quay video · đối chiếu 6 điểm từng dòng · khi có sai lệch · vào máy · lên kệ.",
    "keyDetail": "Ký nhận rồi ghi thêm dòng CHƯA KIỂM HÀNG BÊN TRONG. Một dòng chữ giữ lại toàn bộ quyền khiếu nại.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — NHẬN HÀNG VÀ NHẬP KHO",
      "subtitle": "In ra, ép plastic, dán ngay chỗ nhận hàng. Mỗi lần nhận hàng tick một lượt.",
      "sections": [
        {
          "title": "KHI XE TỚI — CHƯA KÝ GÌ CẢ",
          "items": [
            "Nhắn nhóm Zalo: nhà cung cấp nào, mấy thùng, mấy giờ, kèm ảnh",
            "Đếm số thùng to thành tiếng, đối chiếu vận đơn",
            "Soi từng thùng: móp · rách · ướt · băng keo có bị bóc dán lại không",
            "BẬT VIDEO — quay vòng quanh thùng, quay rõ tem vận đơn, đọc to ngày giờ",
            "Thùng hỏng: bắt người giao ký xác nhận, hoặc từ chối nhận",
            "Ký nhận và ghi thêm: CHƯA KIỂM HÀNG BÊN TRONG"
          ]
        },
        {
          "title": "MỞ THÙNG — VIDEO KHÔNG TẮT",
          "items": [
            "Đặt bill cạnh thùng, cả hai trong khung hình",
            "Quay từ lúc còn nguyên băng keo · quay liên tục · dốc ngược thùng rỗng cuối cùng",
            "Lấy từng món, đọc to tên và số lượng vào máy quay",
            "Xếp thành hàng theo đúng thứ tự các dòng trên bill"
          ]
        },
        {
          "title": "ĐỐI CHIẾU — TICK TỪNG DÒNG MỘT",
          "items": [
            "Đúng tên và ĐÚNG HÀM LƯỢNG",
            "Đúng quy cách đóng gói (hộp mấy vỉ)",
            "Đúng số lượng — đếm lại, không tin số ghi ngoài thùng",
            "Ghi lại SỐ LÔ và HẠN DÙNG",
            "Cảm quan: móp · rách · bong tem · ẩm · nứt nắp",
            "Giá trên bill khớp giá đã chốt lúc đặt",
            "Hàng lạnh và hàng giá trị cao kiểm TRƯỚC — hàng lạnh vào tủ trong 15 phút"
          ]
        },
        {
          "title": "CÓ SAI LỆCH — BÁO TRONG NGÀY, KHÔNG ĐỂ QUA ĐÊM",
          "items": [
            "Dừng lại, giữ nguyên hiện trạng, không xếp đi đâu cả",
            "Chụp ảnh · khoanh dòng sai trên bill · ghi số thực nhận",
            "Gửi nhóm Zalo và gửi nhà cung cấp NGAY HÔM NAY",
            "Phần sai lệch: chưa nhập vào máy"
          ]
        },
        {
          "title": "VÀO MÁY VÀ LÊN KỆ",
          "items": [
            "Chỉ nhập sau khi đã tick xong toàn bộ bill",
            "Nhập ĐỦ số lô và hạn dùng — không bỏ trống ô hạn dùng",
            "Đối chiếu tổng tiền máy với tổng trên bill",
            "Chụp bill gửi nhóm Zalo · cất bản cứng vào kẹp theo tháng",
            "Kéo hàng cũ RA NGOÀI, hàng mới VÀO TRONG",
            "Lô mới hạn ngắn hơn lô cũ thì lô mới nằm ngoài",
            "Dán giá trước khi lên kệ · dọn thùng carton ngay",
            "Nhắn nhóm Zalo khép việc: đã nhập xong, có hay không có sai lệch"
          ]
        }
      ]
    },
    "sop": {
      "title": "NHẬN HÀNG VÀ NHẬP KHO",
      "duration": "Từ lúc xe hàng dừng trước cửa đến lúc hàng lên kệ đúng chỗ. Khoảng 25–40 phút cho một đơn thường.",
      "lamDung": "Sai lệch nào cũng đòi được nhà cung cấp trong 24 giờ. Hàng cận date không lọt vào kho. Máy tính khớp kệ hàng nên sau này không phải kiểm kê mò.",
      "lamAu": "Thiếu hàng nhưng đã ký nhận thì tự chịu. Nhận nhầm hàng cận date thì ba tháng sau đổ đi. Không nhập số lô thì phần mềm không bao giờ cảnh báo được cho bạn.",
      "aiLam": [
        {
          "role": "Người nhận hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Người được phân công trực hôm đó. Không để người giao tự đưa cho ai đang đứng gần nhất.",
          "desc": "",
          "step": "Làm bước 1 đến 24"
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "Được báo ngay khi hàng về và ngay khi có sai lệch.",
          "step": "Duyệt bước 8, 13"
        },
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": "Quyết định nhận hay không nhận hàng cận date, hàng nghi ngờ chất lượng."
        },
        {
          "role": "Quyết bước 13, 14",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "TRƯỚC KHI XE TỚI",
          "subtitle": "làm trong 3 phút, không bỏ qua",
          "steps": [
            {
              "num": "1",
              "action": "Mở lại đơn đã đặt, biết trước hôm nay có mấy thùng, của nhà cung cấp nào, tổng bao nhiêu tiền.",
              "why": "Không biết trước thì người giao đưa mấy thùng cũng nhận, thiếu một thùng cũng không phát hiện ra.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Dọn sẵn một mặt bàn hoặc một khoảng sàn khô, sạch, đủ rộng để bày hết hàng ra.",
              "why": "Mở thùng ngay lối đi thì vừa vướng khách vừa dễ rơi vỡ, và không bày ra được để đối chiếu.",
              "tip": "Đừng đặt thùng xuống nền ẩm. Hộp giấy hút ẩm rất nhanh, sau này bong tem là mất quyền đổi trả."
            },
            {
              "num": "3",
              "action": "Kiểm tra điện thoại còn pin và còn dung lượng để quay video liên tục 10–15 phút.",
              "why": "Video quay được nửa chừng thì hết pin còn tệ hơn không quay, vì nó cho thấy bạn có quay mà lại thiếu đúng đoạn quan trọng.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI XE TỚI — CHƯA KÝ BẤT CỨ THỨ GÌ",
          "subtitle": "đây là 5 phút quyết định toàn bộ quyền khiếu nại của bạn",
          "steps": [
            {
              "num": "4",
              "action": "Nhắn ngay vào nhóm Zalo của quầy: hàng nhà cung cấp nào về, mấy thùng, mấy giờ. Kèm một tấm ảnh chồng thùng.",
              "why": "Quản lý biết có hàng về là biết phải có kết quả nhập kho trong ngày. Tin nhắn có giờ cũng chính là mốc thời gian để tính hạn khiếu nại.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Đếm số thùng, đối chiếu với số ghi trên vận đơn. Đếm to thành tiếng trước mặt người giao.",
              "why": "Đếm thầm thì khi thiếu, hai bên nhớ khác nhau và không ai chứng minh được.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Soi từng thùng trước mặt người giao: móp, méo, bẹp, rách, ướt, đáy thùng có phồng không; băng keo có dấu bị bóc ra dán lại không; lắc nhẹ nghe có tiếng lạo xạo bất thường không.",
              "why": "Thùng đã ký nhận rồi thì mọi hư hỏng bên ngoài mặc định là do bên nhận. Đây là ranh giới trách nhiệm rõ ràng nhất trong cả quy trình.",
              "tip": "Băng keo bị bóc dán lại là dấu hiệu đáng ngại nhất. Gặp thì chụp cận cảnh trước khi làm bất cứ việc gì khác."
            },
            {
              "num": "7",
              "action": "BẬT VIDEO. Quay một vòng quanh chồng thùng, quay rõ tem vận đơn và mã đơn, quay cận chỗ móp hoặc rách nếu có. Đọc to ngày giờ và tên nhà cung cấp vào máy quay.",
              "why": "Video có ngày giờ đọc ra miệng khó chối cãi hơn ảnh chụp rất nhiều. Nhà cung cấp nào cũng chấp nhận video, nhưng nhiều bên từ chối ảnh rời rạc.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Thùng hỏng nặng hoặc nghi bị mở: yêu cầu người giao ký xác nhận tình trạng ngay trên vận đơn, hoặc từ chối nhận thùng đó. Gọi quản lý trước khi quyết định.",
              "why": "Người giao ký một chữ vào vận đơn là bạn có bằng chứng mạnh nhất mà không tốn gì.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Chỉ ký nhận sau khi đã soi xong bên ngoài. Khi ký, ghi thêm dòng: chưa kiểm hàng bên trong.",
              "why": "Chữ ký trơn nghĩa là bạn xác nhận đã nhận đủ và đúng. Một dòng ghi chú giữ lại cho bạn quyền khiếu nại phần bên trong.",
              "tip": "Người giao thường giục vì còn nhiều điểm. Cứ nói: em soi ngoài 2 phút thôi anh. Không ai từ chối được câu đó."
            }
          ]
        },
        {
          "title": "MỞ THÙNG — VIDEO KHÔNG ĐƯỢC TẮT",
          "subtitle": "quay liên tục một mạch, không cắt, không quay lại",
          "steps": [
            {
              "num": "10",
              "action": "Đặt bill giao hàng lên bàn ngay cạnh thùng, sao cho cả bill và thùng cùng nằm trong khung hình.",
              "why": "Video có bill trong khung thì tự nó chứng minh được thùng nào ứng với đơn nào.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Bật video từ lúc thùng còn nguyên băng keo. Quay liên tục cho tới khi lấy hết hàng ra khỏi thùng và dốc ngược thùng cho thấy đã rỗng.",
              "why": "Video bị cắt giữa chừng gần như không có giá trị khi khiếu nại — bên kia sẽ nói đoạn cắt là lúc bạn lấy hàng ra. Đoạn dốc thùng rỗng là đoạn quan trọng nhất mà hầu hết mọi người quên quay.",
              "tip": "Một tay cầm máy thì rất khó. Kê điện thoại lên giá đỡ hoặc dựa vào hộp, chỉnh khung một lần rồi làm bằng hai tay."
            },
            {
              "num": "12",
              "action": "Lấy từng sản phẩm ra, đọc to tên và số lượng vào máy quay. Xếp thành hàng trên bàn theo đúng thứ tự các dòng trên bill.",
              "why": "Xếp theo thứ tự bill thì bước đối chiếu sau đó nhanh gấp đôi và không bỏ sót dòng nào.",
              "tip": ""
            }
          ]
        },
        {
          "title": "ĐỐI CHIẾU TỪNG DÒNG — TICK ĐẾN ĐÂU CHẮC ĐẾN ĐÓ",
          "subtitle": "chậm ở bước này để không mất tiền ở tháng sau",
          "steps": [
            {
              "num": "13",
              "action": "Với mỗi dòng trên bill, kiểm đủ 6 thứ trong bảng dưới đây rồi mới tick. Tick bằng bút ngay trên bill giấy, tick từng dòng một.",
              "why": "Tick cả cụm cho nhanh là nguyên nhân số một khiến quầy nhận thiếu mà vẫn tưởng đủ.",
              "tip": "Kiểm hàng lạnh và hàng giá trị cao TRƯỚC. Hàng lạnh phải vào tủ trong vòng 15 phút."
            },
            {
              "num": "1",
              "action": "Đúng tên, đúng hàm lượng Cùng một tên thương mại nhưng khác hàm lượng là lỗi phổ biến nhất. Đọc kỹ con số trên hộp, đừng nhìn màu hộp.",
              "why": "",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Đúng quy cách đóng gói Hộp 10 vỉ và hộp 3 vỉ trông rất giống nhau. Giá trên bill tính theo quy cách nào thì nhận đúng quy cách đó.",
              "why": "",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Đúng số lượng Đếm lại, không tin số ghi ngoài thùng. Hàng lẻ vỉ phải đếm từng vỉ.",
              "why": "",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Số lô và hạn dùng GHI LẠI cả hai, kể cả khi thấy hạn còn xa. Không ghi lô thì sau này có thu hồi là không truy được đã bán cho ai.",
              "why": "",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Cảm quan bên ngoài Hộp móp, rách, bong tem, ẩm, ố; vỉ thuốc bị bẹp; chai bị nứt nắp; nhãn dán lệch hoặc có dấu dán đè.",
              "why": "",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Giá trên bill so với giá đã chốt Đây là chỗ mất tiền âm thầm nhất. Giá chốt lúc đặt và giá in trên bill lệch nhau là chuyện xảy ra thường xuyên. NGƯỠNG HẠN DÙNG — DƯỚI MỨC NÀY THÌ KHÔNG NHẬN Loại hàng Hạn còn tối thiểu Nếu dưới ngưỡng thì làm gì Hàng bán nhanh, mỗi tháng hết vài hộp Còn ít nhất 2/3 tuổi thọ Gọi nhà cung cấp ngay khi đang mở thùng, hỏi có đổi lô khác không. Hàng bán chậm, vài tháng mới hết một hộp Còn ít nhất 12 tháng Không nhận. Hoặc chỉ nhận nếu có cam kết đổi trả bằng tin nhắn, không nhận cam kết miệng. Hàng mới, lần đầu nhập về bán thử Còn ít nhất 18 tháng Hàng chưa biết bán được không mà hạn ngắn thì gần như chắc chắn phải đổ đi. Hàng khuyến mãi, hàng tặng kèm Vẫn áp đúng ngưỡng như trên Hàng tặng cũng chiếm chỗ trên kệ và cũng phải huỷ nếu hết hạn. Miễn phí không có nghĩa là không tốn gì.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI CÓ SAI LỆCH",
          "subtitle": "xử lý trong ngày, không để qua đêm",
          "steps": []
        },
        {
          "title": "VÀO MÁY",
          "subtitle": "chỉ làm sau khi đã tick xong toàn bộ bill",
          "steps": [
            {
              "num": "14",
              "action": "Nhập vào phần mềm đúng những gì đã tick. Phần đang có sai lệch thì để lại, chưa nhập.",
              "why": "Nhập trước rồi sửa sau là cách nhanh nhất để số trên máy không bao giờ còn khớp với hàng trên kệ.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Nhập đủ số lô và hạn dùng cho từng mã. Không bỏ trống ô hạn dùng, dù phần mềm cho phép bỏ trống.",
              "why": "Đây là ô quyết định việc sáu tháng nữa phần mềm có tự cảnh báo hàng cận date cho bạn hay không. Bỏ trống một lần là mất luôn khả năng đó với lô hàng ấy.",
              "tip": "Bỏ trống ô hạn dùng là lỗi tốn tiền nhất trong cả quy trình này, mà lại là lỗi ít ai để ý nhất."
            },
            {
              "num": "16",
              "action": "Nhập đúng giá nhập thực tế, kể cả hàng khuyến mãi và hàng tặng. Hàng tặng ghi theo quy ước của quầy, không bịa một con số cho tròn.",
              "why": "Giá nhập sai thì mọi con số lãi lỗ sau này đều sai theo, và bạn sẽ định giá bán dựa trên một nền móng lệch.",
              "tip": ""
            },
            {
              "num": "17",
              "action": "Đối chiếu tổng tiền phần mềm tính ra với tổng trên bill. Lệch một đồng cũng phải tìm ra ở dòng nào.",
              "why": "Lệch tổng nghĩa là có một dòng nhập sai số lượng hoặc sai giá. Tìm ngay lúc này mất 2 phút, tìm sau một tháng mất cả buổi.",
              "tip": ""
            },
            {
              "num": "18",
              "action": "Chụp bill đã tick đầy đủ, gửi vào nhóm Zalo. Cất bản cứng vào kẹp hồ sơ theo từng tháng.",
              "why": "Ảnh trên nhóm là bản tra cứu nhanh, bản cứng là bản đối soát công nợ cuối tháng. Thiếu một trong hai là thiếu.",
              "tip": ""
            }
          ]
        },
        {
          "title": "LÊN KỆ",
          "subtitle": "hàng nào hạn gần hơn thì nằm ngoài — không có ngoại lệ",
          "steps": [
            {
              "num": "19",
              "action": "Hàng lạnh vào tủ trước tiên, trong vòng 15 phút kể từ khi mở thùng.",
              "why": "Thời gian nằm ngoài tủ cộng dồn lại qua nhiều lần nhập là thứ không ai đo được nhưng vẫn làm hỏng thuốc.",
              "tip": ""
            },
            {
              "num": "20",
              "action": "Kéo hàng đang có trên kệ RA PHÍA NGOÀI, đặt hàng mới VÀO PHÍA TRONG. Không đổ chồng hàng mới lên trên.",
              "why": "Đổ chồng lên là cách chắc chắn nhất để lô cũ nằm lại trong cùng cho tới ngày hết hạn.",
              "tip": ""
            },
            {
              "num": "21",
              "action": "Đối chiếu hạn dùng trước khi xếp. Nếu lô MỚI có hạn dùng NGẮN HƠN lô đang có trên kệ thì lô mới phải nằm ngoài.",
              "why": "Nguyên tắc đúng với thuốc là hạn gần bán trước, chứ không phải nhập trước bán trước. Hai cái này thường trùng nhau, nhưng khi lệch thì phải theo hạn dùng.",
              "tip": "Đây là điểm mà rất nhiều quầy làm ngược. Nhập sau chưa chắc đã hạn xa hơn — nhất là hàng khuyến mãi."
            },
            {
              "num": "22",
              "action": "Dán giá và mã lên hàng mới trước khi đưa lên kệ, không để dán sau.",
              "why": "Hàng chưa dán giá nằm trên kệ thì lúc đông khách nhân viên phải hỏi, hoặc tệ hơn là đoán.",
              "tip": ""
            },
            {
              "num": "23",
              "action": "Dọn thùng carton rỗng ngay. Không chất đống ở lối đi hoặc trước tủ hàng.",
              "why": "Thùng chất đống che mất tầm nhìn, cản lối thoát hiểm, và là điểm bị nhắc đầu tiên khi có đoàn kiểm tra vào.",
              "tip": ""
            },
            {
              "num": "24",
              "action": "Nhắn khép lại vào nhóm Zalo: đã nhập xong đơn nào, có hay không có sai lệch, sai lệch đã báo nhà cung cấp chưa.",
              "why": "Một dòng tin nhắn khép việc lại giúp quản lý không phải đi hỏi từng người, và giúp ca sau biết hàng mới đã lên kệ.",
              "tip": ""
            }
          ]
        },
        {
          "title": "LƯU VIDEO BAO LÂU",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Ký nhận trước rồi mới mở kiểm",
          "viSao": "Người giao giục, quầy đang có khách, nghĩ rằng lát nữa kiểm cũng được",
          "caiGia": "Mất toàn bộ quyền khiếu nại. Thiếu bao nhiêu tự chịu bấy nhiêu."
        },
        {
          "loi": "Video quay bị cắt, hoặc chỉ chụp ảnh",
          "viSao": "Điện thoại hết pin, hoặc quay xong đoạn đầu thì bận đi bán hàng",
          "caiGia": "Nhà cung cấp không chấp nhận. Coi như không có bằng chứng gì."
        },
        {
          "loi": "Tick cả cụm cho nhanh thay vì từng dòng",
          "viSao": "Bill dài, hàng quen thuộc, nghĩ rằng chắc không sai",
          "caiGia": "Nhận thiếu mà vẫn tưởng đủ. Phát hiện ra khi đã quá hạn khiếu nại."
        },
        {
          "loi": "Nhập máy bỏ trống số lô và hạn dùng",
          "viSao": "Phần mềm cho phép bỏ qua, và bỏ qua thì nhập nhanh hơn",
          "caiGia": "Mất khả năng cảnh báo cận date. Hàng nằm im tới lúc hết hạn."
        },
        {
          "loi": "Xếp hàng mới ra ngoài cho tiện tay",
          "viSao": "Xếp vào trong phải kéo hết hàng cũ ra, mất thêm 5 phút",
          "caiGia": "Lô cũ nằm lại tới ngày hết hạn. Đây là chỗ tiền rò rỉ đều đặn mỗi tháng mà không ai nhìn thấy."
        }
      ]
    }
  },
  {
    "code": "QT-02",
    "title": "ĐẶT HÀNG",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 2,
    "phaseName": "Đợt 2 - Dòng hàng & tiền",
    "pages": 4,
    "targetRole": "Chủ · quản lý",
    "summary": "Cách tính mức tồn tối thiểu cho từng nhóm · dấu hiệu phải đặt gấp · cách chốt giá qua tin nhắn để có bằng chứng · bẫy trong các deal ôm hàng lấy thưởng.",
    "keyDetail": "Chốt giá bằng tin nhắn chứ không bằng điện thoại. Không có tin nhắn thì lúc bill về giá lệch, bạn không có gì trong tay.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — ĐẶT HÀNG",
      "subtitle": "Dán cạnh máy tính chỗ đặt hàng. Mỗi lần đặt tick một lượt.",
      "sections": [
        {
          "title": "TRƯỚC KHI ĐẶT",
          "items": [
            "Xem báo cáo bán 30 ngày gần nhất, không đặt theo cảm giác",
            "Đi một vòng kệ, ghi mã nào sắp hết mà máy chưa báo",
            "Xem mã nào đang tồn nhiều — đừng đặt thêm",
            "Xem tháng này sắp vào mùa bệnh gì"
          ]
        },
        {
          "title": "KHI CHỐT ĐƠN",
          "items": [
            "CHỐT GIÁ BẰNG TIN NHẮN — không chốt qua điện thoại",
            "Ghi rõ: tên · hàm lượng · quy cách · số lượng · đơn giá · chiết khấu",
            "Hỏi rõ hạn dùng lô sẽ giao, ghi vào tin nhắn",
            "Hỏi rõ hàng khuyến mãi là hàng gì, hạn bao lâu",
            "Chụp lại toàn bộ tin nhắn chốt đơn, lưu vào album đơn hàng"
          ]
        },
        {
          "title": "BẪY CẦN TRÁNH",
          "items": [
            "Deal ôm hàng lấy thưởng: tính xem phải bán bao lâu mới hết",
            "Chiết khấu cao bất thường: hỏi ngay hạn dùng còn bao lâu",
            "Hàng mới lần đầu: đặt số lượng nhỏ nhất có thể",
            "Không đặt thêm mã đang tồn quá 60 ngày bán"
          ]
        },
        {
          "title": "SAU KHI ĐẶT",
          "items": [
            "Ghi vào nhóm Zalo: đã đặt nhà cung cấp nào, dự kiến về ngày nào",
            "Đặt nhắc trên điện thoại đúng ngày hẹn giao",
            "Quá hẹn 1 ngày là gọi hỏi ngay, không đợi"
          ]
        }
      ]
    },
    "sop": {
      "title": "ĐẶT HÀNG",
      "duration": "Từ lúc nhìn kệ thấy sắp hết đến lúc chốt đơn với nhà cung cấp. Làm 1–2 lần mỗi tuần, mỗi lần 20–30 phút.",
      "lamDung": "Không bao giờ hết hàng đúng lúc khách hỏi. Tiền không nằm chết trên kệ. Bill về khớp giá đã chốt vì có tin nhắn làm bằng.",
      "lamAu": "Đặt theo cảm giác thì mã bán chạy hết hàng còn mã bán chậm chất đầy kho. Chốt giá qua điện thoại thì lúc bill lệch giá, bạn không có gì trong tay.",
      "aiLam": [
        {
          "role": "Người đặt hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chủ hoặc quản lý cửa hàng. Không giao cho nhân viên mới, vì đặt sai một lần là ôm hàng nhiều tháng.",
          "desc": "",
          "step": "Làm toàn bộ"
        },
        {
          "role": "Nhân viên bán hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Ghi vào sổ đề nghị mỗi khi khách hỏi mã mà quầy không có.",
          "desc": "",
          "step": "Cung cấp thông tin bước 3"
        }
      ],
      "phases": [
        {
          "title": "BƯỚC CHUẨN BỊ — NHÌN SỐ TRƯỚC KHI NHÌN KỆ",
          "subtitle": "10 phút, làm cố định vào một buổi trong tuần",
          "steps": [
            {
              "num": "1",
              "action": "Mở báo cáo bán hàng 30 ngày gần nhất trên phần mềm. Xuất ra danh sách mã bán chạy nhất và mã không bán được lượt nào.",
              "why": "Trí nhớ luôn phóng đại những mã vừa mới có người hỏi và bỏ quên những mã bán đều mỗi ngày. Số liệu 30 ngày sửa lại cả hai sai lệch đó.",
              "tip": "Nếu chưa có phần mềm thì mở sổ bán hàng và đếm tay trên nhóm 30 mã chủ lực. Chỉ cần 30 mã, không cần cả quầy."
            },
            {
              "num": "2",
              "action": "Đi một vòng kệ với tờ danh sách trong tay. Ghi lại mã nào sắp hết mà máy chưa kịp báo, mã nào tồn nhiều hơn mình tưởng.",
              "why": "Số trên máy và hàng trên kệ luôn lệch nhau ít nhiều. Đi một vòng là cách rẻ nhất để phát hiện lệch trước khi đặt sai.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Mở sổ đề nghị của nhân viên: những mã khách hỏi mà quầy không có. Xem mã nào bị hỏi từ 3 lần trở lên trong tháng.",
              "why": "Mỗi lần khách hỏi mà không có là một lần khách đi sang quầy khác. Hỏi ba lần một tháng nghĩa là mã đó đáng nhập.",
              "tip": "Để một quyển sổ nhỏ ngay cạnh máy tính. Nhân viên ghi trong 5 giây, không cần mở phần mềm."
            },
            {
              "num": "4",
              "action": "Xem tháng tới vào mùa bệnh gì ở khu vực mình. Cộng thêm cho nhóm hàng theo mùa.",
              "why": "Mùa bệnh đến rất nhanh và đi cũng rất nhanh. Đặt hàng lúc đã vào mùa thì nhà cung cấp cũng đã hết hàng.",
              "tip": ""
            }
          ]
        },
        {
          "title": "ĐẶT BAO NHIÊU — CÁCH TÍNH ĐƠN GIẢN NHẤT",
          "subtitle": "",
          "steps": []
        },
        {
          "title": "CHỐT ĐƠN — MỌI THỨ PHẢI NẰM TRONG TIN NHẮN",
          "subtitle": "đây là bước quyết định bạn có đòi được gì khi bill sai hay không",
          "steps": [
            {
              "num": "5",
              "action": "Soạn đơn thành tin nhắn, mỗi dòng ghi đủ: tên thuốc, hàm lượng, quy cách đóng gói, số lượng, đơn giá, chiết khấu.",
              "why": "Thiếu hàm lượng hoặc quy cách là hai chỗ hay giao nhầm nhất. Ghi đủ thì lỗi thuộc về bên giao, ghi thiếu thì lỗi thuộc về bạn.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Chốt giá bằng tin nhắn, không chốt qua điện thoại. Nhà cung cấp báo giá miệng thì nhắn lại: em xác nhận lại đơn giá X nhé anh.",
              "why": "Đây là chi tiết quan trọng nhất của cả quy trình. Bill về lệch giá là chuyện xảy ra thường xuyên, và không có tin nhắn thì bạn không đòi được.",
              "tip": "Chỉ cần một câu nhắn lại xác nhận là đủ. Không ai thấy phiền, và nó cứu bạn nhiều lần trong năm."
            },
            {
              "num": "7",
              "action": "Hỏi rõ hạn dùng của lô sẽ giao, ghi vào tin nhắn. Hỏi cả hạn dùng của hàng khuyến mãi.",
              "why": "Hỏi trước thì họ chọn lô cho bạn. Không hỏi thì họ giao lô nào tiện lấy nhất trong kho của họ — thường là lô cận date.",
              "tip": "Câu hỏi hiệu quả nhất: hàng này lô date bao nhiêu vậy anh. Ngắn, lịch sự, và họ buộc phải trả lời bằng con số."
            },
            {
              "num": "8",
              "action": "Hàng khuyến mãi: hỏi rõ tặng cái gì, bao nhiêu, hạn bao lâu. Đừng nhận tặng gì cũng được.",
              "why": "Hàng tặng cũng chiếm chỗ trên kệ, cũng phải nhập máy, và cũng phải huỷ nếu hết hạn. Miễn phí không có nghĩa là không tốn gì.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Với deal ôm hàng lấy thưởng: trước khi gật, tính xem với tốc độ bán hiện tại thì bao lâu mới hết số hàng đó.",
              "why": "Thưởng doanh số nghe hấp dẫn nhưng phần thưởng thường nhỏ hơn nhiều so với số tiền nằm chết trên kệ trong sáu tháng.",
              "tip": "Quy tắc đơn giản: nếu phải hơn 90 ngày mới bán hết thì từ chối, dù thưởng cao đến đâu."
            },
            {
              "num": "10",
              "action": "Chụp lại toàn bộ tin nhắn chốt đơn, lưu vào một album riêng trên điện thoại đặt tên theo tháng.",
              "why": "Khi bill về mà lệch, bạn cần lôi ra trong 30 giây chứ không phải cuộn tin nhắn tìm cả buổi.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SAU KHI ĐẶT",
          "subtitle": "đừng đặt xong rồi quên",
          "steps": [
            {
              "num": "11",
              "action": "Nhắn vào nhóm Zalo của quầy: đã đặt nhà cung cấp nào, gồm nhóm hàng gì, dự kiến về ngày nào.",
              "why": "Nhân viên biết hàng sắp về thì không đi đặt trùng, và biết trả lời khách khi khách hỏi bao giờ có.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Đặt một lời nhắc trên điện thoại đúng ngày hẹn giao.",
              "why": "Đơn quên mất là đơn không bao giờ về. Chờ tới lúc phát hiện ra thì đã hết hàng cả tuần.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Quá hẹn một ngày là gọi hỏi ngay, đừng đợi thêm.",
              "why": "Gọi sớm thì họ còn xoay được. Đợi ba ngày rồi mới gọi thì hàng cũng đã hết ở kho họ.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Đặt theo cảm giác, không xem số",
          "viSao": "Xem báo cáo mất 10 phút, còn đặt theo trí nhớ thì mất 2 phút",
          "caiGia": "Mã bán chạy hết hàng, mã bán chậm chất đầy kho. Tiền nằm sai chỗ."
        },
        {
          "loi": "Chốt giá qua điện thoại",
          "viSao": "Gọi nhanh hơn nhắn, và đang quen nhau nên ngại nhắn lại xác nhận",
          "caiGia": "Bill về lệch giá thì không có gì để đối chiếu. Mỗi lần lệch vài trăm nghìn, một năm là con số lớn."
        },
        {
          "loi": "Gật deal ôm hàng vì thưởng cao",
          "viSao": "Con số thưởng nhìn thấy ngay, còn tiền nằm chết trên kệ thì không ai tính",
          "caiGia": "Ôm hàng 6 tháng để lấy khoản thưởng nhỏ hơn tiền vốn bị kẹt."
        },
        {
          "loi": "Không hỏi hạn dùng trước khi chốt",
          "viSao": "Nghĩ rằng nhà cung cấp sẽ tự giao lô tốt",
          "caiGia": "Nhận lô cận date. Lúc mở thùng ra mới biết thì đã ở thế bị động."
        },
        {
          "loi": "Đặt xong quên theo dõi",
          "viSao": "Đặt xong là xong việc, không ai nhắc",
          "caiGia": "Đơn không về mà không ai biết cho tới lúc kệ trống."
        }
      ]
    }
  },
  {
    "code": "QT-03",
    "title": "CANH HẠN DÙNG VÀ XẢ HÀNG CẬN DATE",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 2,
    "phaseName": "Đợt 2 - Dòng hàng & tiền",
    "pages": 4,
    "targetRole": "Cả quầy",
    "summary": "Rà theo mốc 90-60-30 ngày · cách đánh dấu trên kệ để nhìn phát thấy · thứ tự 5 phương án xả · khi nào phải chấp nhận lỗ để cắt.",
    "keyDetail": "Chấm màu theo tháng hết hạn dán lên hộp. Nhìn kệ là thấy ngay, không phải lật từng hộp đọc chữ nhỏ.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — CANH HẠN DÙNG VÀ XẢ HÀNG CẬN DATE",
      "subtitle": "Dán tại khu vực kho hoặc cạnh kệ chính. Rà vào một ngày cố định mỗi tháng.",
      "sections": [
        {
          "title": "RÀ MỖI THÁNG MỘT LẦN",
          "items": [
            "Chọn một ngày cố định trong tháng — ví dụ ngày 5",
            "Xuất danh sách hàng còn dưới 90 ngày từ phần mềm",
            "Đi kiểm kệ đối chiếu, vì máy không bao giờ đủ",
            "Chia làm 3 nhóm: còn 90 ngày · 60 ngày · 30 ngày"
          ]
        },
        {
          "title": "ĐÁNH DẤU NGAY TRÊN KỆ",
          "items": [
            "Chấm màu VÀNG: còn 90 ngày — ưu tiên tư vấn",
            "Chấm màu CAM: còn 60 ngày — bắt đầu xả",
            "Chấm màu ĐỎ: còn 30 ngày — xả bằng mọi giá hoặc chuẩn bị trả",
            "Dán chấm lên mặt trước hộp, nhìn phát thấy"
          ]
        },
        {
          "title": "THỨ TỰ 5 CÁCH XẢ",
          "items": [
            "1. Ưu tiên tư vấn — dặn cả quầy cùng đẩy mã đó",
            "2. Đóng combo với hàng bán chạy",
            "3. Đề nghị nhà cung cấp đổi lô — làm khi còn ≥60 ngày",
            "4. Giảm giá có kiểm soát, ghi rõ lý do",
            "5. Chấp nhận cắt lỗ — còn hơn để hết hạn rồi huỷ"
          ]
        },
        {
          "title": "ĐIỀU TUYỆT ĐỐI KHÔNG LÀM",
          "items": [
            "Không bán thuốc đã quá hạn, dù chỉ quá một ngày",
            "Không giấu hàng cận date vào trong cho khuất mắt",
            "Không đợi còn 15 ngày mới bắt đầu xả",
            "Không tiếp tục nhập thêm mã đang có hàng cận date"
          ]
        }
      ]
    },
    "sop": {
      "title": "CANH HẠN DÙNG VÀ XẢ HÀNG CẬN DATE",
      "duration": "Rà toàn quầy mỗi tháng một lần, mất khoảng 45 phút. Đây là chỗ tiền rò rỉ đều đặn nhất của một quầy thuốc.",
      "lamDung": "Nhìn kệ là biết ngay mã nào sắp hết hạn. Xả sớm khi còn xả được nên gần như không phải huỷ. Nhà cung cấp còn nhận đổi vì bạn đề nghị sớm.",
      "lamAu": "Hàng nằm im tới ngày hết hạn rồi đổ đi. Đây là khoản lỗ không xuất hiện trên báo cáo nào, chủ quầy chỉ thấy tự nhiên tiền hụt mà không rõ vì đâu.",
      "aiLam": [
        {
          "role": "Người rà hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Một người cố định, không luân phiên. Rà quen tay thì nhanh và ít sót.",
          "desc": "",
          "step": "Làm toàn bộ"
        },
        {
          "role": "Cả quầy",
          "desc": "Được thông báo danh sách mã đang cần xả, ưu tiên tư vấn các mã đó.",
          "step": "Bước 8 đến 12"
        },
        {
          "role": "Chủ hoặc quản lý",
          "desc": "",
          "step": "Duyệt mức giảm giá và quyết định khi phải cắt lỗ."
        },
        {
          "role": "Duyệt bước 11, 12",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "RÀ HÀNG — NGÀY CỐ ĐỊNH MỖI THÁNG",
          "subtitle": "45 phút, đừng dồn tới lúc phát hiện ra mới làm",
          "steps": [
            {
              "num": "1",
              "action": "Chọn một ngày cố định trong tháng và giữ nguyên ngày đó mãi. Ví dụ ngày mùng 5 hằng tháng.",
              "why": "Việc không có ngày cố định là việc bị đẩy lùi mãi. Ngày cố định biến nó thành thói quen chứ không phải quyết định.",
              "tip": "Chọn ngày đầu tháng, đừng chọn cuối tháng. Cuối tháng luôn bận đối soát và kiểm kê."
            },
            {
              "num": "2",
              "action": "Xuất từ phần mềm danh sách toàn bộ mã có hạn dùng còn dưới 90 ngày.",
              "why": "Đây là lúc bạn gặt hái thành quả của việc nhập đủ số lô và hạn dùng lúc nhận hàng. Không nhập thì không có danh sách này.",
              "tip": "Chưa có phần mềm thì rà tay theo nhóm: mỗi tháng rà một phần tư số kệ, bốn tháng hết một vòng."
            },
            {
              "num": "3",
              "action": "Cầm danh sách đi đối chiếu trên kệ. Kiểm cả những mã máy không báo — nhất là hàng khuyến mãi và hàng tặng.",
              "why": "Hàng tặng thường không được nhập đầy đủ vào máy nên không bao giờ được cảnh báo, mà lại là nhóm hay hết hạn nhất.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Chia thành ba nhóm: còn 90 ngày, còn 60 ngày, còn 30 ngày. Ghi số lượng và giá trị từng nhóm.",
              "why": "Ba mốc này ứng với ba cách xử lý khác nhau. Gộp chung một danh sách thì không biết bắt đầu từ đâu.",
              "tip": ""
            }
          ]
        },
        {
          "title": "ĐÁNH DẤU — ĐỂ NHÌN LÀ THẤY",
          "subtitle": "10 phút, làm ngay sau khi rà",
          "steps": [
            {
              "num": "5",
              "action": "Dán chấm màu lên mặt trước hộp: vàng cho nhóm 90 ngày, cam cho 60 ngày, đỏ cho 30 ngày.",
              "why": "Chữ nhỏ in trên hộp thì phải cầm lên đọc mới thấy. Chấm màu thì đứng cách một mét đã nhìn ra.",
              "tip": "Mua sẵn một cuộn nhãn tròn ba màu, giá vài chục nghìn, dùng được cả năm. Đây là khoản đầu tư có lãi nhất trong cả quy trình."
            },
            {
              "num": "6",
              "action": "Kéo toàn bộ hàng đã đánh dấu ra phía ngoài kệ, đặt ngang tầm mắt.",
              "why": "Hàng cận date phải là hàng dễ lấy nhất, không phải hàng nằm sâu nhất. Nhiều quầy làm ngược vì ngại nhìn thấy nó.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Chụp ảnh danh sách gửi vào nhóm Zalo của quầy, kèm câu: tháng này ưu tiên tư vấn các mã sau.",
              "why": "Một người rà mà cả quầy không biết thì không ai đẩy. Xả hàng cận date là việc của cả quầy chứ không phải của người rà.",
              "tip": ""
            }
          ]
        },
        {
          "title": "NĂM CÁCH XẢ — LÀM THEO ĐÚNG THỨ TỰ NÀY",
          "subtitle": "",
          "steps": [
            {
              "num": "1",
              "action": "Ưu tiên tư vấn Áp dụng ngay từ mốc 90 ngày. Cả quầy cùng đẩy một danh sách ngắn, tối đa 10 mã. Danh sách dài quá thì không ai nhớ nổi.",
              "why": "",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Đóng combo với hàng bán chạy Ghép mã cận date với mã đang bán tốt, cùng nhóm nhu cầu. Combo phải hợp lý về chuyên môn, không ghép bừa cho hết hàng.",
              "why": "",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Đề nghị nhà cung cấp đổi lô Làm khi còn ít nhất 60 ngày. Dưới 60 ngày thì gần như không ai nhận. Đề nghị bằng tin nhắn, kèm ảnh hạn dùng trên hộp.",
              "why": "",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Giảm giá có kiểm soát Chủ hoặc quản lý duyệt mức giảm. Ghi rõ lý do vào phần mềm để tháng sau nhìn lại còn biết vì sao. Đừng giảm âm thầm.",
              "why": "",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Chấp nhận cắt lỗ Còn dưới 30 ngày và không xả được thì bán hoà vốn hoặc dưới vốn vẫn hơn là huỷ. Huỷ là mất trắng cộng thêm công làm thủ tục. Nguyên tắc quyết định: càng để lâu thì càng ít lựa chọn. Ở mốc 90 ngày bạn có cả năm cách. Ở mốc 30 ngày bạn chỉ còn cách thứ tư và thứ năm. Đây là lý do phải rà mỗi tháng chứ không phải mỗi quý.",
              "why": "",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Đợi tới lúc còn 30 ngày mới xử lý",
          "viSao": "Còn 90 ngày thì thấy vẫn còn xa, chưa vội",
          "caiGia": "Mất bốn trong năm cách xả. Chỉ còn cách giảm giá hoặc huỷ."
        },
        {
          "loi": "Giấu hàng cận date vào trong kệ",
          "viSao": "Nhìn thấy nó mỗi ngày thì khó chịu",
          "caiGia": "Nó nằm im tới ngày hết hạn. Đây là phản xạ tự nhiên nhưng tốn tiền nhất."
        },
        {
          "loi": "Bỏ qua hàng tặng và hàng khuyến mãi",
          "viSao": "Hàng miễn phí nên không thấy tiếc",
          "caiGia": "Hàng tặng chiếm chỗ, và khi hết hạn vẫn phải làm thủ tục huỷ như hàng mua."
        },
        {
          "loi": "Rà không có ngày cố định",
          "viSao": "Ai cũng bận, việc không gấp thì để mai",
          "caiGia": "Ba tháng không rà một lần là đủ để một loạt mã trôi qua mốc còn cứu được."
        }
      ]
    }
  },
  {
    "code": "QT-04",
    "title": "KIỂM KÊ MÀ KHÔNG PHẢI ĐÓNG CỬA",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 2,
    "phaseName": "Đợt 2 - Dòng hàng & tiền",
    "pages": 4,
    "targetRole": "Quản lý · nhân viên",
    "summary": "Chia kệ thành cụm nhỏ kiểm luân phiên · cách đếm hai vòng · phân loại nguyên nhân chênh lệch · ngưỡng chênh phải truy tới cùng.",
    "keyDetail": "Kiểm cuốn chiếu mỗi ngày một cụm nhỏ thay vì kiểm toàn bộ mỗi quý. Không mất buổi nào mà tháng nào cũng kiểm xong hết quầy.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — KIỂM KÊ MÀ KHÔNG PHẢI ĐÓNG CỬA",
      "subtitle": "Dán cạnh kệ hoặc trong sổ kiểm kê. Mỗi ngày kiểm một cụm, tick xong cụm nào gạch cụm đó.",
      "sections": [
        {
          "title": "CÁCH CHIA CỤM",
          "items": [
            "Chia toàn quầy thành 20–25 cụm nhỏ, mỗi cụm khoảng 15 phút",
            "Đánh số cụm và dán số lên kệ",
            "Mỗi ngày kiểm 1 cụm vào giờ vắng khách",
            "Hết một vòng thì quay lại cụm 1"
          ]
        },
        {
          "title": "KIỂM MỘT CỤM — ĐẾM HAI VÒNG",
          "items": [
            "Vòng 1: đếm không nhìn số trên máy",
            "Ghi số đếm được ra giấy",
            "Vòng 2: mở máy so, lệch thì đếm lại chính mã đó",
            "Vẫn lệch sau hai vòng thì mới ghi là chênh lệch thật"
          ]
        },
        {
          "title": "KIỂM KỸ HƠN VỚI 4 NHÓM NÀY",
          "items": [
            "Hàng giá trị cao",
            "Hàng nhỏ gọn dễ bỏ túi",
            "Hàng đang khuyến mãi",
            "Hàng vừa nhập trong tuần"
          ]
        },
        {
          "title": "CHÊNH LỆCH THÌ LÀM GÌ",
          "items": [
            "Không sửa số trên máy cho khớp ngay",
            "Ghi lại: mã · số máy · số thực · ngày · người kiểm",
            "Truy 4 nguyên nhân: bán quên bấm · nhập sai · trả hàng chưa trừ · thất thoát",
            "Chênh lặp lại ở cùng một mã là dấu hiệu phải báo chủ"
          ]
        }
      ]
    },
    "sop": {
      "title": "KIỂM KÊ MÀ KHÔNG PHẢI ĐÓNG CỬA",
      "duration": "Chia quầy thành cụm nhỏ, mỗi ngày kiểm một cụm trong 15 phút. Một tháng đi hết cả quầy mà không mất buổi nào.",
      "lamDung": "Tháng nào cũng biết chính xác quầy có gì. Phát hiện thất thoát khi nó còn nhỏ. Không bao giờ phải đóng cửa nửa ngày để kiểm.",
      "lamAu": "Kiểm kê một năm một lần thì lúc phát hiện chênh đã không truy được nguyên nhân. Số trên máy sai dần cho tới lúc không dùng để đặt hàng được nữa.",
      "aiLam": [
        {
          "role": "Người kiểm",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nhân viên trực ca vắng khách. Luân phiên để không ai kiểm mãi một cụm.",
          "desc": "",
          "step": "Bước 1 đến 9"
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Xem kết quả mỗi tuần. Truy nguyên nhân khi có chênh lệch.",
          "desc": "",
          "step": "Bước 10 đến 13"
        },
        {
          "role": "Chủ cơ sở",
          "desc": "Được báo khi chênh lặp lại hoặc vượt ngưỡng.",
          "step": "Bước 13"
        }
      ],
      "phases": [
        {
          "title": "CHUẨN BỊ MỘT LẦN — CHIA CỤM",
          "subtitle": "làm một lần đầu tiên, sau đó dùng mãi",
          "steps": [
            {
              "num": "1",
              "action": "Chia toàn bộ hàng trong quầy thành 20–25 cụm nhỏ. Mỗi cụm là một khoang kệ hoặc một nhóm hàng, đủ để kiểm xong trong 15 phút.",
              "why": "Cụm nhỏ thì kiểm được vào giờ vắng khách mà không phải bỏ dở. Chia to quá là quay lại đúng vấn đề của kiểm kê toàn quầy.",
              "tip": "Đếm thử một khoang xem mất bao lâu rồi mới chia. Đừng chia trên giấy theo cảm tính."
            },
            {
              "num": "2",
              "action": "Đánh số từng cụm và dán số lên kệ. Lập một bảng theo dõi ghi ngày kiểm gần nhất của từng cụm.",
              "why": "Có số trên kệ thì ai vào ca cũng biết hôm nay tới lượt cụm nào, không cần hỏi ai.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Đánh dấu riêng bốn nhóm cần kiểm dày hơn: hàng giá trị cao, hàng nhỏ gọn dễ bỏ túi, hàng đang khuyến mãi, hàng vừa nhập trong tuần.",
              "why": "Bốn nhóm này chiếm phần lớn rủi ro thất thoát và sai sót. Kiểm hai tuần một lần thay vì một tháng.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KIỂM MỘT CỤM — ĐẾM HAI VÒNG",
          "subtitle": "15 phút mỗi ngày, vào giờ vắng khách",
          "steps": [
            {
              "num": "4",
              "action": "Vòng một: đếm số lượng thực tế trên kệ, KHÔNG mở phần mềm ra xem trước.",
              "why": "Nhìn số trên máy trước rồi đi đếm là đang tự kỷ ám thị. Não sẽ đếm ra đúng con số nó đã nhìn thấy.",
              "tip": "Đây là chi tiết quan trọng nhất của cả quy trình. Nhiều người kiểm kê cả năm mà chưa bao giờ phát hiện ra chênh lệch, chỉ vì họ nhìn máy trước."
            },
            {
              "num": "5",
              "action": "Ghi số đếm được ra giấy hoặc vào điện thoại. Ghi hết cả cụm rồi mới sang bước sau.",
              "why": "Ghi từng mã rồi so từng mã sẽ làm bạn bị ảnh hưởng ở những mã sau.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Vòng hai: mở phần mềm, so từng dòng. Mã nào lệch thì quay lại đếm lại đúng mã đó.",
              "why": "Phần lớn chênh lệch là do đếm sót, nhất là hàng xếp chồng nhiều lớp. Đếm lại lần hai loại được đa số trường hợp.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Sau hai vòng vẫn lệch thì mới ghi là chênh lệch thật. Ghi đủ: mã hàng, số trên máy, số thực đếm, ngày, người kiểm.",
              "why": "Ghi thiếu người kiểm và ngày thì tháng sau nhìn lại không truy được gì.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Với hàng có nhiều lô: đếm và ghi theo từng lô, không gộp tổng.",
              "why": "Gộp tổng thì đúng số nhưng sai lô, và lúc có thu hồi lô cụ thể là không biết còn bao nhiêu.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Gạch cụm vừa kiểm trên bảng theo dõi, ghi ngày.",
              "why": "Không gạch thì hôm sau người khác vào ca lại kiểm trùng cụm đó, còn cụm khác thì bỏ quên.",
              "tip": ""
            }
          ]
        },
        {
          "title": "XỬ LÝ CHÊNH LỆCH",
          "subtitle": "đừng sửa số cho khớp rồi thôi",
          "steps": [
            {
              "num": "10",
              "action": "Tuyệt đối không sửa số trên máy cho khớp ngay. Để nguyên và báo quản lý.",
              "why": "Sửa cho khớp là xoá mất bằng chứng duy nhất. Sau ba lần sửa như vậy thì không ai còn biết quầy đang mất hàng ở đâu.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Quản lý truy theo bốn nguyên nhân, theo đúng thứ tự này: bán mà quên bấm máy · nhập hàng sai số lượng · trả hàng cho nhà cung cấp chưa trừ kho · thất thoát.",
              "why": "Ba nguyên nhân đầu chiếm phần lớn các trường hợp và đều là lỗi quy trình sửa được. Nghĩ ngay tới thất thoát là vừa oan người vừa bỏ qua lỗi thật.",
              "tip": "Kiểm tra nguyên nhân thứ nhất bằng cách đối chiếu số bán trong ngày với số hoá đơn đã xuất."
            },
            {
              "num": "12",
              "action": "Ghi kết luận nguyên nhân vào bảng theo dõi rồi mới điều chỉnh số trên máy.",
              "why": "Có ghi nguyên nhân thì ba tháng sau nhìn lại mới thấy được nguyên nhân nào đang lặp lại.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Chênh lệch lặp lại ở cùng một mã hoặc cùng một cụm từ hai lần trở lên: báo chủ cơ sở.",
              "why": "Chênh một lần là sai sót. Chênh lặp lại ở đúng một chỗ là dấu hiệu của lỗi quy trình hoặc thất thoát có hệ thống.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Nhìn số trên máy trước khi đếm",
          "viSao": "Nhìn trước thì đếm nhanh hơn và ít phải đếm lại",
          "caiGia": "Đếm ra đúng con số trên máy dù thực tế không phải vậy. Kiểm kê thành hình thức."
        },
        {
          "loi": "Sửa số cho khớp rồi thôi",
          "viSao": "Sửa mất 5 giây, còn truy nguyên nhân mất nửa tiếng",
          "caiGia": "Xoá mất bằng chứng. Thất thoát tiếp diễn mà không ai biết."
        },
        {
          "loi": "Kiểm toàn quầy một lần mỗi năm",
          "viSao": "Kiểm cuốn chiếu nghe phiền hơn, phải nhớ hôm nay tới cụm nào",
          "caiGia": "Phát hiện chênh sau một năm thì không truy được nguyên nhân nào cả."
        },
        {
          "loi": "Bỏ qua lô khi đếm, chỉ đếm tổng",
          "viSao": "Đếm theo lô chậm hơn",
          "caiGia": "Đúng tổng nhưng sai lô. Có thu hồi lô là không biết còn bao nhiêu trong quầy."
        }
      ]
    }
  },
  {
    "code": "QT-05",
    "title": "TRẢ HÀNG VÀ ĐỔI HÀNG CHO NHÀ CUNG CẤP",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 2,
    "phaseName": "Đợt 2 - Dòng hàng & tiền",
    "pages": 3,
    "targetRole": "Quản lý",
    "summary": "Trường hợp nào được trả · chuẩn bị bằng chứng gì · viết tin nhắn đề nghị thế nào cho được duyệt · theo dõi tới khi có hàng đổi về.",
    "keyDetail": "Gửi kèm đúng ba thứ: video mở thùng, ảnh bill có khoanh dòng, ảnh hàng đặt cạnh bill. Thiếu một là bị kéo dài.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — TRẢ HÀNG VÀ ĐỔI HÀNG CHO NHÀ CUNG CẤP",
      "subtitle": "Dán cạnh khu hàng chờ xử lý. Mỗi lần đề nghị trả hàng tick một lượt.",
      "sections": [
        {
          "title": "GỬI ĐỦ 3 THỨ NÀY — THIẾU 1 LÀ BỊ KÉO DÀI",
          "items": [
            "Video mở thùng lúc nhận hàng (nếu là lỗi giao hàng)",
            "Ảnh bill có khoanh dòng cần trả",
            "Ảnh hàng đặt cạnh bill trong cùng một khung hình"
          ]
        },
        {
          "title": "CHUẨN BỊ TRƯỚC KHI ĐỀ NGHỊ",
          "items": [
            "Giữ nguyên hàng: không bóc · không dán giá · không xé tem",
            "Chụp rõ số lô và hạn dùng trên hộp",
            "Đếm chính xác số lượng đề nghị trả",
            "Tìm lại tin nhắn chốt đơn lúc đặt"
          ]
        },
        {
          "title": "VIẾT TIN NHẮN ĐỀ NGHỊ",
          "items": [
            "Ghi rõ: số đơn · ngày nhận · tên hàng · số lô · số lượng",
            "Nêu lý do bằng một câu, không kể lể",
            "Đề nghị rõ muốn gì: đổi lô khác hay trả và hoàn tiền",
            "Gửi kèm 3 loại bằng chứng ở trên"
          ]
        },
        {
          "title": "THEO DÕI ĐẾN CÙNG",
          "items": [
            "Ghi vào bảng theo dõi: ngày đề nghị · nội dung · trạng thái",
            "Chưa trả lời sau 3 ngày thì nhắc lại",
            "Chưa xong sau 2 tuần thì báo chủ cơ sở",
            "Hàng chờ trả: để riêng, dán giấy, KHÔNG bán"
          ]
        }
      ]
    },
    "sop": {
      "title": "TRẢ HÀNG VÀ ĐỔI HÀNG CHO NHÀ CUNG CẤP",
      "duration": "Từ lúc phát hiện hàng cần trả đến lúc nhận được hàng đổi hoặc tiền về. Chuẩn bị mất 15 phút, theo dõi có thể mất vài tuần.",
      "lamDung": "Đòi được hàng đổi hoặc tiền hoàn thay vì tự chịu. Nhà cung cấp thấy quầy làm chặt chẽ nên lần sau giao cẩn thận hơn hẳn.",
      "lamAu": "Thiếu bằng chứng thì đề nghị bị treo vô thời hạn. Hàng chờ trả để lẫn vào kệ thì có ngày bán nhầm ra cho khách.",
      "aiLam": [
        {
          "role": "Người phát hiện",
          "desc": "",
          "step": ""
        },
        {
          "role": "Ai phát hiện thì người đó tách hàng ra và chụp ảnh ngay tại chỗ.",
          "desc": "",
          "step": "Bước 1 đến 4"
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Soạn tin nhắn đề nghị và theo dõi tới khi xong.",
          "desc": "",
          "step": "Bước 5 đến 11"
        },
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": "Quyết định với hàng nghi ngờ chất lượng."
        },
        {
          "role": "Bước 2",
          "desc": "",
          "step": ""
        },
        {
          "role": "TRƯỜNG HỢP NÀO ĐỀ NGHỊ ĐƯỢC",
          "desc": "",
          "step": ""
        },
        {
          "role": "Trường hợp",
          "desc": "Đề nghị gì",
          "step": ""
        },
        {
          "role": "Khả năng được duyệt",
          "desc": "",
          "step": ""
        },
        {
          "role": "Giao thiếu, giao thừa, giao sai hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Bổ sung hoặc thu hồi",
          "desc": "",
          "step": ""
        },
        {
          "role": "Cao — nếu có video mở thùng và báo trong 24 giờ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Hàng hỏng, móp, rách, bong tem khi nhận",
          "desc": "Đổi hàng khác",
          "step": ""
        },
        {
          "role": "Cao — nếu báo ngay trong ngày nhận",
          "desc": "",
          "step": ""
        },
        {
          "role": "Hàng cận date dưới ngưỡng khi nhận",
          "desc": "Đổi lô có hạn xa hơn",
          "step": ""
        },
        {
          "role": "Trung bình — phải báo ngay lúc mở thùng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Hàng còn hạn nhưng bán chậm, muốn trả",
          "desc": "Đổi mã khác",
          "step": ""
        },
        {
          "role": "Thấp — phụ thuộc hoàn toàn vào chính sách và quan hệ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Hàng bị thu hồi theo thông báo",
          "desc": "",
          "step": ""
        },
        {
          "role": "Thu hồi và hoàn tiền",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chắc chắn — đây là nghĩa vụ của bên cung cấp",
          "desc": "",
          "step": ""
        },
        {
          "role": "Hàng đã bóc, đã dán giá, đã lên kệ lâu",
          "desc": "",
          "step": ""
        },
        {
          "role": "Gần như không đề nghị được",
          "desc": "",
          "step": ""
        },
        {
          "role": "Rất thấp — đây là lý do phải phát hiện sớm",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "CHUẨN BỊ — 15 PHÚT, LÀM KỸ MỘT LẦN",
          "subtitle": "chuẩn bị đủ thì đề nghị được duyệt nhanh",
          "steps": [
            {
              "num": "1",
              "action": "Tách hàng cần trả ra khỏi kệ ngay. Để vào khu hàng chờ xử lý, dán giấy ghi rõ: chờ trả nhà cung cấp, ngày, không được bán.",
              "why": "Hàng chờ trả để lẫn trên kệ là chuyện có thật và xảy ra thường xuyên. Bán nhầm ra rồi thì vừa mất quyền trả vừa có hàng lỗi ngoài thị trường.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Giữ nguyên hiện trạng: không bóc, không dán giá, không xé tem, không tách vỉ.",
              "why": "Hàng đã bóc hoặc đã dán giá gần như không bên nào nhận lại. Đây là lỗi khiến rất nhiều đề nghị trả hàng bị từ chối ngay từ đầu.",
              "tip": "Nếu lỡ dán giá rồi thì đừng cố bóc ra — bóc để lại vết còn tệ hơn. Chụp ảnh nguyên trạng và nói thật khi đề nghị."
            },
            {
              "num": "3",
              "action": "Chụp ảnh rõ số lô và hạn dùng in trên hộp. Chụp thêm ảnh toàn cảnh số hàng đề nghị trả.",
              "why": "Số lô là thứ bên cung cấp dùng để đối chiếu với kho của họ. Ảnh mờ không đọc được số lô là bị hỏi lại và mất thêm mấy ngày.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Đếm chính xác số lượng đề nghị trả, đếm hai lần.",
              "why": "Đề nghị trả 10 mà thực tế gửi đi 8 thì lần sau họ không tin bất kỳ con số nào bạn đưa ra.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SOẠN VÀ GỬI ĐỀ NGHỊ",
          "subtitle": "gửi bằng tin nhắn, không gọi điện",
          "steps": [
            {
              "num": "5",
              "action": "Soạn tin nhắn theo đúng thứ tự: số đơn, ngày nhận hàng, tên hàng, hàm lượng, số lô, hạn dùng, số lượng đề nghị trả.",
              "why": "Người xử lý bên kia mỗi ngày nhận hàng chục đề nghị. Tin nhắn đủ thông tin theo thứ tự thì họ xử lý ngay; tin nhắn thiếu thì bị đẩy xuống cuối hàng.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Nêu lý do bằng đúng một câu, nói sự việc chứ không kể lể và không trách móc.",
              "why": "Người nhận tin nhắn không phải người gây ra lỗi. Viết trách móc thì họ phòng thủ, và việc chậm lại.",
              "tip": "Câu mẫu: Bên em nhận đơn ngày 01/10, mã X lô Y hạn dùng chỉ còn 5 tháng, dưới mức bên em nhận được."
            },
            {
              "num": "7",
              "action": "Đề nghị rõ mình muốn gì: đổi lô khác, hay trả và hoàn tiền, hay trừ vào công nợ.",
              "why": "Không nói rõ thì họ chọn phương án tiện nhất cho họ, thường là chậm nhất cho bạn.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Gửi kèm đủ ba thứ: video mở thùng nếu là lỗi giao hàng, ảnh bill có khoanh dòng, ảnh hàng đặt cạnh bill trong cùng khung hình.",
              "why": "Ba thứ này trả lời trước ba câu hỏi họ chắc chắn sẽ hỏi. Gửi đủ ngay từ đầu là rút ngắn được cả tuần.",
              "tip": "Ảnh hàng đặt cạnh bill là ảnh quan trọng nhất — nó chứng minh hàng này đến từ đơn này."
            },
            {
              "num": "9",
              "action": "Gửi vào đúng kênh chính thức của nhà cung cấp, không nhắn riêng cho người quen.",
              "why": "Nhắn riêng thì khi người đó nghỉ phép hoặc nghỉ việc là đề nghị của bạn biến mất không dấu vết.",
              "tip": ""
            }
          ]
        },
        {
          "title": "THEO DÕI ĐẾN CÙNG",
          "subtitle": "phần lớn đề nghị chết ở bước này",
          "steps": [
            {
              "num": "10",
              "action": "Ghi vào một bảng theo dõi: ngày đề nghị, nội dung, số tiền, trạng thái, ngày nhắc gần nhất.",
              "why": "Không có bảng thì sau ba tuần bạn không nhớ nổi mình đã đề nghị những gì và cái nào chưa xong.",
              "tip": "Một sheet Excel bốn cột là đủ. Đừng cầu kỳ, cầu kỳ là không ai điền."
            },
            {
              "num": "11",
              "action": "Chưa có phản hồi sau 3 ngày làm việc thì nhắc lại, nhắc trên chính đoạn tin nhắn cũ.",
              "why": "Nhắc trên đoạn cũ thì họ nhìn thấy lại toàn bộ bằng chứng mà không phải tìm. Mở đoạn mới là bắt đầu lại từ đầu.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Chưa xong sau 2 tuần thì báo chủ cơ sở để trao đổi ở cấp cao hơn.",
              "why": "Quá hai tuần thường không còn là vấn đề của người xử lý mà là vấn đề chính sách. Người xử lý không giải quyết được.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Khi nhận được hàng đổi: kiểm nhận lại đầy đủ theo QT-01, kể cả khi là hàng đổi.",
              "why": "Hàng đổi cũng có thể sai lần nữa. Nhiều quầy kiểm rất kỹ hàng mua nhưng nhận hàng đổi thì cho qua.",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Khi xong: cập nhật kho trên phần mềm và đóng dòng theo dõi.",
              "why": "Trả hàng mà không trừ kho là nguyên nhân chênh lệch kiểm kê phổ biến thứ ba.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Bóc hoặc dán giá rồi mới phát hiện",
          "viSao": "Nhận hàng xong dán giá luôn cho nhanh, chưa kiểm kỹ",
          "caiGia": "Mất quyền trả. Đây là lý do QT-01 bắt kiểm xong mới dán giá."
        },
        {
          "loi": "Gửi đề nghị thiếu bằng chứng",
          "viSao": "Chụp đủ ảnh mất thêm 10 phút",
          "caiGia": "Bị hỏi lại nhiều vòng, mỗi vòng mất vài ngày, cuối cùng quá hạn."
        },
        {
          "loi": "Nhắn riêng cho người quen bên nhà cung cấp",
          "viSao": "Nhắn người quen thì nhanh và dễ nói chuyện",
          "caiGia": "Người đó nghỉ là đề nghị biến mất. Không có dấu vết chính thức nào."
        },
        {
          "loi": "Trả hàng xong quên trừ kho",
          "viSao": "Việc đã xong rồi, không ai nghĩ tới bước cuối",
          "caiGia": "Số trên máy vẫn còn hàng đã trả. Chênh lệch kiểm kê không truy ra nguyên nhân."
        }
      ]
    }
  },
  {
    "code": "QT-06",
    "title": "HÀNG HỎNG, HÀNG THU HỒI, HÀNG HUỶ",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Dược sĩ · quản lý",
    "summary": "Nhận thông báo thu hồi thì làm gì trong 24 giờ đầu · rà tồn và rà khách đã mua · biệt trữ · thủ tục huỷ.",
    "keyDetail": "Tra ngược trong phần mềm ra danh sách khách đã mua lô đó - chỉ làm được nếu lúc nhập đã ghi số lô. Đây là lúc bạn thấy vì sao phải ghi.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — HÀNG HỎNG, HÀNG THU HỒI, HÀNG HUỶ",
      "subtitle": "Dán tại khu hàng chờ xử lý. Đây là quy trình ít dùng nhưng khi cần thì cần gấp.",
      "sections": [
        {
          "title": "PHẢN XẠ ĐẦU TIÊN — LUÔN GIỐNG NHAU",
          "items": [
            "DỪNG BÁN mã đó ngay lập tức",
            "Lấy TOÀN BỘ hàng cùng lô ra khỏi kệ",
            "Dán giấy đỏ: HÀNG CHỜ XỬ LÝ — KHÔNG ĐƯỢC BÁN",
            "Để vào khu riêng, tốt nhất là có khoá",
            "Báo người có chuyên môn dược và quản lý ngay trong ca"
          ]
        },
        {
          "title": "NHẬN THÔNG BÁO THU HỒI — 24 GIỜ ĐẦU",
          "items": [
            "Đọc kỹ: thu hồi mã nào, SỐ LÔ nào, mức độ nào",
            "Tra trong phần mềm còn tồn bao nhiêu của đúng lô đó",
            "Gom hết khỏi kệ, kể cả hàng đang trưng bày",
            "Tra ngược danh sách khách đã mua lô đó",
            "Báo nhà cung cấp và làm thủ tục trả",
            "Ghi vào sổ theo dõi thu hồi"
          ]
        },
        {
          "title": "TRƯỚC KHI HUỶ",
          "items": [
            "Đã hỏi nhà cung cấp có nhận trả không chưa",
            "Đã lập danh mục đầy đủ: tên · lô · hạn · số lượng chưa",
            "Đã có mặt đủ người chứng kiến chưa",
            "Đã chuẩn bị biên bản chưa"
          ]
        },
        {
          "title": "TUYỆT ĐỐI KHÔNG",
          "items": [
            "Không bán tiếp mã đang chờ xử lý, dù khách năn nỉ",
            "Không vứt thuốc vào thùng rác thường",
            "Không huỷ mà không có biên bản và người chứng kiến",
            "Không để hàng chờ xử lý lẫn vào kệ bán"
          ]
        }
      ]
    },
    "sop": {
      "title": "HÀNG HỎNG, HÀNG THU HỒI, HÀNG HUỶ",
      "duration": "Ba tình huống khác nhau nhưng cùng một phản xạ đầu tiên: tách ra khỏi kệ ngay. Xử lý thu hồi gấp nhất — 24 giờ đầu quyết định.",
      "lamDung": "Thu hồi được đúng lô trong 24 giờ. Truy ngược ra khách đã mua để báo. Hàng hỏng không bao giờ lọt ra ngoài.",
      "lamAu": "Bán nhầm hàng thuộc lô bị thu hồi là rủi ro trực tiếp cho người bệnh và là lỗi nặng nhất khi bị kiểm tra. Huỷ không biên bản thì không giải trình được vì sao hàng biến mất khỏi kho.",
      "aiLam": [
        {
          "role": "Người phát hiện",
          "desc": "",
          "step": ""
        },
        {
          "role": "Tách hàng, dán giấy, báo ngay. Không tự quyết định gì thêm.",
          "desc": "",
          "step": "Bước 1 đến 3"
        },
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": "Quyết định hướng xử lý. Chủ trì việc huỷ."
        },
        {
          "role": "Bước 4 và toàn bộ phần huỷ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Tra tồn, tra khách đã mua, làm việc với nhà cung cấp.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Phần thu hồi",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "BA TÌNH HUỐNG — MỘT PHẢN XẠ ĐẦU TIÊN",
          "subtitle": "làm trong 5 phút, đừng phân tích trước",
          "steps": [
            {
              "num": "1",
              "action": "Dừng bán mã đó ngay. Nếu đang có khách hỏi đúng mã đó thì xin lỗi và nói hàng đang tạm dừng để kiểm tra.",
              "why": "Vài phút chần chừ để quyết định là đủ để bán thêm vài hộp. Dừng trước, nghĩ sau.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Lấy toàn bộ hàng cùng lô ra khỏi kệ, kể cả hàng đang trưng bày ở tủ kính và hàng để trong kho.",
              "why": "Lấy sót một chỗ là chỗ đó sẽ được bán ra. Đi đủ ba nơi: kệ bán, tủ trưng bày, kho.",
              "tip": "Với hàng có nhiều lô nằm lẫn nhau, lấy hết ra rồi mới lọc theo lô. Lọc ngay trên kệ là chắc chắn sót."
            },
            {
              "num": "3",
              "action": "Dán giấy đỏ ghi rõ: HÀNG CHỜ XỬ LÝ — KHÔNG ĐƯỢC BÁN, kèm ngày và lý do ngắn. Để vào khu riêng, tốt nhất là có khoá.",
              "why": "Ca sau không biết chuyện gì đã xảy ra. Tờ giấy đỏ là thứ duy nhất nói cho họ biết.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Báo người có chuyên môn dược và quản lý ngay trong ca. Không đợi hết ca mới báo.",
              "why": "Với thu hồi thì mỗi giờ trôi qua là thêm rủi ro. Với hàng hỏng thì báo sớm còn kịp đề nghị nhà cung cấp đổi.",
              "tip": ""
            }
          ]
        },
        {
          "title": "TÌNH HUỐNG 1 — NHẬN THÔNG BÁO THU HỒI",
          "subtitle": "gấp nhất, 24 giờ đầu quyết định",
          "steps": [
            {
              "num": "5",
              "action": "Đọc kỹ thông báo: thu hồi mã nào, hàm lượng nào, SỐ LÔ nào, mức độ thu hồi.",
              "why": "Thu hồi thường chỉ áp cho một vài lô cụ thể chứ không phải toàn bộ mã. Đọc lướt rồi gom nhầm cả mã là gây rối vô ích cho chính mình.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Tra trong phần mềm xem còn tồn bao nhiêu của đúng lô đó, và đã bán ra bao nhiêu.",
              "why": "Đây chính là lúc bạn thấy vì sao QT-01 bắt nhập đủ số lô. Không nhập lô thì bước này không làm được, và bạn phải gom toàn bộ mã đó.",
              "tip": "Không có số lô trong máy thì cách duy nhất là gom hết mã đó và đối chiếu tay trên từng hộp."
            },
            {
              "num": "7",
              "action": "Gom hết khỏi kệ và cách ly như bước 2, 3.",
              "why": "",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Tra ngược danh sách khách đã mua lô đó. Với khách có lưu số điện thoại thì liên hệ báo.",
              "why": "Đây là việc phân biệt một quầy làm nghề tử tế với một quầy chỉ bán hàng. Cũng là thứ khách nhớ rất lâu và kể lại cho người khác.",
              "tip": "Gọi điện chứ đừng nhắn tin. Nội dung ngắn: bên em vừa nhận thông báo thu hồi lô thuốc anh chị mua ngày X, mời anh chị mang ra bên em đổi."
            },
            {
              "num": "9",
              "action": "Báo nhà cung cấp, làm thủ tục trả theo QT-05. Ghi vào sổ theo dõi thuốc bị thu hồi.",
              "why": "Thu hồi là trường hợp gần như chắc chắn được nhận trả và hoàn tiền — đây là nghĩa vụ của bên cung cấp.",
              "tip": ""
            }
          ]
        },
        {
          "title": "TÌNH HUỐNG 2 — HÀNG HỎNG, NGHI NGỜ CHẤT LƯỢNG",
          "subtitle": "người có chuyên môn quyết định",
          "steps": [
            {
              "num": "10",
              "action": "Người có chuyên môn dược xem trực tiếp trong vòng 24 giờ và kết luận theo một trong ba hướng.",
              "why": "Để quá 24 giờ thì hàng nằm trong khu chờ xử lý ngày càng nhiều và không ai nhớ vì sao từng món nằm đó.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Hướng một — vẫn dùng được: ghi lý do vào sổ, trả về kệ. Hướng hai — đề nghị nhà cung cấp đổi trả theo QT-05. Hướng ba — chuyển sang huỷ.",
              "why": "Ghi lý do cả với trường hợp cho lưu hành tiếp. Không ghi thì lần sau gặp đúng tình huống đó lại phải quyết định lại từ đầu.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Nếu khách là người phát hiện và mang hàng đến phản ánh: xử lý theo QT-17 trước, xong mới quay lại quy trình này.",
              "why": "Khách đang đứng đó thì việc gấp là xử lý với khách, không phải xử lý với hộp thuốc.",
              "tip": ""
            }
          ]
        },
        {
          "title": "TÌNH HUỐNG 3 — HUỶ THUỐC",
          "subtitle": "làm đúng thủ tục, đừng làm tắt",
          "steps": [
            {
              "num": "13",
              "action": "Trước khi huỷ, hỏi lại nhà cung cấp một lần nữa có nhận trả không.",
              "why": "Huỷ là mất trắng cộng thêm công làm thủ tục. Trả được dù chỉ một phần cũng hơn.",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Lập danh mục đầy đủ những gì sẽ huỷ: tên, hàm lượng, số lô, hạn dùng, số lượng, lý do huỷ.",
              "why": "Danh mục này là phần chính của biên bản. Ghi thiếu số lô là biên bản không có giá trị đối chiếu.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Huỷ có mặt ít nhất hai người, trong đó có người có chuyên môn dược. Lập biên bản, tất cả cùng ký.",
              "why": "Một người tự huỷ một mình thì không giải trình được vì sao hàng biến mất khỏi kho, kể cả khi hoàn toàn ngay thẳng.",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Huỷ theo cách làm cho thuốc không còn dùng lại được: bóc khỏi vỉ, làm biến dạng, tách khỏi bao bì có nhãn. Không vứt nguyên hộp vào thùng rác.",
              "why": "Thuốc vứt nguyên hộp bị nhặt lại và bán ra thị trường là chuyện có thật. Bao bì còn nguyên nhãn cũng có thể bị dùng để làm hàng giả.",
              "tip": "Tách riêng phần bao bì có in tên và số lô, xử lý riêng phần đó."
            },
            {
              "num": "17",
              "action": "Chụp ảnh quá trình huỷ, lưu cùng biên bản.",
              "why": "Ảnh là thứ chứng minh việc huỷ đã thực sự diễn ra chứ không chỉ nằm trên giấy.",
              "tip": ""
            },
            {
              "num": "18",
              "action": "Trừ kho trên phần mềm và lưu biên bản vào hồ sơ.",
              "why": "Không trừ kho thì số trên máy vẫn còn hàng đã huỷ, và kiểm kê sẽ ra chênh lệch không truy được.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Chỉ gom hàng trên kệ, quên tủ trưng bày và kho",
          "viSao": "Kệ là chỗ nghĩ tới đầu tiên, hai chỗ kia thì quên",
          "caiGia": "Còn sót hàng thuộc lô thu hồi và bán ra. Đây là rủi ro trực tiếp cho người bệnh."
        },
        {
          "loi": "Không tra được lô vì lúc nhập bỏ trống ô số lô",
          "viSao": "Nhập lô mất thêm thời gian và phần mềm cho phép bỏ trống",
          "caiGia": "Phải gom toàn bộ mã đó thay vì một lô. Mất hàng chục lần nhiều hơn cần thiết."
        },
        {
          "loi": "Huỷ mà không lập biên bản",
          "viSao": "Chỉ vài hộp, thấy làm biên bản là quá hình thức",
          "caiGia": "Không giải trình được khi kiểm kê lệch hoặc khi bị kiểm tra."
        },
        {
          "loi": "Vứt thuốc nguyên hộp vào thùng rác",
          "viSao": "Nhanh và tiện",
          "caiGia": "Thuốc bị nhặt lại bán ra ngoài, hoặc bao bì bị dùng làm hàng giả — và tên quầy in trên đó."
        }
      ]
    }
  },
  {
    "code": "QT-07",
    "title": "SẮP XẾP KỆ VÀ ĐỔI LAYOUT THEO MÙA",
    "flowId": "flow-hang",
    "flowName": "Dòng Hàng",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Chủ · quản lý",
    "summary": "Bốn khu vực vàng theo luồng đi của khách · nhóm nào đặt đâu và vì sao · lịch đổi 4 lần mỗi năm theo mùa bệnh.",
    "keyDetail": "Đặt hàng mua theo thói quen ở chỗ khách phải đi qua nhiều nhất, không phải chỗ dễ lấy nhất cho nhân viên.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — SẮP XẾP KỆ VÀ ĐỔI LAYOUT THEO MÙA",
      "subtitle": "Dán trong kho hoặc khu nhân viên. Dùng khi sắp xếp lại và khi đổi layout theo mùa.",
      "sections": [
        {
          "title": "4 KHU VỰC VÀNG — HÀNG NÀO ĐẶT ĐÂU",
          "items": [
            "Ngang tầm mắt: hàng lợi nhuận tốt, hàng muốn đẩy",
            "Ngay quầy thanh toán: hàng nhỏ, mua theo cảm hứng",
            "Lối khách phải đi qua: hàng theo mùa đang cao điểm",
            "Kệ thấp và kệ cao: hàng khách chủ động tìm, hàng ít bán"
          ]
        },
        {
          "title": "NGUYÊN TẮC BẤT DI BẤT DỊCH",
          "items": [
            "Thuốc kê đơn tách riêng, không để khách tự lấy",
            "Hàng không phải thuốc tách khu, có biển rõ",
            "Trong mỗi nhóm: hạn gần bán trước, hàng cũ ra ngoài",
            "Không xếp thuốc trực tiếp trên sàn",
            "Không để hàng che biển chỉ dẫn và lối đi"
          ]
        },
        {
          "title": "ĐỔI LAYOUT 4 LẦN MỖI NĂM",
          "items": [
            "Tháng 3–4: giao mùa, hô hấp dị ứng",
            "Tháng 6–7: nắng nóng, tiêu hoá, da liễu, chống nắng",
            "Tháng 9–10: tựu trường, hô hấp, sốt virus",
            "Tháng 12–1: lạnh, xương khớp, quà tặng và thực phẩm bổ sung"
          ]
        },
        {
          "title": "SAU KHI ĐỔI",
          "items": [
            "Chụp ảnh sơ đồ mới, gửi nhóm Zalo",
            "Đi một vòng cùng cả quầy, chỉ rõ cái gì chuyển đi đâu",
            "Hai tuần sau xem lại: nhóm nào bán tốt hơn, nhóm nào tệ đi"
          ]
        }
      ]
    },
    "sop": {
      "title": "SẮP XẾP KỆ VÀ ĐỔI LAYOUT THEO MÙA",
      "duration": "Sắp xếp lại toàn quầy mất nửa buổi, làm 4 lần mỗi năm. Đây là cách tăng doanh thu rẻ nhất vì không tốn tiền, chỉ tốn công.",
      "lamDung": "Khách tự tìm thấy thứ họ cần nên nhân viên đỡ mất thời gian chỉ đường. Hàng theo mùa nằm đúng chỗ nên bán được nhiều hơn hẳn mà không tốn đồng quảng cáo nào.",
      "lamAu": "Kệ xếp theo thói quen thì hàng bán chạy nằm chỗ khuất, hàng ế nằm chỗ đẹp. Không đổi theo mùa thì bỏ lỡ đúng lúc nhu cầu lên cao nhất.",
      "aiLam": [
        {
          "role": "Chủ hoặc quản lý",
          "desc": "",
          "step": "Quyết định sơ đồ và nhóm hàng nào đặt đâu."
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Cả quầy",
          "desc": "",
          "step": ""
        },
        {
          "role": "Thực hiện việc di chuyển. Sau đó phải được đi một vòng để biết cái gì chuyển đi đâu.",
          "desc": "",
          "step": "Bước 6 đến 12"
        }
      ],
      "phases": [
        {
          "title": "4 KHU VỰC VÀNG — VÀ VÌ SAO",
          "subtitle": "",
          "steps": []
        },
        {
          "title": "SẮP XẾP LẦN ĐẦU — LÀM KỸ MỘT LẦN",
          "subtitle": "nửa buổi, chọn ngày vắng khách",
          "steps": [
            {
              "num": "1",
              "action": "Vẽ sơ đồ quầy ra giấy trước khi động vào bất cứ thứ gì. Đánh dấu cửa vào, quầy thanh toán, lối đi chính.",
              "why": "Sắp xếp trực tiếp trên kệ là làm tới đâu nghĩ tới đó, và sẽ phải làm lại. Nửa tờ giấy tiết kiệm cả buổi.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Đứng ở cửa vào nhìn vào quầy trong 30 giây, ghi lại ba thứ mắt bạn nhìn thấy đầu tiên.",
              "why": "Ba chỗ đó chính là ba vị trí đắt giá nhất của quầy. Hầu hết quầy đang để đúng ba chỗ đó cho những nhóm hàng không cần được nhìn thấy.",
              "tip": "Nhờ một người chưa từng vào quầy làm việc này. Chủ quầy nhìn quen mắt nên không còn thấy được nữa."
            },
            {
              "num": "3",
              "action": "Đánh dấu trên sơ đồ bốn khu vực vàng theo bảng trên.",
              "why": "",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Xếp các nhóm hàng vào sơ đồ theo bốn khu vực. Xếp trên giấy trước, kiểm tra lại rồi mới làm thật.",
              "why": "Trên giấy thì đổi ý mất 10 giây. Trên kệ thì đổi ý mất 20 phút.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Kiểm tra sơ đồ với năm nguyên tắc bắt buộc: thuốc kê đơn tách riêng · hàng không phải thuốc tách khu có biển · điều kiện bảo quản đúng theo nhãn · không đặt thuốc trên sàn · không che biển chỉ dẫn và lối đi.",
              "why": "Sắp đẹp mà sai năm điều này thì phải làm lại từ đầu, và có thể bị lập biên bản.",
              "tip": ""
            }
          ]
        },
        {
          "title": "LÀM THẬT",
          "subtitle": "chọn giờ vắng khách, làm gọn từng khu một",
          "steps": [
            {
              "num": "6",
              "action": "Làm từng khu một, xong khu nào gọn khu đó. Không dỡ cả quầy ra cùng lúc.",
              "why": "Dỡ hết ra một lượt thì giữa chừng có khách vào là hỗn loạn, và hàng dễ xếp nhầm chỗ.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Trong mỗi nhóm, xếp theo nguyên tắc hạn gần bán trước: hàng hạn gần hơn nằm ngoài.",
              "why": "Sắp xếp lại là cơ hội tốt nhất trong năm để rà lại toàn bộ hạn dùng. Đừng bỏ lỡ.",
              "tip": "Kết hợp luôn với QT-03: vừa xếp vừa dán chấm màu cho hàng cận date."
            },
            {
              "num": "8",
              "action": "Lau sạch kệ trước khi xếp hàng vào.",
              "why": "Một năm chỉ có bốn dịp kệ trống để lau kỹ. Bỏ qua thì bụi tích lại ở đúng chỗ không ai với tới.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Dán lại biển chỉ dẫn nhóm hàng cho đúng vị trí mới.",
              "why": "Biển cũ chỉ sai chỗ còn tệ hơn không có biển, vì khách đi theo biển rồi không thấy gì.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SAU KHI XONG",
          "subtitle": "đừng bỏ qua ba bước này",
          "steps": [
            {
              "num": "10",
              "action": "Chụp ảnh toàn bộ kệ sau khi xếp xong, gửi vào nhóm Zalo.",
              "why": "Ảnh này là chuẩn để so sánh. Một tháng sau kệ sẽ tự lộn xộn dần, và có ảnh thì biết phải chỉnh lại về đâu.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Đi một vòng cùng cả quầy, chỉ rõ cái gì đã chuyển đi đâu.",
              "why": "Nhân viên không biết hàng chuyển chỗ thì lúc khách hỏi họ nói không có. Đây là lỗi gây mất khách ngay sau mỗi lần sắp xếp lại.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Hai tuần sau, xem lại số bán theo nhóm. Nhóm nào tốt hơn, nhóm nào tệ đi.",
              "why": "Sắp xếp là một giả thuyết, không phải một chân lý. Chỉ số bán mới nói được bạn đoán đúng hay sai.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Xếp theo chỗ nào tiện cho nhân viên lấy",
          "viSao": "Người sắp là người bán, nên sắp theo tay mình",
          "caiGia": "Chỗ đẹp nhất dành cho hàng không cần được nhìn thấy. Mất doanh thu mà không biết."
        },
        {
          "loi": "Sắp xong không báo cả quầy",
          "viSao": "Ai làm thì người đó biết, tưởng người khác cũng thấy",
          "caiGia": "Khách hỏi, nhân viên nói không có. Mất khách ngay tuần đầu sau khi sắp xếp."
        },
        {
          "loi": "Cả năm không đổi layout",
          "viSao": "Đổi mất nửa buổi và ai cũng ngại",
          "caiGia": "Bỏ lỡ đúng những tuần nhu cầu lên cao nhất trong năm."
        },
        {
          "loi": "Xếp đẹp nhưng sai nguyên tắc bắt buộc",
          "viSao": "Chỉ nghĩ tới thẩm mỹ và tiện lấy",
          "caiGia": "Bị lập biên bản, và phải dỡ ra làm lại toàn bộ."
        }
      ]
    }
  },
  {
    "code": "QT-08",
    "title": "MỞ CA",
    "flowId": "flow-tien",
    "flowName": "Dòng Tiền",
    "phase": 1,
    "phaseName": "Đợt 1 - Bản chạy thử",
    "pages": 3,
    "targetRole": "Nhân viên ca sáng",
    "summary": "Thứ tự việc từ lúc mở cửa: kiểm tra bên ngoài · bật thiết bị · đo nhiệt ẩm · kiểm tiền đầu ca · rà hàng đặt hôm nay · nhìn lướt kệ hàng.",
    "keyDetail": "Đi một vòng nhìn kệ trước khi mở cửa, 60 giây. Mất hàng qua đêm hay tủ lạnh hỏng đều lộ ra ở vòng đi này.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — MỞ CA",
      "subtitle": "Dán ngay sau quầy, chỗ nhìn thấy khi vừa bước vào. Sáng nào cũng tick một lượt.",
      "sections": [
        {
          "title": "TRƯỚC KHI MỞ CỬA — 3 PHÚT",
          "items": [
            "Nhìn bên ngoài: cửa · khoá · biển hiệu · có dấu cạy phá không",
            "Đi một vòng nhìn kệ 60 giây — có gì khác thường không",
            "Kiểm tủ lạnh: có điện không, nhiệt độ bao nhiêu",
            "Bật đèn, điều hoà, máy tính, máy in"
          ]
        },
        {
          "title": "10 PHÚT ĐẦU — GHI VÀ ĐẾM",
          "items": [
            "Đo và ghi nhiệt độ, độ ẩm — ghi ngay, không ghi bù",
            "Đếm tiền đầu ca, đối chiếu với biên bản ca trước",
            "Mở phần mềm, kiểm tra kết nối và máy in hoá đơn",
            "Đọc biên bản bàn giao ca trước: việc dở dang, sự cố"
          ]
        },
        {
          "title": "TRƯỚC KHI ĐÓN KHÁCH ĐẦU TIÊN",
          "items": [
            "Xem hôm nay có đơn hàng nào về không",
            "Xem hôm nay có khách hẹn lấy thuốc không",
            "Nhìn danh sách mã đang cần đẩy (hàng cận date)",
            "Kiểm túi, bao bì ra lẻ, bút, sổ — đủ dùng cả ca chưa"
          ]
        },
        {
          "title": "THẤY BẤT THƯỜNG THÌ BÁO NGAY",
          "items": [
            "Dấu cạy phá · mất hàng · tủ lạnh mất điện qua đêm",
            "Tiền đầu ca không khớp biên bản",
            "Phần mềm không vào được hoặc mất kết nối",
            "Báo quản lý TRƯỚC khi mở cửa đón khách"
          ]
        }
      ]
    },
    "sop": {
      "title": "MỞ CA",
      "duration": "12 việc trong 10 phút đầu ngày, làm theo đúng thứ tự này. Làm đủ thì cả ngày không phải chữa cháy.",
      "lamDung": "Phát hiện sự cố qua đêm trước khi khách vào. Cả ca chạy trơn vì mọi thứ đã sẵn sàng. Sổ nhiệt ẩm ghi đúng giờ nên không bao giờ phải ghi bù.",
      "lamAu": "Mở cửa rồi mới phát hiện tủ lạnh hỏng qua đêm thì thuốc đã hỏng. Không đếm tiền đầu ca thì cuối ca lệch cũng không biết lệch từ đâu.",
      "aiLam": [
        {
          "role": "Nhân viên ca sáng",
          "desc": "",
          "step": "Làm toàn bộ 12 việc theo thứ tự."
        },
        {
          "role": "Bước 1 đến 12",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "Được báo ngay khi có bất thường, trước khi quầy mở cửa.",
          "step": "Bước 13"
        }
      ],
      "phases": [
        {
          "title": "3 PHÚT ĐẦU — NHÌN TRƯỚC KHI CHẠM",
          "subtitle": "làm khi cửa còn đóng",
          "steps": [
            {
              "num": "1",
              "action": "Nhìn bên ngoài trước khi tra chìa: cửa cuốn, khoá, biển hiệu, mặt kính. Có dấu cạy phá, vết bẩn lạ, giấy dán lạ không.",
              "why": "Nếu có đột nhập thì đây là lúc duy nhất hiện trường còn nguyên. Vào rồi mới phát hiện thì đã xoá hết dấu vết.",
              "tip": "Thấy dấu cạy phá thì đừng vào. Đứng ngoài gọi quản lý trước."
            },
            {
              "num": "2",
              "action": "Vào trong, đi một vòng nhìn kệ trong 60 giây. Không kiểm đếm gì, chỉ nhìn xem có gì khác so với lúc đóng cửa.",
              "why": "Mắt người rất giỏi phát hiện cái khác thường khi chỉ nhìn lướt. Mất hàng, đổ vỡ, nước rò đều lộ ra ở vòng đi này.",
              "tip": "Đi cùng một đường mỗi sáng. Đi quen một đường thì cái khác thường tự nhảy ra."
            },
            {
              "num": "3",
              "action": "Kiểm tủ lạnh trước tiên: có điện không, nhiệt độ đang bao nhiêu, có đọng nước hay đá bám bất thường không.",
              "why": "Tủ lạnh mất điện qua đêm là sự cố tốn kém nhất có thể xảy ra ngoài giờ. Biết sớm 10 phút vẫn còn cứu được vài thứ.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Bật đèn, điều hoà, máy tính, máy in theo thứ tự. Bật điều hoà sớm để quầy kịp về nhiệt độ trước khi đông khách.",
              "why": "Điều hoà cần thời gian. Bật cùng lúc khách vào thì cả buổi sáng quầy vẫn nóng và ẩm.",
              "tip": ""
            }
          ]
        },
        {
          "title": "10 PHÚT ĐẦU — GHI VÀ ĐẾM",
          "subtitle": "đây là phần không được bỏ dù bận",
          "steps": [
            {
              "num": "5",
              "action": "Đo nhiệt độ và độ ẩm, ghi ngay vào sổ. Ghi đúng giờ thật, không làm tròn.",
              "why": "Ghi ngay lúc đo là cách duy nhất để số liệu đúng. Để lát nữa ghi là bắt đầu con đường dẫn tới ghi bù cả tuần.",
              "tip": "Để sổ và bút ngay cạnh nhiệt ẩm kế. Phải đi lấy sổ là lý do khiến người ta để lát nữa ghi."
            },
            {
              "num": "6",
              "action": "Đếm tiền đầu ca, đối chiếu với số ghi trong biên bản bàn giao ca trước.",
              "why": "Không đếm đầu ca thì cuối ca lệch cũng không biết lệch phát sinh trong ca nào.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Mở phần mềm, kiểm tra vào được không, máy in hoá đơn có giấy và in thử một tờ.",
              "why": "Phát hiện máy in hết giấy lúc khách đang đứng chờ là mất điểm không đáng có.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Đọc biên bản bàn giao của ca trước: việc dở dang, sự cố, khách hẹn quay lại.",
              "why": "Ca trước ghi mà ca sau không đọc thì việc ghi biên bản trở thành vô nghĩa.",
              "tip": ""
            }
          ]
        },
        {
          "title": "TRƯỚC KHI ĐÓN KHÁCH ĐẦU TIÊN",
          "subtitle": "4 việc, mỗi việc dưới 1 phút",
          "steps": [
            {
              "num": "9",
              "action": "Xem hôm nay có đơn hàng nào về không, khoảng mấy giờ.",
              "why": "Biết trước thì chuẩn bị chỗ và không bị động khi xe tới đúng lúc đông khách.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Xem hôm nay có khách hẹn lấy thuốc hay khách cần gọi nhắc không.",
              "why": "Khách hẹn mà quầy quên là mất khách. Nhìn danh sách mất 30 giây.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Nhìn lại danh sách mã đang cần đẩy theo QT-03, nhớ 3–5 mã đầu.",
              "why": "Nhớ được vài mã thì cả ngày có cơ hội là đẩy. Không nhìn thì cả ngày không nhớ ra.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Kiểm túi đựng, bao bì ra lẻ, bút, sổ, tiền lẻ — đủ dùng hết ca chưa.",
              "why": "Hết túi hoặc hết tiền lẻ giữa lúc đông khách là loại rắc rối nhỏ nhưng làm chậm cả hàng người đang chờ.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Mở cửa đón khách rồi mới làm các việc mở ca",
          "viSao": "Sáng có khách sớm, thấy ngại để khách đợi",
          "caiGia": "Cả ca bị cuốn đi và không bao giờ quay lại làm. Sổ nhiệt ẩm ghi bù vào cuối ngày."
        },
        {
          "loi": "Bỏ qua vòng đi nhìn kệ 60 giây",
          "viSao": "Thấy chẳng để làm gì, hôm nào cũng như hôm nào",
          "caiGia": "Ngày có chuyện thật thì phát hiện muộn, lúc hiện trường đã bị xáo trộn."
        },
        {
          "loi": "Không đếm tiền đầu ca",
          "viSao": "Tin nhau, và ca trước đã đếm rồi",
          "caiGia": "Cuối ca lệch thì hai ca đổ lỗi cho nhau, không có căn cứ nào để phân định."
        }
      ]
    }
  },
  {
    "code": "QT-09",
    "title": "ĐÓNG CA",
    "flowId": "flow-tien",
    "flowName": "Dòng Tiền",
    "phase": 1,
    "phaseName": "Đợt 1 - Bản chạy thử",
    "pages": 3,
    "targetRole": "Nhân viên ca cuối",
    "summary": "Kết ca · đếm tiền · cất hàng giá trị cao · kiểm tủ lạnh · tắt thiết bị theo thứ tự · khoá và kiểm khoá.",
    "keyDetail": "Đếm tiền TRƯỚC khi dọn dẹp, không phải sau. Đếm lúc đã mệt và vội vã là lúc sai nhiều nhất.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — ĐÓNG CA",
      "subtitle": "Dán ngay sau quầy cạnh bảng mở ca. Tối nào cũng tick một lượt trước khi khoá cửa.",
      "sections": [
        {
          "title": "LÀM TRƯỚC KHI DỌN DẸP",
          "items": [
            "Kết ca trên phần mềm, in hoặc chụp màn hình tổng kết",
            "ĐẾM TIỀN — đếm hai lần, đếm lúc còn tỉnh táo",
            "Đối chiếu tiền thực với số trên máy, ghi cả hai con số",
            "Lệch thì ghi đúng số lệch, KHÔNG tự bù cho khớp"
          ]
        },
        {
          "title": "CẤT VÀ KIỂM",
          "items": [
            "Cất hàng giá trị cao vào tủ có khoá",
            "Đo và ghi nhiệt độ, độ ẩm lần cuối ngày",
            "Kiểm tủ lạnh: nhiệt độ, cửa đóng kín, không kẹt đồ",
            "Thu hàng trưng bày ngoài trời, ngoài hiên vào trong"
          ]
        },
        {
          "title": "GHI LẠI CHO NGÀY MAI",
          "items": [
            "Việc dở dang: khách đặt chưa lấy · đơn chờ · khiếu nại chưa xong",
            "Sự cố trong ca, kể cả sự cố nhỏ đã tự xử lý xong",
            "Mã nào sắp hết cần đặt",
            "Nhắn nhóm Zalo tổng kết ca"
          ]
        },
        {
          "title": "TẮT VÀ KHOÁ — THỨ TỰ CỐ ĐỊNH",
          "items": [
            "Tắt máy tính, máy in, thiết bị điện — trừ tủ lạnh",
            "TỦ LẠNH KHÔNG BAO GIỜ TẮT",
            "Tắt điều hoà, tắt đèn khu vực, để lại đèn bảo vệ",
            "Khoá trong, khoá ngoài, kéo thử một lần xem đã chốt chưa"
          ]
        }
      ]
    },
    "sop": {
      "title": "ĐÓNG CA",
      "duration": "14 việc trong 15 phút cuối ngày. Thứ tự quan trọng: đếm tiền TRƯỚC khi dọn dẹp, không phải sau.",
      "lamDung": "Sáng hôm sau mở cửa là chạy được ngay. Lệch tiền phát hiện đúng trong ca phát sinh. Ca sau biết chính xác việc gì còn dở.",
      "lamAu": "Đếm tiền lúc đã mệt và vội về là lúc sai nhiều nhất. Quên kiểm tủ lạnh một tối là mất cả tủ thuốc bảo quản lạnh.",
      "aiLam": [
        {
          "role": "Nhân viên ca cuối",
          "desc": "",
          "step": "Làm toàn bộ 14 việc theo thứ tự."
        },
        {
          "role": "Bước 1 đến 14",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nhận tin tổng kết ca. Được báo ngay nếu lệch tiền.",
          "desc": "",
          "step": "Bước 12, 13"
        }
      ],
      "phases": [
        {
          "title": "15 PHÚT CUỐI — TIỀN TRƯỚC, DỌN SAU",
          "subtitle": "đây là thứ tự quan trọng nhất của cả quy trình",
          "steps": [
            {
              "num": "1",
              "action": "Dừng nhận việc mới trừ khách đang đứng tại quầy. Bắt đầu quy trình đóng ca.",
              "why": "Bắt đầu lúc đã quá giờ đóng cửa thì mọi việc sau đó đều làm vội.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Kết ca trên phần mềm. In hoặc chụp lại màn hình tổng kết: số hoá đơn, tiền mặt, tiền chuyển khoản.",
              "why": "Có ảnh màn hình thì hôm sau tra lại được. Không có thì phải mở lại phần mềm và lọc theo ngày.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "ĐẾM TIỀN NGAY BÂY GIỜ, trước khi dọn dẹp. Đếm hai lần.",
              "why": "Đếm sau khi dọn dẹp là đếm lúc đã mệt và đang vội về. Đây là lúc sai nhiều nhất trong cả ngày.",
              "tip": "Đếm lần hai theo thứ tự ngược lại với lần một. Đếm lại cùng một cách thì sẽ lặp lại cùng một lỗi."
            },
            {
              "num": "4",
              "action": "Đối chiếu tiền thực đếm với số trên máy. Ghi cả hai con số vào biên bản, kể cả khi khớp.",
              "why": "Ghi cả khi khớp thì mới có chuỗi số liệu để nhìn ra bất thường. Chỉ ghi khi lệch thì không so sánh được gì.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Lệch thì ghi đúng số lệch. Tuyệt đối không bỏ tiền túi bù vào cho khớp.",
              "why": "Bù một lần là che mất một lỗi đang lặp lại — có thể là nhập sai giá, quên bấm máy, hoặc thất thoát thật. Chủ mất nhiều tháng mới phát hiện, lúc đó số tiền đã lớn.",
              "tip": "Một quầy mà tháng nào cũng khớp tuyệt đối không lệch một lần là dấu hiệu đáng nghi, chứ không phải dấu hiệu tốt."
            }
          ]
        },
        {
          "title": "CẤT VÀ KIỂM",
          "subtitle": "5 phút",
          "steps": [
            {
              "num": "6",
              "action": "Cất hàng giá trị cao và hàng nhỏ dễ mất vào tủ có khoá.",
              "why": "Hàng để ngoài qua đêm là mời gọi. Cất mất 2 phút, mất hàng thì mất nhiều hơn thế.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Đo và ghi nhiệt độ, độ ẩm lần cuối ngày.",
              "why": "Hai lần đo mỗi ngày, sáng và tối. Bỏ lần tối thì sổ chỉ còn một nửa giá trị.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Kiểm tủ lạnh: nhiệt độ đang bao nhiêu, cửa có đóng kín không, có hộp nào kẹt làm hở cửa không.",
              "why": "Cửa tủ hở một đêm là hỏng toàn bộ hàng bên trong. Kiểm mất 15 giây.",
              "tip": "Kiểm bằng cách áp tay quanh mép cửa xem có hơi lạnh phả ra không, nhanh hơn nhìn."
            },
            {
              "num": "9",
              "action": "Thu hàng trưng bày ngoài hiên, ngoài trời vào trong.",
              "why": "Hàng để ngoài qua đêm vừa dễ mất vừa bị sương và nhiệt độ ban đêm làm hỏng.",
              "tip": ""
            }
          ]
        },
        {
          "title": "GHI LẠI CHO NGÀY MAI",
          "subtitle": "3 phút, đừng bỏ",
          "steps": [
            {
              "num": "10",
              "action": "Ghi việc dở dang: khách đặt hàng chưa lấy, đơn thuốc khách chờ quay lại, khiếu nại chưa xử lý xong, hàng nhà cung cấp hẹn giao.",
              "why": "Việc dở dang không được ghi lại là việc bị bỏ rơi. Khách quay lại hôm sau mà không ai biết là mất khách.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Ghi sự cố trong ca, kể cả sự cố nhỏ đã tự xử lý xong.",
              "why": "Sự cố nhỏ lặp lại nhiều lần mới là thứ đáng lo. Không ghi thì không ai nhìn ra nó đang lặp lại.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Ghi những mã sắp hết cần đặt, để người đặt hàng có sẵn danh sách.",
              "why": "Người bán biết mã nào sắp hết chính xác hơn báo cáo, vì họ vừa lấy hàng suốt cả ngày.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Nhắn nhóm Zalo tổng kết ca: doanh số, có lệch tiền không, sự cố gì, việc gì còn dở.",
              "why": "Quản lý không có mặt tại quầy thì đây là cách duy nhất họ biết chuyện gì đã xảy ra trong ngày.",
              "tip": "Nhắn theo một mẫu cố định để đọc lướt là nắm được, và để so sánh giữa các ngày."
            }
          ]
        },
        {
          "title": "TẮT VÀ KHOÁ",
          "subtitle": "thứ tự cố định, không đổi",
          "steps": [
            {
              "num": "14",
              "action": "Tắt máy tính, máy in và thiết bị điện. TRỪ TỦ LẠNH. Tủ lạnh không bao giờ tắt.",
              "why": "Tắt nhầm tủ lạnh vì đang tắt một loạt ổ điện là chuyện đã xảy ra ở nhiều quầy. Hậu quả là mất toàn bộ hàng bảo quản lạnh.",
              "tip": "Dán một nhãn đỏ lên ổ cắm tủ lạnh ghi KHÔNG TẮT. Chi phí gần bằng không."
            },
            {
              "num": "15",
              "action": "Tắt điều hoà, tắt đèn khu vực, để lại đèn bảo vệ và camera.",
              "why": "",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Khoá trong, khoá ngoài, rồi kéo thử một lần xem đã chốt chưa.",
              "why": "Kéo thử là thói quen mất 2 giây và loại bỏ hoàn toàn cái cảm giác lúc về tới nhà mới tự hỏi mình đã khoá chưa.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Đếm tiền sau khi đã dọn dẹp xong",
          "viSao": "Dọn xong mới thấy rảnh để đếm",
          "caiGia": "Đếm lúc mệt và vội về. Đây là lúc sai nhiều nhất trong ngày."
        },
        {
          "loi": "Tự bù tiền cho khớp",
          "viSao": "Lệch ít, ngại bị hỏi, muốn biên bản đẹp",
          "caiGia": "Che mất một lỗi đang lặp lại. Chủ phát hiện sau nhiều tháng, lúc số tiền đã lớn."
        },
        {
          "loi": "Quên kiểm tủ lạnh hoặc tắt nhầm ổ điện tủ lạnh",
          "viSao": "Tắt một loạt cho nhanh",
          "caiGia": "Mất toàn bộ hàng bảo quản lạnh chỉ sau một đêm."
        }
      ]
    }
  },
  {
    "code": "QT-10",
    "title": "BÀN GIAO CA",
    "flowId": "flow-tien",
    "flowName": "Dòng Tiền",
    "phase": 2,
    "phaseName": "Đợt 2 - Dòng hàng & tiền",
    "pages": 3,
    "targetRole": "Cả hai ca",
    "summary": "Sáu nhóm nội dung bắt buộc bàn giao · cách đối chiếu · xử lý khi chênh · quy tắc không bàn giao miệng.",
    "keyDetail": "Không tự bù tiền túi cho khớp. Bù một lần là che mất một lỗi đang lặp lại, và chờ mất nhiều tháng mới phát hiện.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — BÀN GIAO CA",
      "subtitle": "Dán ngay chỗ giao ca. Cả hai người cùng nhìn vào bảng này khi bàn giao.",
      "sections": [
        {
          "title": "NGƯỜI GIAO CA — 6 VIỆC",
          "items": [
            "Kết ca trên phần mềm",
            "Đếm tiền mặt hai lần",
            "Đếm hàng nhạy cảm theo danh mục",
            "Ghi việc dở dang và sự cố trong ca",
            "Ghi sổ nhiệt ẩm của ca mình — không để ca sau ghi hộ",
            "Chỉ rời quầy sau khi có đủ 2 chữ ký"
          ]
        },
        {
          "title": "NGƯỜI NHẬN CA — 4 VIỆC",
          "items": [
            "Có mặt trước giờ vào ca 10 phút",
            "Đếm lại tiền và hàng nhạy cảm — đếm thật, không nhìn qua",
            "Chưa rõ thì hỏi ngay; chưa được giải thích rõ thì CHƯA KÝ",
            "Ký và ghi đúng giờ thật, không ghi giờ tròn cho đẹp"
          ]
        },
        {
          "title": "6 NHÓM BẮT BUỘC BÀN GIAO",
          "items": [
            "TIỀN: tiền mặt thực đếm · số trên máy · chênh lệch · tiền khách đặt cọc",
            "HÀNG: hàng nhạy cảm · hàng đang biệt trữ · hàng mới nhập chưa lên kệ",
            "VIỆC DỞ: đơn khách chưa lấy · khiếu nại đang xử lý · hàng hẹn giao",
            "SỰ CỐ: mọi sự cố trong ca, kể cả đã tự xử lý xong",
            "SỔ SÁCH: sổ nào đã ghi, sổ nào còn bỏ trống",
            "THÔNG TIN: khách quen dặn gì · thông báo từ quản lý · lịch hẹn ca sau"
          ]
        },
        {
          "title": "CHÊNH LỆCH THÌ LÀM GÌ",
          "items": [
            "Ghi đúng số chênh, dù rất nhỏ",
            "Ghi lý do nghi ngờ nếu có",
            "Gọi quản lý NGAY TRONG CA, không đợi hết ngày",
            "KHÔNG tự bù tiền cho khớp"
          ]
        }
      ]
    },
    "sop": {
      "title": "BÀN GIAO CA",
      "duration": "15 phút cuối ca người giao chuẩn bị, 10 phút trước ca người nhận có mặt. Không có bàn giao miệng.",
      "lamDung": "Việc nào cũng có người chịu trách nhiệm. Chênh lệch truy được ngay ca phát sinh. Khách quay lại được đón đúng như ca trước đã hứa.",
      "lamAu": "Bàn giao miệng là nguyên nhân phổ biến nhất gây mâu thuẫn nội bộ. Khi mất hàng thì cả hai đều tin mình đúng và không có gì để đối chiếu.",
      "aiLam": [
        {
          "role": "Người giao ca",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chuẩn bị đủ nội dung trước khi hết ca. Chỉ rời quầy khi người nhận đã ký.",
          "desc": "",
          "step": "Bước 1 đến 7"
        },
        {
          "role": "Người nhận ca",
          "desc": "",
          "step": ""
        },
        {
          "role": "Có mặt sớm 10 phút. Kiểm tra thật trước khi ký. Có quyền không ký.",
          "desc": "",
          "step": "Bước 8 đến 10"
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Rà biên bản mỗi ngày. Xử lý chênh lệch. Tổng hợp theo tuần.",
          "desc": "",
          "step": "Bước 11 đến 13"
        }
      ],
      "phases": [
        {
          "title": "NGƯỜI GIAO CA CHUẨN BỊ",
          "subtitle": "15 phút cuối ca",
          "steps": [
            {
              "num": "1",
              "action": "Trước khi hết ca 15 phút, dừng nhận việc mới trừ khách đang đứng tại quầy.",
              "why": "Bàn giao làm vội trong 3 phút thì chỉ là ký cho xong.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Kết ca trên phần mềm, in hoặc chụp màn hình tổng kết ca.",
              "why": "",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Đếm tiền mặt thực tế hai lần, đối chiếu với số ở bước 2. Ghi cả hai con số vào biên bản kể cả khi khớp.",
              "why": "Ghi cả khi khớp thì mới có chuỗi số liệu để nhìn ra bất thường về sau.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Đếm hàng nhạy cảm theo danh mục đã quy định: thuốc kê đơn giá trị cao, hàng đang khuyến mãi, hàng nhỏ dễ mất.",
              "why": "Đếm hết cả quầy thì không ca nào làm nổi. Danh mục 15–20 mã là vừa đủ để bảo vệ phần lớn giá trị.",
              "tip": "Danh mục này do quản lý lập, dán ngay chỗ giao ca, và rà lại mỗi quý."
            },
            {
              "num": "5",
              "action": "Ghi việc đang dở dang: khách đã đặt hàng chưa lấy, đơn thuốc khách chờ quay lại, hàng nhà cung cấp hẹn giao, khiếu nại chưa xử lý xong.",
              "why": "",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Ghi sự cố phát sinh trong ca, kể cả sự cố nhỏ đã tự xử lý xong: mất điện, nhiệt độ vượt ngưỡng, khách phàn nàn, thiết bị trục trặc, người lạ có biểu hiện bất thường.",
              "why": "Sự cố nhỏ lặp lại nhiều ca mới là thứ đáng lo. Không ghi thì không ai nhìn ra nó đang lặp lại.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Kiểm sổ nhiệt độ, độ ẩm của ca mình đã ghi chưa. Chưa thì ghi ngay, không để ca sau ghi hộ.",
              "why": "Ca sau ghi hộ nghĩa là ghi một con số không ai đo. Đây là cách sổ nhiệt ẩm mất giá trị mà không ai để ý.",
              "tip": ""
            }
          ]
        },
        {
          "title": "NGƯỜI NHẬN CA KIỂM TRA",
          "subtitle": "10 phút trước giờ vào ca",
          "steps": [
            {
              "num": "8",
              "action": "Có mặt trước giờ vào ca 10 phút. Đây là thời gian làm việc, không phải đến sớm cho vui.",
              "why": "Đến đúng giờ vào ca thì bàn giao lấn sang giờ bán hàng và luôn bị làm vội.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Kiểm tra trực tiếp từng mục: đếm lại tiền, nhìn lại hàng nhạy cảm, đọc phần việc dở dang và sự cố.",
              "why": "Đếm lại thật, không nhìn qua. Chữ ký của bạn nghĩa là bạn xác nhận số đó đúng, và từ giờ nó thuộc trách nhiệm của bạn.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Chưa rõ điểm nào thì hỏi ngay tại chỗ. Chưa được giải thích rõ thì chưa ký, và gọi quản lý.",
              "why": "Ký rồi mới thắc mắc thì đã muộn. Quyền không ký là quyền quan trọng nhất của người nhận ca.",
              "tip": "Không cần ngại. Nói: em chưa rõ chỗ này, anh chị giải thích giúp em rồi em ký."
            }
          ]
        },
        {
          "title": "KÝ VÀ KHÉP LẠI",
          "subtitle": "cả hai cùng làm",
          "steps": [
            {
              "num": "11",
              "action": "Hai bên ký vào biên bản, ghi rõ giờ bàn giao thật.",
              "why": "Giờ ghi tròn trịa giống hệt nhau mọi ngày là dấu hiệu biên bản được điền cho có.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Người giao ca chỉ rời quầy sau khi đã có đủ hai chữ ký.",
              "why": "Rời trước là bỏ lại một khoảng trống trách nhiệm mà không ai chịu.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Có chênh lệch tiền hoặc hàng: ghi rõ số chênh và hướng xử lý vào biên bản, gọi quản lý ngay trong ca.",
              "why": "Trong ca thì còn nhớ được chuyện gì đã xảy ra. Hết ngày mới báo thì không ai nhớ nổi.",
              "tip": ""
            }
          ]
        },
        {
          "title": "QUẢN LÝ RÀ LẠI",
          "subtitle": "cuối mỗi ngày và cuối tuần",
          "steps": [
            {
              "num": "14",
              "action": "Cuối mỗi ngày, rà toàn bộ biên bản trong ngày, ký xác nhận, lưu vào kẹp hồ sơ.",
              "why": "Nhân viên biết có người đọc thì viết nghiêm túc. Biết không ai đọc thì ba tuần sau bắt đầu điền cho có.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Cuối tuần, tổng hợp: số lần chênh lệch, tổng mức chênh, sự cố nào lặp lại. Gửi chủ cơ sở.",
              "why": "Con số theo tuần cho thấy xu hướng. Một lần chênh là sai sót, bốn lần trong tuần là vấn đề quy trình.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Ký trước rồi mới kiểm",
          "viSao": "Ca sau đến muộn, ca trước vội về",
          "caiGia": "Chữ ký mất hết ý nghĩa. Mất tiền hoặc mất hàng thì không truy được của ca nào."
        },
        {
          "loi": "Bàn giao miệng vì quen nhau",
          "viSao": "Thân nhau, nói vài câu là xong, để cuối tuần ghi bù",
          "caiGia": "Nguyên nhân phổ biến nhất gây mâu thuẫn nội bộ. Cả hai đều tin mình đúng."
        },
        {
          "loi": "Tự bù tiền cho khớp",
          "viSao": "Lệch ít, ngại bị hỏi",
          "caiGia": "Che mất lỗi đang lặp lại. Chủ phát hiện sau nhiều tháng."
        },
        {
          "loi": "Quầy trống người có chuyên môn lúc giao ca",
          "viSao": "Ca trước về trước khi ca sau tới",
          "caiGia": "Rủi ro chuyên môn khi khách hỏi thuốc, và dễ bị lập biên bản nếu bị kiểm tra đúng lúc."
        }
      ]
    }
  },
  {
    "code": "QT-11",
    "title": "KIỂM QUỸ VÀ CHỐNG THẤT THOÁT",
    "flowId": "flow-tien",
    "flowName": "Dòng Tiền",
    "phase": 2,
    "phaseName": "Đợt 2 - Dòng hàng & tiền",
    "pages": 4,
    "targetRole": "Chủ · quản lý",
    "summary": "Người bán và người đối chiếu phải khác nhau · dấu hiệu thất thoát nhìn qua số liệu · cách kiểm bất chợt mà không gây mất lòng.",
    "keyDetail": "Thất thoát hiếm khi lộ ở tiền mặt - nó lộ ở tỷ lệ huỷ hoá đơn và ở nhóm hàng nhỏ dễ giấu. Nhìn hai chỗ đó trước.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — KIỂM QUỸ VÀ CHỐNG THẤT THOÁT",
      "subtitle": "Bảng này dành cho chủ và quản lý, không dán công khai. Cất trong cặp hồ sơ quản lý.",
      "sections": [
        {
          "title": "NGUYÊN TẮC CỨNG",
          "items": [
            "Người bán hàng và người đối chiếu phải là hai người khác nhau",
            "Không ai vừa bán, vừa huỷ hoá đơn, vừa tự đối chiếu",
            "Mọi khoản chi tại quầy phải có chứng từ, dù nhỏ",
            "Chênh lệch nào cũng ghi, kể cả vài nghìn đồng"
          ]
        },
        {
          "title": "KIỂM HẰNG NGÀY — 10 PHÚT",
          "items": [
            "Đối chiếu tiền mặt cuối ca với tổng kết ca trên máy",
            "Xem số hoá đơn bị huỷ trong ngày — bao nhiêu, ai huỷ, lý do",
            "Xem các giao dịch chỉnh sửa giá thủ công",
            "Xem chi tiền mặt tại quầy có chứng từ không"
          ]
        },
        {
          "title": "SOI SÂU MỖI THÁNG — 4 CHỖ NHÌN TRƯỚC",
          "items": [
            "Tỷ lệ hoá đơn bị huỷ theo từng người bán",
            "Nhóm hàng nhỏ gọn giá trị cao — chênh kiểm kê",
            "Hàng khuyến mãi và hàng tặng — chỗ khó đối soát nhất",
            "Giao dịch vào giờ vắng khách và giờ chuyển ca"
          ]
        },
        {
          "title": "KIỂM BẤT CHỢT — LÀM ĐÚNG CÁCH",
          "items": [
            "Kiểm bất chợt 2–3 lần mỗi tháng, không báo trước",
            "Kiểm CẢ QUẦY chứ không nhắm vào một người",
            "Nói rõ từ đầu đây là quy định chung, không phải nghi ngờ ai",
            "Kết quả tốt thì cũng thông báo, đừng chỉ nói khi có vấn đề"
          ]
        }
      ]
    },
    "sop": {
      "title": "KIỂM QUỸ VÀ CHỐNG THẤT THOÁT",
      "duration": "Dành cho quầy mà chủ không đứng bán cả ngày. Kiểm hằng ngày mất 10 phút, soi sâu mỗi tháng mất 1 tiếng.",
      "lamDung": "Phát hiện rò rỉ khi còn nhỏ. Nhân viên trung thực được bảo vệ khỏi nghi ngờ vô cớ vì mọi thứ đều có số liệu.",
      "lamAu": "Thất thoát hiếm khi lộ ra ở tiền mặt. Nó lộ ở tỷ lệ huỷ hoá đơn và ở nhóm hàng nhỏ — hai chỗ mà không kiểm thì không bao giờ thấy.",
      "aiLam": [
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Kiểm hằng ngày. Báo cáo tuần.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Phần kiểm hằng ngày",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chủ cơ sở",
          "desc": "",
          "step": ""
        },
        {
          "role": "Soi sâu hằng tháng. Quyết định khi có dấu hiệu.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Phần soi sâu",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nhân viên bán hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Không tự đối chiếu phần mình bán. Mọi khoản chi phải có chứng từ.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Tuân thủ nguyên tắc cứng",
          "desc": "Đọc trước khi áp dụng: quy trình này không phải để bắt lỗi ai. Phần lớn chênh lệch tại quầy thuốc là lỗi quy trình chứ không phải gian lận. Nhưng không có quy trình kiểm thì lỗi quy trình và gian lận trông giống hệt nhau, và người trung thực là người chịu thiệt vì bị nghi oan.",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "BỐN NGUYÊN TẮC CỨNG",
          "subtitle": "thiết lập một lần, giữ mãi",
          "steps": [
            {
              "num": "1",
              "action": "Người bán hàng và người đối chiếu cuối ngày phải là hai người khác nhau.",
              "why": "Một người vừa bán vừa tự đối chiếu thì việc đối chiếu không còn ý nghĩa gì. Đây là nguyên tắc nền của mọi hệ thống kiểm soát.",
              "tip": "Quầy ít người thì để chủ đối chiếu qua ảnh chụp và số liệu phần mềm từ xa, không cần có mặt."
            },
            {
              "num": "2",
              "action": "Không ai được vừa bán hàng, vừa có quyền huỷ hoá đơn, vừa tự đối chiếu.",
              "why": "Ba quyền này nằm trong tay một người là kẽ hở lớn nhất của một quầy thuốc. Tách ít nhất một quyền ra là bịt được phần lớn.",
              "tip": "Nếu phần mềm cho phép, đặt quyền huỷ hoá đơn cần mật khẩu của quản lý."
            },
            {
              "num": "3",
              "action": "Mọi khoản chi tiền mặt tại quầy phải có chứng từ, dù nhỏ đến đâu.",
              "why": "Chi lặt vặt không chứng từ là chỗ tiền rò rỉ đều đặn nhất, và cũng là chỗ khó truy nhất vì mỗi lần chỉ vài chục nghìn.",
              "tip": "Một quyển sổ chi nhỏ đặt cạnh két: ngày, nội dung, số tiền, ai chi. Ba dòng là đủ."
            },
            {
              "num": "4",
              "action": "Chênh lệch nào cũng ghi, kể cả vài nghìn đồng.",
              "why": "Ngưỡng bỏ qua là thứ luôn bị nới rộng dần. Bỏ qua 5 nghìn thì tháng sau bỏ qua 20 nghìn.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KIỂM HẰNG NGÀY",
          "subtitle": "10 phút, làm sau khi ca cuối đóng",
          "steps": [
            {
              "num": "5",
              "action": "Đối chiếu tiền mặt thực tế cuối ca với tổng kết ca trên phần mềm.",
              "why": "",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Xem số hoá đơn bị huỷ trong ngày: bao nhiêu cái, ai huỷ, lý do gì.",
              "why": "Đây là chỗ quan trọng hơn cả tiền mặt. Huỷ hoá đơn sau khi khách đã trả tiền là cách thất thoát khó phát hiện nhất nếu không ai nhìn vào con số này.",
              "tip": "Không cần nghi ngờ gì cũng nên nhìn mỗi ngày. Nhìn quen thì con số bất thường tự nhảy ra."
            },
            {
              "num": "7",
              "action": "Xem các giao dịch có chỉnh giá thủ công hoặc giảm giá không theo chương trình.",
              "why": "Giảm giá cho người quen là chuyện bình thường, nhưng phải nhìn thấy được. Không nhìn thấy thì không phân biệt được với thất thoát.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Xem sổ chi tiền mặt tại quầy: có chứng từ đầy đủ không.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "SOI SÂU MỖI THÁNG",
          "subtitle": "1 tiếng, chủ cơ sở làm",
          "steps": [
            {
              "num": "9",
              "action": "Tính tỷ lệ hoá đơn bị huỷ theo từng người bán. So sánh giữa các người và so với tháng trước.",
              "why": "Một người có tỷ lệ huỷ cao hơn hẳn những người khác là dấu hiệu cần tìm hiểu — có thể do thao tác chưa quen, cũng có thể không.",
              "tip": "Tìm hiểu chứ chưa kết luận. Hỏi thẳng và hỏi nhẹ nhàng: anh thấy tháng này em huỷ nhiều hơn, có gì vướng không."
            },
            {
              "num": "10",
              "action": "Đối chiếu kết quả kiểm kê nhóm hàng nhỏ gọn giá trị cao trong tháng.",
              "why": "Thất thoát hàng hoá gần như luôn tập trung ở nhóm nhỏ, dễ cầm, giá cao. Nhóm cồng kềnh gần như không bao giờ mất.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Rà riêng hàng khuyến mãi và hàng tặng.",
              "why": "Đây là nhóm khó đối soát nhất vì thường không được nhập đầy đủ vào máy, và ai cũng mặc định nó không có giá trị.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Xem phân bố giao dịch theo giờ, chú ý giờ vắng khách và giờ chuyển ca.",
              "why": "Hai khung giờ này ít người chứng kiến nhất. Không có nghĩa là có vấn đề, nhưng đây là chỗ nên nhìn trước.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KIỂM BẤT CHỢT",
          "subtitle": "2–3 lần mỗi tháng, không báo trước",
          "steps": [
            {
              "num": "13",
              "action": "Kiểm bất chợt tiền mặt và một nhóm hàng, chọn ngẫu nhiên, không báo trước.",
              "why": "Biết trước thì không còn là kiểm nữa. Nhưng phải làm đúng cách để không phá vỡ lòng tin.",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Luôn kiểm cả quầy, không nhắm vào một người cụ thể.",
              "why": "Kiểm nhắm vào một người là tuyên bố công khai rằng bạn nghi người đó. Dù đúng hay sai thì quan hệ cũng hỏng.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Nói rõ ngay từ khi tuyển người: đây là quy định chung áp dụng cho mọi ca, không phải nghi ngờ ai.",
              "why": "Nói trước từ đầu thì không ai thấy bị xúc phạm. Bắt đầu kiểm sau ba tháng làm việc mà không giải thích thì ai cũng nghĩ mình đang bị nghi.",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Kết quả tốt thì cũng thông báo, đừng chỉ lên tiếng khi có vấn đề.",
              "why": "Chỉ nói khi có vấn đề thì việc kiểm trở thành thứ đáng sợ. Nói cả khi tốt thì nó thành việc bình thường.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI CÓ DẤU HIỆU — LÀM GÌ VÀ KHÔNG LÀM GÌ",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Chỉ nhìn tiền mặt, không nhìn hoá đơn huỷ",
          "viSao": "Tiền mặt là thứ dễ hiểu, báo cáo huỷ phải đi tìm",
          "caiGia": "Bỏ sót đúng chỗ khó phát hiện nhất. Tiền mặt luôn khớp mà quầy vẫn hụt."
        },
        {
          "loi": "Đặt ngưỡng bỏ qua chênh lệch nhỏ",
          "viSao": "Chênh vài nghìn mà truy thì mất thời gian",
          "caiGia": "Ngưỡng luôn bị nới rộng dần. Sau nửa năm thì bỏ qua cả những khoản đáng kể."
        },
        {
          "loi": "Nêu nghi ngờ khi chưa có số liệu",
          "viSao": "Cảm giác rất mạnh và rất muốn nói ra",
          "caiGia": "Mất người trung thực, và người thật sự có vấn đề thì kịp xoá dấu vết."
        }
      ]
    }
  },
  {
    "code": "QT-12",
    "title": "ĐỐI SOÁT CÔNG NỢ NHÀ CUNG CẤP",
    "flowId": "flow-tien",
    "flowName": "Dòng Tiền",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 3,
    "targetRole": "Chủ · quản lý",
    "summary": "Gom bill theo tháng · đối chiếu với sao kê của nhà cung cấp · xử lý chênh · thứ tự ưu tiên thanh toán.",
    "keyDetail": "Chênh lệch công nợ gần như luôn bắt nguồn từ hàng khuyến mại và hàng trả lại. Soi hai mục đó trước khi soi cả bảng.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — ĐỐI SOÁT CÔNG NỢ NHÀ CUNG CẤP",
      "subtitle": "Dán cạnh chỗ để kẹp hồ sơ bill. Làm vào ngày cố định mỗi tháng.",
      "sections": [
        {
          "title": "CHUẨN BỊ",
          "items": [
            "Gom đủ bill trong tháng, xếp theo từng nhà cung cấp",
            "Gom biên bản trả hàng, đổi hàng trong tháng",
            "Gom tin nhắn chốt giá của các đơn có nghi ngờ",
            "Xin sao kê công nợ từ nhà cung cấp"
          ]
        },
        {
          "title": "SOI 3 CHỖ NÀY TRƯỚC",
          "items": [
            "HÀNG KHUYẾN MÃI: bên họ tính tiền hay tính 0 đồng",
            "HÀNG TRẢ LẠI: đã trừ chưa, trừ đủ chưa",
            "GIÁ: đơn giá trên bill có khớp giá đã chốt không",
            "Ba chỗ này chiếm phần lớn chênh lệch công nợ"
          ]
        },
        {
          "title": "ĐỐI CHIẾU TỪNG DÒNG",
          "items": [
            "Số bill nào bên mình có mà sao kê không có",
            "Số bill nào sao kê có mà bên mình không có",
            "Tổng tiền từng bill khớp không",
            "Các khoản chiết khấu, thưởng đã trừ đúng chưa"
          ]
        },
        {
          "title": "CÓ CHÊNH THÌ LÀM GÌ",
          "items": [
            "Ghi rõ: số bill · dòng nào · chênh bao nhiêu · vì sao",
            "Gửi bằng tin nhắn kèm ảnh bill khoanh dòng",
            "CHƯA THANH TOÁN phần đang tranh chấp",
            "Vẫn thanh toán đúng hạn phần không tranh chấp"
          ]
        }
      ]
    },
    "sop": {
      "title": "ĐỐI SOÁT CÔNG NỢ NHÀ CUNG CẤP",
      "duration": "Làm vào một ngày cố định cuối tháng, mất 1–2 tiếng tuỳ số nhà cung cấp. Đây là chỗ mất tiền âm thầm nhất trong cả năm.",
      "lamDung": "Không trả thừa một đồng nào. Chiết khấu và thưởng được trừ đủ. Quan hệ với nhà cung cấp rõ ràng vì tháng nào cũng đối soát.",
      "lamAu": "Không đối soát thì mỗi tháng trả thừa một ít, cả năm là con số lớn. Hàng đã trả mà vẫn bị tính tiền là chuyện xảy ra thường xuyên.",
      "aiLam": [
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Gom chứng từ, đối soát từng dòng, lập danh sách chênh lệch.",
          "desc": "",
          "step": "Bước 1 đến 9"
        },
        {
          "role": "Chủ cơ sở",
          "desc": "",
          "step": "Duyệt danh sách chênh, quyết định thanh toán."
        },
        {
          "role": "Bước 10 đến 12",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "CHUẨN BỊ CHỨNG TỪ",
          "subtitle": "30 phút — làm được nhanh nếu trong tháng đã lưu đúng cách",
          "steps": [
            {
              "num": "1",
              "action": "Gom toàn bộ bill trong tháng, xếp theo từng nhà cung cấp, trong mỗi nhà cung cấp xếp theo ngày.",
              "why": "Bill để lộn xộn thì riêng việc tìm đã mất cả buổi. Đây là lý do QT-01 bắt cất bill vào kẹp theo tháng ngay sau khi nhập.",
              "tip": "Dùng kẹp riêng cho mỗi nhà cung cấp lớn. Chi phí vài chục nghìn, tiết kiệm mỗi tháng một tiếng."
            },
            {
              "num": "2",
              "action": "Gom biên bản trả hàng, đổi hàng phát sinh trong tháng.",
              "why": "Đây là nhóm chứng từ hay bị quên nhất, và cũng là nguyên nhân chênh lệch phổ biến nhất.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Gom lại tin nhắn chốt giá của những đơn mà bạn nhớ là có nghi ngờ về giá.",
              "why": "Không cần gom hết. Chỉ những đơn có nghi ngờ, để đối chiếu nhanh.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Xin sao kê công nợ từ nhà cung cấp, yêu cầu chi tiết theo từng bill chứ không chỉ tổng số.",
              "why": "Sao kê chỉ có tổng số thì không đối soát được gì. Cứ yêu cầu chi tiết, đây là việc bình thường.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SOI BA CHỖ NÀY TRƯỚC",
          "subtitle": "phần lớn chênh lệch nằm ở đây",
          "steps": [
            {
              "num": "5",
              "action": "Hàng khuyến mãi và hàng tặng: kiểm xem bên họ tính tiền hay tính không đồng.",
              "why": "Đây là chỗ chênh phổ biến nhất. Hàng lẽ ra tặng nhưng lại được tính vào công nợ là chuyện xảy ra ở gần như mọi nhà cung cấp.",
              "tip": "Đối chiếu với tin nhắn chốt đơn lúc đặt, chỗ ghi rõ tặng gì bao nhiêu."
            },
            {
              "num": "6",
              "action": "Hàng đã trả lại: kiểm xem đã được trừ chưa và trừ đủ chưa.",
              "why": "Trả hàng xong mà bên kia chưa trừ là chuyện thường. Không đối soát thì bạn vẫn trả tiền cho hàng đã gửi về.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Đơn giá từng dòng: đối chiếu với giá đã chốt trong tin nhắn.",
              "why": "Giá trên bill lệch giá chốt là chuyện xảy ra thường xuyên và ít khi cố ý — nhưng không ai tự phát hiện hộ bạn.",
              "tip": ""
            }
          ]
        },
        {
          "title": "ĐỐI CHIẾU TỪNG DÒNG",
          "subtitle": "1 tiếng",
          "steps": [
            {
              "num": "8",
              "action": "Đối chiếu danh sách bill: bill nào bên mình có mà sao kê không có, và ngược lại.",
              "why": "Bill bên mình có mà họ không có nghĩa là bạn đang giữ hàng chưa được ghi nợ — sớm muộn cũng bị truy. Ngược lại là bạn đang bị tính tiền cho hàng chưa nhận.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Đối chiếu tổng tiền từng bill.",
              "why": "",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Kiểm các khoản chiết khấu, thưởng doanh số đã được trừ đúng chưa.",
              "why": "Thưởng doanh số hay bị tính chậm một kỳ hoặc bị bỏ sót. Không nhắc thì rất ít khi được tự động bù.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Lập danh sách chênh lệch: số bill, dòng nào, chênh bao nhiêu, lý do.",
              "why": "Danh sách rõ ràng thì bên kia xử lý trong một lần. Nêu chung chung là bị hỏi lại nhiều vòng.",
              "tip": ""
            }
          ]
        },
        {
          "title": "XỬ LÝ CHÊNH VÀ THANH TOÁN",
          "subtitle": "làm dứt điểm trong tuần đầu tháng sau",
          "steps": [
            {
              "num": "12",
              "action": "Gửi danh sách chênh bằng tin nhắn hoặc email, kèm ảnh bill có khoanh dòng.",
              "why": "Gửi văn bản thì có dấu vết. Gọi điện trao đổi xong không ai ghi lại thì tháng sau lặp lại y nguyên.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Chưa thanh toán phần đang tranh chấp, nhưng vẫn thanh toán đúng hạn phần không tranh chấp.",
              "why": "Giữ lại toàn bộ để gây sức ép là cách nhanh nhất làm hỏng quan hệ và mất chính sách giá tốt. Tách ra là cách làm chuyên nghiệp.",
              "tip": "Nói rõ khi thanh toán: bên em chuyển phần đã khớp, còn X triệu đang chờ anh chị xác nhận lại."
            },
            {
              "num": "14",
              "action": "Theo dõi tới khi chênh được xử lý xong. Chưa xong sau 2 tuần thì báo chủ cơ sở.",
              "why": "",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Ghi lại nguyên nhân chênh của tháng này để tháng sau soi trước đúng chỗ đó.",
              "why": "Chênh lệch thường lặp lại ở cùng một nhà cung cấp và cùng một loại nguyên nhân. Biết trước thì tháng sau đối soát nhanh hơn nhiều.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Không đối soát, cứ theo sao kê mà trả",
          "viSao": "Đối soát mất 2 tiếng, tin nhau cho nhanh",
          "caiGia": "Trả thừa đều đặn mỗi tháng. Cả năm là con số đáng kể mà không ai biết."
        },
        {
          "loi": "Bill để lộn xộn không theo tháng",
          "viSao": "Nhập xong là quăng vào ngăn kéo",
          "caiGia": "Riêng việc tìm bill đã mất cả buổi, nên cuối cùng bỏ luôn việc đối soát."
        },
        {
          "loi": "Giữ lại toàn bộ tiền khi có tranh chấp nhỏ",
          "viSao": "Muốn gây sức ép cho họ xử lý nhanh",
          "caiGia": "Hỏng quan hệ, mất chính sách giá tốt, và lần sau bị giao hàng chậm."
        }
      ]
    }
  },
  {
    "code": "QT-13",
    "title": "ĐÓN KHÁCH VÀ SÁU BƯỚC TƯ VẤN",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 1,
    "phaseName": "Đợt 1 - Bản chạy thử",
    "pages": 5,
    "targetRole": "Cả quầy",
    "summary": "Câu chào chuẩn · sáu bước khai thác theo thứ tự · cách hỏi tế nhị chủ đề nhạy cảm · câu dặn khi giao thuốc · xử lý khi quầy đông.",
    "keyDetail": "Hỏi ai dùng trước khi hỏi triệu chứng. Đảo thứ tự này là hỏng cả cuộc tư vấn - vì thuốc cho trẻ, cho bà bầu, cho người già khác hẳn nhau.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — ĐÓN KHÁCH VÀ SÁU BƯỚC TƯ VẤN",
      "subtitle": "Dán ngay sau quầy, chỗ nhân viên liếc thấy khi đang đứng bán.",
      "sections": [
        {
          "title": "SÁU BƯỚC — ĐÚNG THỨ TỰ NÀY",
          "items": [
            "1. AI DÙNG — người lớn, trẻ em mấy tuổi mấy cân, bà bầu, người già",
            "2. TRIỆU CHỨNG GÌ — bị mấy ngày rồi, nặng hay nhẹ",
            "3. ĐÃ DÙNG GÌ CHƯA — thuốc gì, có đỡ không",
            "4. ĐANG DÙNG THUỐC GÌ KHÁC — bệnh mạn tính, thuốc đang uống",
            "5. CÓ DỊ ỨNG THUỐC NÀO KHÔNG",
            "6. CHỌN THUỐC VÀ DẶN CÁCH DÙNG"
          ]
        },
        {
          "title": "VÌ SAO PHẢI HỎI AI DÙNG TRƯỚC",
          "items": [
            "Hỏi triệu chứng trước rồi mới biết là cho trẻ 2 tuổi",
            "→ phải tư vấn lại từ đầu, khách mất kiên nhẫn",
            "Trẻ em · phụ nữ có thai · cho con bú · người già · người bệnh mạn tính",
            "Năm nhóm này đổi hoàn toàn cách chọn thuốc"
          ]
        },
        {
          "title": "DẤU HIỆU PHẢI KHUYÊN ĐI KHÁM NGAY",
          "items": [
            "Sốt cao liên tục trên 3 ngày không hạ",
            "Đau bụng dữ dội, đau khu trú một chỗ",
            "Khó thở, tức ngực, môi tím",
            "Nôn ra máu, đi ngoài phân đen",
            "Co giật, lơ mơ, nói khó, yếu nửa người",
            "Trẻ dưới 3 tháng tuổi sốt · trẻ bỏ bú, li bì",
            "Triệu chứng nặng lên sau khi đã tự dùng thuốc"
          ]
        },
        {
          "title": "DẶN GÌ KHI GIAO THUỐC",
          "items": [
            "Uống lúc nào, mấy lần một ngày, trước hay sau ăn",
            "Dùng trong bao nhiêu ngày",
            "Tác dụng phụ hay gặp — nói trước để khách không hoảng",
            "Khi nào phải quay lại hoặc đi khám",
            "Cho khách nhắc lại cách dùng của loại quan trọng nhất"
          ]
        },
        {
          "title": "CÂU TUYỆT ĐỐI KHÔNG NÓI",
          "items": [
            "Thuốc này uống là khỏi hẳn",
            "Thuốc này tốt nhất trên thị trường",
            "Cứ uống đi không sao đâu",
            "Bên em bán cả nghìn người rồi có ai bị gì đâu"
          ]
        }
      ]
    },
    "sop": {
      "title": "ĐÓN KHÁCH VÀ SÁU BƯỚC TƯ VẤN",
      "duration": "Từ lúc khách bước vào đến lúc giao thuốc xong. Bình thường 3–5 phút, ca khó có thể 10 phút.",
      "lamDung": "Chọn đúng thuốc ngay lần đầu. Khách thấy được hỏi kỹ nên tin và quay lại. Tránh được những ca đáng lẽ phải đi khám mà lại tự điều trị.",
      "lamAu": "Hỏi thiếu một câu là chọn sai thuốc cho một nhóm đặc biệt. Đây là loại sai lầm mà hậu quả rơi vào người bệnh, không phải vào quầy.",
      "aiLam": [
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ sáu bước và quyết định chọn thuốc.",
          "desc": "",
          "step": "Bước 1 đến 12"
        },
        {
          "role": "Nhân viên không có chuyên môn",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chỉ đón khách, mời chờ, thu tiền, đóng gói. Không tư vấn, không chọn thuốc.",
          "desc": "",
          "step": "Bước 1, 12"
        }
      ],
      "phases": [
        {
          "title": "ĐÓN KHÁCH",
          "subtitle": "10 giây đầu quyết định cả cuộc tư vấn",
          "steps": [
            {
              "num": "1",
              "action": "Nhìn lên, chào, và hỏi một câu mở: anh chị cần em tư vấn gì ạ.",
              "why": "Câu hỏi mở làm khách kể ra vấn đề. Câu hỏi đóng kiểu anh mua gì làm khách chỉ đọc tên thuốc, và bạn mất luôn cơ hội tư vấn.",
              "tip": "Nếu đang bận với khách khác, vẫn nhìn lên gật đầu và nói: anh chị chờ em một chút ạ. Khách không phiền vì phải chờ, họ phiền vì bị lờ đi."
            },
            {
              "num": "2",
              "action": "Quan sát nhanh: khách mua cho ai, có vẻ vội hay có thời gian, có đang khó chịu trong người không.",
              "why": "Người đang sốt hoặc đau thì cần được xử lý nhanh và ngồi chờ. Người mua hộ thì cần hỏi kỹ hơn vì họ không tự mô tả được.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SÁU BƯỚC KHAI THÁC",
          "subtitle": "đúng thứ tự — đảo thứ tự là hỏng",
          "steps": [
            {
              "num": "3",
              "action": "Bước 1 — Hỏi AI DÙNG trước tiên: người lớn hay trẻ em, trẻ mấy tuổi và bao nhiêu cân, có đang mang thai hoặc cho con bú không, người già có bệnh nền không.",
              "why": "Đây là chi tiết quan trọng nhất của cả quy trình. Hỏi triệu chứng trước rồi mới phát hiện là cho trẻ 2 tuổi thì phải tư vấn lại từ đầu, và khách mất kiên nhẫn.",
              "tip": "Câu hỏi ngắn nhất: dạ thuốc này mình dùng cho ai ạ. Một câu, năm giây, và nó định hình toàn bộ phần còn lại."
            },
            {
              "num": "4",
              "action": "Bước 2 — Triệu chứng gì, bị mấy ngày rồi, mức độ ra sao.",
              "why": "Số ngày quan trọng ngang triệu chứng. Ho hai ngày và ho ba tuần là hai chuyện hoàn toàn khác nhau.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Bước 3 — Đã dùng thuốc gì chưa, dùng bao lâu, có đỡ không.",
              "why": "Đã dùng mà không đỡ là dấu hiệu cần cân nhắc khuyên đi khám, chứ không phải đổi sang thuốc khác cùng nhóm.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Bước 4 — Đang dùng thuốc gì khác không, có bệnh mạn tính nào đang điều trị không.",
              "why": "Đây là bước hay bị bỏ qua nhất và cũng là bước nguy hiểm nhất khi bỏ qua, nhất là với người cao tuổi đang dùng nhiều thuốc.",
              "tip": "Với người già, hỏi thêm: bác có mang theo đơn hay vỏ hộp thuốc đang uống không ạ. Nhiều người có mang theo."
            },
            {
              "num": "7",
              "action": "Bước 5 — Có dị ứng thuốc nào không.",
              "why": "Câu này mất ba giây và loại bỏ được rủi ro lớn nhất trong cả cuộc tư vấn.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Bước 6 — Chọn thuốc phù hợp và giải thích ngắn vì sao chọn loại này.",
              "why": "Giải thích một câu thôi. Khách hiểu vì sao thì tuân thủ tốt hơn nhiều so với chỉ nhận thuốc.",
              "tip": ""
            }
          ]
        },
        {
          "title": "CÁCH HỎI TẾ NHỊ VỚI CHỦ ĐỀ NHẠY CẢM",
          "subtitle": "",
          "steps": []
        },
        {
          "title": "GIAO THUỐC VÀ DẶN DÒ",
          "subtitle": "đừng để khách cầm thuốc đi mà chưa hiểu",
          "steps": [
            {
              "num": "9",
              "action": "Dặn đủ năm điều: uống lúc nào, mấy lần một ngày, trước hay sau ăn, dùng trong bao nhiêu ngày, khi nào phải quay lại hoặc đi khám.",
              "why": "Thiếu điều cuối cùng là thiếu điều quan trọng nhất. Khách cần biết ranh giới của việc tự điều trị.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Nói trước tác dụng phụ hay gặp.",
              "why": "Nói trước thì khách yên tâm khi gặp. Không nói thì họ hoảng, bỏ thuốc giữa chừng, và không quay lại quầy bạn nữa.",
              "tip": "Nói nhẹ: thuốc này có thể hơi buồn ngủ, mình đừng lái xe ngay sau khi uống nhé."
            },
            {
              "num": "11",
              "action": "Cho khách nhắc lại cách dùng của loại quan trọng nhất.",
              "why": "Đây là cách duy nhất biết chắc khách đã hiểu. Hỏi anh hiểu chưa thì ai cũng gật.",
              "tip": "Nói khéo: dạ anh nhắc lại giúp em loại này uống thế nào để em chắc là em dặn rõ ạ."
            },
            {
              "num": "12",
              "action": "Ghi cách dùng lên vỏ hộp hoặc lên túi nếu đơn có nhiều loại.",
              "why": "Về đến nhà là khách quên. Chữ ghi trên hộp là thứ duy nhất còn lại.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI QUẦY ĐÔNG",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Hỏi triệu chứng trước, hỏi ai dùng sau",
          "viSao": "Khách vào là kể luôn triệu chứng, mình đi theo mạch đó",
          "caiGia": "Tư vấn xong mới biết là cho trẻ nhỏ hoặc bà bầu. Phải làm lại từ đầu, hoặc tệ hơn là không phát hiện ra."
        },
        {
          "loi": "Bỏ qua bước hỏi thuốc đang dùng",
          "viSao": "Mất thời gian, và khách thường không nhớ",
          "caiGia": "Bỏ sót tương tác thuốc, nhất là ở người cao tuổi dùng nhiều thuốc cùng lúc."
        },
        {
          "loi": "Không nói trước tác dụng phụ",
          "viSao": "Sợ nói ra thì khách không mua",
          "caiGia": "Khách gặp tác dụng phụ thì hoảng, bỏ thuốc, và không quay lại nữa. Mất nhiều hơn là được."
        },
        {
          "loi": "Hỏi khách hiểu chưa thay vì cho nhắc lại",
          "viSao": "Nhanh hơn, và ai cũng gật",
          "caiGia": "Về nhà uống sai. Lần sau khách nói thuốc bên em không có tác dụng."
        }
      ]
    }
  },
  {
    "code": "QT-14",
    "title": "BÁN THUỐC KÊ ĐƠN VÀ CÁCH TỪ CHỐI",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 5,
    "targetRole": "Người có chuyên môn",
    "summary": "Kiểm đơn · xác thực đơn điện tử · 7 trường hợp phải từ chối kèm câu nói mẫu · giữ khách trong khi vẫn từ chối.",
    "keyDetail": "Câu từ chối phải kèm một lối ra cho khách: đi khám ở đâu, bên em giữ hàng sẵn. Từ chối trơn là mất khách vĩnh viễn.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — BÁN THUỐC KÊ ĐƠN VÀ CÁCH TỪ CHỐI",
      "subtitle": "Dán ngay sau quầy. Đây là bảng có rủi ro pháp lý cao nhất — cả quầy phải đọc và ký đã hiểu.",
      "sections": [
        {
          "title": "5 BƯỚC KHI CÓ ĐƠN",
          "items": [
            "Kiểm đơn có hợp lệ không: đủ thông tin · có chữ ký người kê · còn hạn",
            "Đơn điện tử: PHẢI tra cứu xác thực trên hệ thống, không nhìn ảnh chụp màn hình",
            "Hỏi: đã dùng chưa · đang dùng thuốc gì · có dị ứng không · có thai không",
            "Lấy ĐÚNG thuốc, ĐÚNG hàm lượng, ĐÚNG số lượng trong đơn",
            "Dặn cách dùng và ghi sổ NGAY, không ghi bù"
          ]
        },
        {
          "title": "KHÔNG BÁN TRONG 7 TRƯỜNG HỢP",
          "items": [
            "Không có đơn",
            "Đơn đã hết hạn",
            "Đơn của người khác",
            "Đơn photo, ảnh chụp màn hình không xác thực được",
            "Khách đòi mua nhiều hơn số lượng ghi trong đơn",
            "Người mua có biểu hiện bất thường, mua số lượng lớn bất thường",
            "Người có chuyên môn dược không có mặt"
          ]
        },
        {
          "title": "CÂU TỪ CHỐI CHUẨN — LUÔN KÈM MỘT LỐI RA",
          "items": [
            "Loại này bên em bắt buộc phải có đơn bác sĩ mới bán được ạ",
            "Anh chị đi khám rồi mang đơn qua, bên em giữ sẵn hàng cho mình",
            "Gần đây có phòng khám X, đi bộ mấy phút thôi ạ",
            "Từ chối trơn là mất khách. Từ chối kèm lối ra là giữ được khách"
          ]
        },
        {
          "title": "NHÂN VIÊN KHÔNG CÓ CHUYÊN MÔN DƯỢC",
          "items": [
            "ĐƯỢC: chào khách · mời chờ · thu tiền · đóng gói sau khi đã kiểm",
            "KHÔNG ĐƯỢC: nhận và đánh giá đơn · tự lấy thuốc kê đơn khỏi kệ",
            "KHÔNG ĐƯỢC: hướng dẫn cách dùng · trả lời câu hỏi về bệnh",
            "KHÔNG ĐƯỢC: quyết định bán hay từ chối · ký vào sổ",
            "Câu chuyển tiếp: phần này chị dược sĩ bên em tư vấn kỹ hơn ạ"
          ]
        }
      ]
    },
    "sop": {
      "title": "BÁN THUỐC KÊ ĐƠN VÀ CÁCH TỪ CHỐI",
      "duration": "Từ lúc khách đưa đơn đến lúc giao thuốc. Có đơn hợp lệ thì 4–6 phút. Không có đơn thì đây là quy trình từ chối mà vẫn giữ được khách.",
      "lamDung": "Không bao giờ bán sai. Khách bị từ chối vẫn quay lại vì được chỉ đường tử tế. Sổ ghi đủ nên xuất trình được bất cứ lúc nào.",
      "lamAu": "Bán một hộp thuốc kê đơn không có đơn có thể khiến cơ sở bị xử phạt và đình chỉ. Không có khách nào đáng để đánh đổi cả nhà thuốc.",
      "aiLam": [
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ quy trình. Là người chịu trách nhiệm cuối cùng.",
          "desc": "",
          "step": "Bước 1 đến 11"
        },
        {
          "role": "Nhân viên không có chuyên môn",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chỉ bước 1 và bước 11. Xem bảng ranh giới ở cuối.",
          "desc": "",
          "step": "Bước 1, 11"
        },
        {
          "role": "Trước khi đọc tiếp: bán thuốc kê đơn khi không có đơn hợp lệ là hành vi bị xử phạt và có thể dẫn tới đình chỉ hoạt động. Quy trình này phải được toàn bộ nhân sự đọc và ký xác nhận đã hiểu trước khi đứng quầy.",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "KHI KHÁCH ĐƯA ĐƠN",
          "subtitle": "4–6 phút",
          "steps": [
            {
              "num": "1",
              "action": "Nhận đơn, chào và mời khách chờ trong lúc kiểm tra. Đừng vừa cầm đơn vừa quay đi lấy thuốc.",
              "why": "Cầm đơn đi lấy thuốc luôn là bỏ qua bước kiểm. Dừng lại đọc đơn trước là thói quen quan trọng nhất.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Kiểm tính hợp lệ: đủ thông tin người bệnh · có tên và chữ ký người kê đơn · có tên cơ sở khám chữa bệnh · thuốc ghi rõ tên, hàm lượng, liều dùng, số lượng · còn trong thời hạn của đơn.",
              "why": "Thiếu bất kỳ mục nào cũng làm đơn không đủ căn cứ để cấp phát.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Với đơn điện tử: tra cứu xác thực trên hệ thống trước khi cấp phát. Không cấp phát chỉ dựa vào ảnh chụp màn hình.",
              "why": "Ảnh chụp màn hình không phân biệt được với ảnh đã chỉnh sửa, và không ghi nhận được việc đã cấp phát nên một đơn có thể được dùng lại ở nhiều nơi.",
              "tip": "Nếu hệ thống đang lỗi không tra được, mời khách chờ hoặc quay lại. Không cấp phát khi chưa xác thực."
            },
            {
              "num": "4",
              "action": "Đối chiếu đơn với danh mục thuốc kê đơn, xác định thuốc nào trong đơn phải bán theo đơn.",
              "why": "",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Khai thác thêm: người mua có phải người bệnh không · đã dùng thuốc này lần nào chưa · đang dùng thuốc gì khác · có tiền sử dị ứng thuốc không · có đang mang thai hoặc cho con bú không.",
              "why": "Đơn cho biết bác sĩ kê gì, không cho biết người bệnh đang dùng thêm gì ở nhà. Khoảng trống đó là việc của người bán thuốc.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Phát hiện bất thường trong đơn — liều bất thường, tương tác đáng lưu ý, trùng hoạt chất: liên hệ lại người kê đơn hoặc hướng dẫn khách quay lại cơ sở khám chữa bệnh. Không tự ý sửa đơn. Ghi vào sổ.",
              "why": "Tự ý sửa đơn là vượt quá phạm vi và có thể gây hại. Nhưng im lặng bán theo đơn có vấn đề cũng không phải cách làm đúng.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Lấy đúng thuốc, đúng hàm lượng, đúng số lượng ghi trong đơn. Kiểm hạn dùng và cảm quan trước khi giao. Không bán vượt số lượng.",
              "why": "",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Dặn cách dùng đầy đủ theo QT-13, nhấn thêm: không được tự ý dừng khi nào, dấu hiệu phải quay lại khám ngay.",
              "why": "Với thuốc kê đơn thì việc dùng đủ liệu trình quan trọng hơn hẳn so với thuốc thông thường.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Ghi vào sổ theo dõi bán thuốc kê đơn NGAY, không để cuối ngày.",
              "why": "Ghi bù cuối ngày là ghi theo trí nhớ. Số liệu sai và nét chữ giống nhau ở nhiều dòng liền là dấu hiệu bị phát hiện ngay khi đối chiếu.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Lưu đơn hoặc bản sao đơn theo quy định. Với đơn điện tử, lưu thông tin xác thực trên hệ thống.",
              "why": "",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Thu tiền và giao thuốc. Bước này nhân viên không có chuyên môn được làm, sau khi thuốc đã được người có chuyên môn lấy, kiểm và tư vấn xong.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "BẢY TRƯỜNG HỢP PHẢI TỪ CHỐI — VÀ CÂU NÓI CỤ THỂ",
          "subtitle": "",
          "steps": []
        },
        {
          "title": "TUYỆT ĐỐI KHÔNG ĐƯỢC LÀM",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Bán cho khách quen không có đơn",
          "viSao": "Khách quen, nể nang, khách nói bác sĩ dặn rồi",
          "caiGia": "Lỗi nặng nhất của một cơ sở bán lẻ thuốc. Có thể bị xử phạt và đình chỉ."
        },
        {
          "loi": "Nhìn ảnh chụp đơn điện tử rồi bán luôn",
          "viSao": "Trên ảnh có đủ thông tin, tra cứu mất thêm thời gian",
          "caiGia": "Không phân biệt được với ảnh đã chỉnh sửa. Một đơn dùng lại nhiều nơi."
        },
        {
          "loi": "Ghi sổ bù vào cuối ngày",
          "viSao": "Đang bán thì không tiện dừng lại ghi",
          "caiGia": "Số liệu sai, nét chữ giống nhau nhiều dòng liền, bị phát hiện ngay khi đối chiếu."
        },
        {
          "loi": "Nhân viên không chuyên môn tự lấy thuốc lúc đông",
          "viSao": "Dược sĩ đang bận, khách đang chờ, hàng thì ở ngay đó",
          "caiGia": "Vi phạm về nhân sự, và là rủi ro chuyên môn thật sự. Đoàn kiểm tra quan sát trực tiếp việc này."
        }
      ]
    }
  },
  {
    "code": "QT-15",
    "title": "BÁN KÈM ĐÚNG CÁCH",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Cả quầy",
    "summary": "Bốn trục bán kèm · thời điểm nói · câu mời theo từng nhóm nhu cầu · dấu hiệu phải dừng lại ngay.",
    "keyDetail": "Nói lời khuyên trước, nhắc sản phẩm sau. Đảo lại là khách nghe ra tiếng bán hàng và đóng cửa ngay.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — BÁN KÈM ĐÚNG CÁCH",
      "subtitle": "Dán sau quầy cạnh bảng sáu bước tư vấn.",
      "sections": [
        {
          "title": "NGUYÊN TẮC SỐ MỘT",
          "items": [
            "NÓI LỜI KHUYÊN TRƯỚC, NHẮC SẢN PHẨM SAU",
            "Đảo lại là khách nghe ra tiếng bán hàng và đóng cửa ngay",
            "Sai: &quot;Anh lấy thêm men tiêu hoá nhé&quot;",
            "Đúng: &quot;Kháng sinh này hay làm rối loạn tiêu hoá. Nếu thấy khó chịu bụng thì có men vi sinh dùng kèm ạ&quot;"
          ]
        },
        {
          "title": "4 TRỤC BÁN KÈM",
          "items": [
            "CÙNG VẤN ĐỀ: hỗ trợ điều trị chính xử lý triệu chứng còn lại",
            "GIẢM TÁC DỤNG PHỤ: men vi sinh với kháng sinh, bảo vệ dạ dày",
            "PHÒNG NGỪA: tăng đề kháng, tránh tái phát",
            "TIỆN ÍCH: nhiệt kế, băng gạc, nước muối, dụng cụ dùng kèm"
          ]
        },
        {
          "title": "THỜI ĐIỂM NÓI",
          "items": [
            "SAU khi đã chọn xong thuốc chính và dặn cách dùng",
            "TRƯỚC khi khách rút ví",
            "Không nói khi khách đang vội hoặc đang khó chịu trong người",
            "Không nói khi phía sau còn hàng người đang chờ"
          ]
        },
        {
          "title": "DẤU HIỆU PHẢI DỪNG NGAY",
          "items": [
            "Khách nói không, một lần là đủ — không nói lại lần hai",
            "Khách nhìn đồng hồ, lùi lại, tay đã cầm túi",
            "Khách hỏi giá bằng giọng khó chịu",
            "Khách nói để lần sau",
            "Dừng đúng lúc quan trọng hơn bán thêm được một món"
          ]
        }
      ]
    },
    "sop": {
      "title": "BÁN KÈM ĐÚNG CÁCH",
      "duration": "Không phải một quy trình riêng mà là cách làm gắn vào bước cuối của mỗi lần tư vấn. Thêm 20–30 giây, không hơn.",
      "lamDung": "Khách được giải quyết trọn vẹn vấn đề chứ không chỉ mua một thứ. Giá trị mỗi lần mua tăng lên mà không cần thêm khách mới.",
      "lamAu": "Bán kèm sai cách là cách nhanh nhất mất một khách quen. Họ không phàn nàn, họ chỉ không quay lại và bạn không bao giờ biết vì sao.",
      "aiLam": [
        {
          "role": "Người có chuyên môn dược",
          "desc": "Đề xuất bán kèm, vì mọi đề xuất đều phải có căn cứ chuyên môn.",
          "step": ""
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nhân viên không có chuyên môn",
          "desc": "",
          "step": ""
        },
        {
          "role": "Không tự đề xuất thuốc. Chỉ được nhắc nhóm tiện ích: nhiệt kế, băng gạc, khẩu trang.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chỉ trục 4",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "NGUYÊN TẮC — THỨ TỰ CÂU NÓI",
          "subtitle": "đây là toàn bộ bí quyết của quy trình này",
          "steps": [
            {
              "num": "1",
              "action": "Nói lời khuyên chuyên môn trước. Nhắc tới sản phẩm sau, và nhắc như một lựa chọn chứ không phải một đề nghị.",
              "why": "Cùng một nội dung, đảo thứ tự thì khách nghe ra hai thứ hoàn toàn khác nhau: một bên là dược sĩ đang dặn dò, một bên là người bán đang chào hàng.",
              "tip": "So sánh: &quot;Anh lấy thêm men tiêu hoá nhé&quot; và &quot;Kháng sinh này hay làm rối loạn tiêu hoá. Nếu thấy khó chịu bụng thì có men vi sinh dùng kèm ạ.&quot; Câu sau bán được, câu trước thì không."
            },
            {
              "num": "2",
              "action": "Mỗi lần chỉ đề xuất một món, tối đa hai. Không kể ra một danh sách.",
              "why": "Ba món trở lên thì khách chuyển từ trạng thái nghe tư vấn sang trạng thái phòng thủ, và thường từ chối cả ba.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Nói xong thì dừng lại, chờ khách phản ứng. Không nói tiếp để lấp khoảng lặng.",
              "why": "Khoảng lặng vài giây là lúc khách cân nhắc. Nói chen vào là phá mất đúng khoảnh khắc đó.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Khách nói không thì dừng ngay, chuyển sang khép lại giao dịch một cách vui vẻ.",
              "why": "Nói lại lần hai gần như không bao giờ đổi được ý khách, nhưng chắc chắn làm họ khó chịu.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI NÀO TUYỆT ĐỐI KHÔNG BÁN KÈM",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Nhắc sản phẩm trước, giải thích sau",
          "viSao": "Ngắn gọn hơn, và người bán nghĩ khách hiểu ngay",
          "caiGia": "Nghe ra tiếng chào hàng. Khách từ chối theo phản xạ dù món đó thật sự cần."
        },
        {
          "loi": "Kể ra ba bốn món cùng lúc",
          "viSao": "Muốn khách có nhiều lựa chọn",
          "caiGia": "Khách chuyển sang phòng thủ và từ chối cả loạt."
        },
        {
          "loi": "Nói lại lần hai khi khách đã từ chối",
          "viSao": "Nghĩ rằng khách chưa hiểu hết lợi ích",
          "caiGia": "Gần như không bao giờ đổi được ý, nhưng chắc chắn làm khách khó chịu và nhớ lâu."
        },
        {
          "loi": "Bán kèm vì đang tồn nhiều chứ không vì khách cần",
          "viSao": "Áp lực xả hàng cận date",
          "caiGia": "Mất lòng tin. Một khách nhận ra điều này sẽ kể lại cho nhiều người khác."
        }
      ]
    }
  },
  {
    "code": "QT-16",
    "title": "12 TÌNH HUỐNG KHÓ TẠI QUẦY",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 1,
    "phaseName": "Đợt 1 - Bản chạy thử",
    "pages": 7,
    "targetRole": "Cả quầy",
    "summary": "Mỗi tình huống một quy trình 4 bước và câu nói mẫu: đòi trả thuốc đã bóc · mua Rx không đơn · uống thuốc bị mệt · quay video trong quầy · khách say · mặc cả gay gắt · đăng bài nói xấu...",
    "keyDetail": "Với khách quay video: không che ống kính, không giật máy. Đứng thẳng, nói bình thường - mọi phản ứng che giấu đều thành bằng chứng chống lại quầy.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — 12 TÌNH HUỐNG KHÓ TẠI QUẦY",
      "subtitle": "Dán ngay sau quầy. Đây là bảng được nhìn nhiều nhất trong cả bộ quy trình.",
      "sections": [
        {
          "title": "BỐN BƯỚC CHUNG CHO MỌI TÌNH HUỐNG",
          "items": [
            "1. HẠ NHIỆT — hạ giọng, chậm lại, không đứng đối diện trực tiếp",
            "2. NGHE HẾT — không cắt lời, dù bạn đã biết họ sẽ nói gì",
            "3. TÁCH RIÊNG — mời sang một góc nếu quầy đang đông",
            "4. NÊU HƯỚNG XỬ LÝ — nói mình sẽ làm gì, không nói mình không làm gì"
          ]
        },
        {
          "title": "BA CÂU CỨU NGUY DÙNG ĐƯỢC MỌI LÚC",
          "items": [
            "&quot;Dạ em nghe anh chị nói ạ.&quot; (rồi im, để họ nói hết)",
            "&quot;Em hiểu vì sao anh chị bực. Để em xem giúp mình ngay ạ.&quot;",
            "&quot;Cái này em cần hỏi lại chị dược sĩ, anh chị chờ em 2 phút nhé.&quot;"
          ]
        },
        {
          "title": "TUYỆT ĐỐI KHÔNG",
          "items": [
            "Không tranh cãi tay đôi, không nói to hơn khách",
            "Không nói &quot;cái này không phải lỗi bên em&quot;",
            "Không nói &quot;thuốc này không thể gây ra như vậy được&quot;",
            "Không che ống kính, không giật điện thoại của khách",
            "Không xử lý một mình khi thấy có nguy cơ mất an toàn"
          ]
        },
        {
          "title": "GỌI NGAY QUẢN LÝ KHI",
          "items": [
            "Khách yêu cầu bồi thường hoặc doạ kiện, doạ đăng bài",
            "Có dấu hiệu mất an toàn cho người hoặc tài sản",
            "Khách phản ánh dùng thuốc gặp vấn đề sức khoẻ",
            "Đoàn kiểm tra vào — chuyển sang QT-24"
          ]
        }
      ]
    },
    "sop": {
      "title": "12 TÌNH HUỐNG KHÓ TẠI QUẦY",
      "duration": "Mỗi tình huống có 4 bước xử lý và câu nói cụ thể. Đọc trước để lúc gặp không phải nghĩ.",
      "lamDung": "Nhân viên không hoảng vì đã biết trước phải làm gì. Phần lớn tình huống được dập tắt ngay tại quầy, không leo thang thành chuyện lớn.",
      "lamAu": "Một câu nói sai lúc căng thẳng biến lời phàn nàn thành bài đăng trên mạng. Mất nhiều tháng để lấy lại thứ mất trong ba mươi giây.",
      "aiLam": [
        {
          "role": "Người đang trực quầy",
          "desc": "",
          "step": ""
        },
        {
          "role": "Bốn bước chung. Xử lý các tình huống mức nhẹ.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": ""
        },
        {
          "role": "Mọi tình huống liên quan chuyên môn và sức khoẻ.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Tình huống 3, 5, 9",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "Được gọi ngay trong các trường hợp ghi ở bảng dán tường.",
          "step": ""
        },
        {
          "role": "Khi được gọi",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "BỐN BƯỚC CHUNG — ÁP CHO MỌI TÌNH HUỐNG",
          "subtitle": "làm trong 30 giây đầu, trước khi nghĩ tới nội dung",
          "steps": [
            {
              "num": "1",
              "action": "Hạ nhiệt trước khi giải quyết. Hạ giọng thấp hơn khách, nói chậm lại, đứng chếch sang một bên chứ không đối diện trực tiếp.",
              "why": "Người đang bực sẽ tự động hạ giọng theo người đối diện sau khoảng ba câu. Đứng đối diện trực tiếp là tư thế đối đầu, đứng chếch bên là tư thế cùng nhìn về một vấn đề.",
              "tip": "Đây là kỹ thuật hiệu quả nhất trong cả quy trình và gần như không ai được dạy. Thử một lần là thấy."
            },
            {
              "num": "2",
              "action": "Nghe hết, không cắt lời, dù bạn đã biết họ sắp nói gì.",
              "why": "Người đang bực cần được nói hết trước khi nghe được bất cứ điều gì. Cắt lời là kéo dài cuộc trò chuyện thêm gấp đôi.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Nếu quầy đang đông, mời khách sang một góc: dạ anh chị qua đây em xem giúp mình cho tiện ạ.",
              "why": "Có khán giả thì cả hai bên đều khó xuống thang. Tách riêng ra là hạ được một nửa mức căng thẳng.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Nêu hướng xử lý — nói mình SẼ LÀM GÌ, không nói mình KHÔNG LÀM ĐƯỢC GÌ.",
              "why": "Cùng một kết quả, nhưng câu mở ra một hướng đi thì khách chấp nhận, còn câu đóng lại thì khách leo thang.",
              "tip": "So sánh: &quot;Bên em không đổi được&quot; và &quot;Để em xem giúp mình phương án này ạ&quot;. Cùng nghĩa, khác hẳn kết cục."
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Cắt lời khách khi đã đoán được họ sắp nói gì",
          "viSao": "Muốn giải quyết nhanh, và thấy mình đã hiểu vấn đề",
          "caiGia": "Khách phải nói lại từ đầu với giọng to hơn. Cuộc trò chuyện dài gấp đôi."
        },
        {
          "loi": "Nói thuốc này không thể gây ra như vậy",
          "viSao": "Phản xạ tự vệ khi bị phản ánh về chuyên môn",
          "caiGia": "Biến một lời phàn nàn thành một cuộc tranh cãi. Đây là câu gây hậu quả nặng nhất trong cả bảng."
        },
        {
          "loi": "Xử lý ngay trước mặt cả hàng người đang chờ",
          "viSao": "Ngại mời khách sang chỗ khác vì sợ họ nghĩ mình né tránh",
          "caiGia": "Có khán giả thì không bên nào xuống thang được."
        }
      ]
    }
  },
  {
    "code": "QT-17",
    "title": "KHIẾU NẠI VÀ PHẢN ÁNH VỀ THUỐC",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Dược sĩ",
    "summary": "Bốn bước xử lý tại chỗ · khi nào phải mời khách đi khám ngay · ghi nhận thế nào · báo cáo và theo dõi sau đó.",
    "keyDetail": "Không bao giờ nói \"thuốc này không thể gây ra như vậy\". Câu đó biến một lời phàn nàn thành một vụ tranh cãi.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — KHIẾU NẠI VÀ PHẢN ÁNH VỀ THUỐC",
      "subtitle": "Dán sau quầy cạnh bảng 12 tình huống. Phần trên cùng là phần dùng khi có người đang khó chịu.",
      "sections": [
        {
          "title": "CÂU HỎI ĐẦU TIÊN — LUÔN LÀ CÂU NÀY",
          "items": [
            "&quot;Dạ hiện giờ người dùng thuốc thế nào rồi ạ?&quot;",
            "Hỏi về NGƯỜI trước, hỏi về HỘP THUỐC sau",
            "Đây là câu vừa đúng chuyên môn vừa làm dịu khách ngay lập tức"
          ]
        },
        {
          "title": "CÓ DẤU HIỆU NẶNG → KHUYÊN ĐI KHÁM NGAY",
          "items": [
            "Khó thở · sưng môi mặt lưỡi · nổi mề đay lan nhanh",
            "Nôn nhiều, không uống được nước",
            "Sốt cao không hạ · lơ mơ · co giật",
            "Đau bụng dữ dội · đi ngoài phân đen · nôn ra máu",
            "Đề nghị gọi giúp xe nếu cần, đừng chỉ nói suông"
          ]
        },
        {
          "title": "GHI NHẬN ĐỦ 6 THỨ",
          "items": [
            "Thuốc gì, hàm lượng, SỐ LÔ (xin xem vỏ hộp)",
            "Mua ngày nào, ở đâu",
            "Dùng thế nào: liều, số lần, mấy ngày",
            "Biểu hiện gì, xuất hiện bao lâu sau khi dùng",
            "Đang dùng thuốc gì khác không",
            "Thông tin liên hệ của khách"
          ]
        },
        {
          "title": "TUYỆT ĐỐI KHÔNG NÓI",
          "items": [
            "Thuốc này không thể gây ra như vậy được",
            "Bên em bán cả nghìn người có ai bị gì đâu",
            "Chắc do anh chị uống sai thôi",
            "Cái này không phải lỗi bên em"
          ]
        }
      ]
    },
    "sop": {
      "title": "KHIẾU NẠI VÀ PHẢN ÁNH VỀ THUỐC",
      "duration": "Từ lúc khách phản ánh đến lúc khép lại. Xử lý tại chỗ 10–15 phút, theo dõi sau đó vài ngày.",
      "lamDung": "Khách được xử lý tử tế thì phần lớn trở thành khách trung thành hơn cả trước khi có sự việc. Ghi nhận đủ thì truy được lô và báo được nhà cung cấp.",
      "lamAu": "Một câu phủ nhận trong ba giây đầu biến người đang lo lắng thành người đang tức giận. Và nếu đúng là thuốc có vấn đề thì bạn đã bỏ lỡ cơ hội phát hiện sớm.",
      "aiLam": [
        {
          "role": "Người tiếp nhận đầu tiên",
          "desc": "",
          "step": ""
        },
        {
          "role": "Hỏi về người bệnh, làm dịu, gọi người có chuyên môn.",
          "desc": "",
          "step": "Bước 1 đến 3"
        },
        {
          "role": "Người có chuyên môn dược",
          "desc": "Đánh giá, quyết định hướng xử lý, ghi nhận chuyên môn.",
          "step": "Bước 4 đến 10"
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Theo dõi sau đó, làm việc với nhà cung cấp nếu cần.",
          "desc": "",
          "step": "Bước 11 đến 13"
        }
      ],
      "phases": [
        {
          "title": "BA MƯƠI GIÂY ĐẦU",
          "subtitle": "quyết định toàn bộ phần còn lại",
          "steps": [
            {
              "num": "1",
              "action": "Hỏi câu đầu tiên về NGƯỜI, không phải về hộp thuốc: &quot;Dạ hiện giờ người dùng thuốc thế nào rồi ạ?&quot;",
              "why": "Đây là chi tiết quan trọng nhất của cả quy trình. Câu này vừa đúng về chuyên môn — vì việc gấp là tình trạng người bệnh — vừa cho khách thấy bạn quan tâm tới người nhà họ chứ không phải tới trách nhiệm của quầy.",
              "tip": "Phản xạ tự nhiên là hỏi mua ngày nào, hộp đâu, còn vỏ không. Phải luyện để đổi phản xạ này."
            },
            {
              "num": "2",
              "action": "Nghe hết, không phủ nhận, không giải thích gì trong lúc khách đang nói.",
              "why": "Mọi câu giải thích trong ba mươi giây đầu đều bị nghe thành chối bỏ trách nhiệm.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Gọi người có chuyên môn dược ra tiếp ngay.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "ĐÁNH GIÁ MỨC ĐỘ",
          "subtitle": "người có chuyên môn làm",
          "steps": [
            {
              "num": "4",
              "action": "Hỏi các dấu hiệu nặng: khó thở, sưng môi mặt lưỡi, nổi mề đay lan nhanh, nôn nhiều, sốt cao không hạ, lơ mơ, co giật, đau bụng dữ dội, đi ngoài phân đen.",
              "why": "Đây là nhóm dấu hiệu cần được khám ngay chứ không phải xử lý tại quầy.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Có dấu hiệu nặng: khuyên đi khám ngay, đề nghị gọi giúp xe nếu cần. Dừng thuốc nghi ngờ.",
              "why": "Đề nghị gọi xe giúp chứ đừng chỉ nói anh chị nên đi khám. Sự khác biệt giữa hai câu là rất lớn với người đang lo.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Không có dấu hiệu nặng: hỏi kỹ cách dùng thực tế của khách.",
              "why": "Phần lớn phản ánh xuất phát từ việc dùng chưa đúng: uống lúc đói khi lẽ ra phải uống sau ăn, uống chung với thứ không nên, uống sai liều. Hỏi kỹ thường ra ngay nguyên nhân.",
              "tip": "Hỏi bằng giọng tìm hiểu chứ không phải giọng kiểm tra: dạ anh chị uống vào lúc nào trong ngày ạ."
            }
          ]
        },
        {
          "title": "GHI NHẬN",
          "subtitle": "làm ngay tại chỗ, không để nhớ rồi ghi sau",
          "steps": [
            {
              "num": "7",
              "action": "Ghi đủ sáu thứ: tên thuốc và hàm lượng · số lô (xin xem vỏ hộp) · ngày mua · cách dùng thực tế · biểu hiện và thời điểm xuất hiện · các thuốc khác đang dùng.",
              "why": "Số lô là thứ quan trọng nhất và cũng là thứ hay quên hỏi nhất. Không có lô thì không truy được gì và không báo được nhà cung cấp.",
              "tip": "Xin chụp ảnh vỏ hộp nếu khách có mang theo. Nhanh hơn chép tay và không sai."
            },
            {
              "num": "8",
              "action": "Xin thông tin liên hệ của khách để báo lại kết quả.",
              "why": "Có gọi lại báo kết quả hay không là thứ phân biệt một quầy làm nghề với một quầy bán hàng.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Nếu khách còn thuốc: đề nghị giữ lại, không vứt đi, kể cả phần đã bóc.",
              "why": "Số lô và tình trạng thuốc thật là bằng chứng duy nhất nếu sau này cần đối chiếu với nhà cung cấp.",
              "tip": ""
            }
          ]
        },
        {
          "title": "XỬ LÝ VÀ THEO DÕI",
          "subtitle": "đừng để việc chết ở đây",
          "steps": [
            {
              "num": "10",
              "action": "Nếu nguyên nhân là dùng chưa đúng: hướng dẫn lại cách dùng đúng, ghi lên hộp, hẹn khách báo lại sau 1–2 ngày.",
              "why": "",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Nếu nghi ngờ chất lượng thuốc: biệt trữ toàn bộ lô đó theo QT-06, không bán tiếp cho tới khi có kết luận.",
              "why": "Một phản ánh có thể là ngẫu nhiên, cũng có thể là dấu hiệu đầu tiên của một lô có vấn đề. Dừng bán trong lúc chờ là chi phí rất nhỏ so với rủi ro.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Nếu nghi ngờ phản ứng có hại của thuốc: ghi vào sổ theo dõi và thực hiện báo cáo theo hướng dẫn hiện hành.",
              "why": "",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Gọi lại cho khách sau 1–2 ngày hỏi tình hình, dù kết luận là gì.",
              "why": "Cuộc gọi này tốn hai phút và là thứ khách nhớ lâu nhất. Rất nhiều khách trở thành khách trung thành sau đúng cuộc gọi này.",
              "tip": "Không cần nói gì phức tạp: dạ em gọi hỏi thăm xem bên nhà mình đỡ chưa ạ."
            },
            {
              "num": "14",
              "action": "Ghi kết quả vào sổ theo dõi khiếu nại và khép lại.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI KHÁCH YÊU CẦU BỒI THƯỜNG",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Hỏi về hộp thuốc trước khi hỏi về người",
          "viSao": "Phản xạ tự nhiên khi bị phản ánh là xác minh sự việc",
          "caiGia": "Khách nghe ra là quầy đang lo trách nhiệm chứ không lo cho người nhà họ. Căng thẳng leo thang ngay."
        },
        {
          "loi": "Phủ nhận trong ba câu đầu",
          "viSao": "Tin chắc là thuốc không gây ra chuyện đó",
          "caiGia": "Biến người đang lo thành người đang tức. Và nếu thuốc thật sự có vấn đề thì đã bỏ lỡ dấu hiệu đầu tiên."
        },
        {
          "loi": "Không xin số lô, không gọi lại sau đó",
          "viSao": "Xử lý xong tại chỗ là coi như xong việc",
          "caiGia": "Không truy được lô, không báo được nhà cung cấp, và mất cơ hội biến sự cố thành lòng tin."
        }
      ]
    }
  },
  {
    "code": "QT-18",
    "title": "CHĂM KHÁCH DÙNG THUỐC DÀI NGÀY",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Nhân viên · quản lý",
    "summary": "Ai đáng theo dõi · ghi lại gì · nhắc trước mấy ngày · mẫu tin nhắn theo tình huống · giới hạn tuyệt đối khi nhắn về thuốc.",
    "keyDetail": "Nhắc trước 3 ngày, không phải đúng ngày hết. Đúng ngày hết là lúc họ đã ghé quầy khác trên đường đi làm.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — CHĂM KHÁCH DÙNG THUỐC DÀI NGÀY",
      "subtitle": "Dán sau quầy. Phần trên dùng lúc bán, phần dưới dùng lúc nhắc khách.",
      "sections": [
        {
          "title": "AI ĐÁNG GHI LẠI ĐỂ NHẮC",
          "items": [
            "Khách mua thuốc mạn tính: huyết áp · tiểu đường · mỡ máu · tuyến giáp",
            "Khách mua thuốc dùng theo liệu trình dài: xương khớp, dạ dày, da liễu",
            "Khách mua thực phẩm bổ sung theo đợt cho người nhà",
            "Khách cao tuổi mua nhiều loại cùng lúc",
            "KHÔNG ghi khách mua thuốc cấp tính thông thường"
          ]
        },
        {
          "title": "GHI GÌ — CHỈ 4 THỨ, 30 GIÂY",
          "items": [
            "Tên gọi khách (biệt danh cũng được) và số Zalo",
            "Thuốc gì, dùng mỗi ngày bao nhiêu",
            "Mua ngày nào, đủ dùng bao nhiêu ngày",
            "Ngày dự kiến hết → ngày cần nhắc = trừ đi 3 ngày"
          ]
        },
        {
          "title": "NHẮC TRƯỚC 3 NGÀY — KHÔNG PHẢI ĐÚNG NGÀY HẾT",
          "items": [
            "Đúng ngày hết là lúc họ đã ghé quầy khác trên đường đi làm",
            "Nhắn Zalo, đừng gọi — trừ khách cao tuổi thì gọi",
            "Nhắn vào buổi sáng hoặc đầu giờ chiều",
            "Một tin nhắn thôi. Không nhắc lại lần hai nếu không trả lời"
          ]
        },
        {
          "title": "TUYỆT ĐỐI KHÔNG KHI NHẮN TIN",
          "items": [
            "Không quảng cáo thuốc trong tin nhắn",
            "Không nêu tên bệnh của khách trong tin nhắn",
            "Không nhắn vào nhóm chung có nhiều khách",
            "Không nhắn buổi tối muộn hoặc sáng sớm",
            "Không nhắn cho khách đã nói không muốn nhận tin"
          ]
        }
      ]
    },
    "sop": {
      "title": "CHĂM KHÁCH DÙNG THUỐC DÀI NGÀY",
      "duration": "Ghi lại lúc bán mất 30 giây. Nhắc lại trước 3 ngày mất 1 phút. Đây là cách giữ khách rẻ nhất mà một quầy có.",
      "lamDung": "Khách dùng thuốc mạn tính gần như không bao giờ mất đi. Họ mua đều đặn hàng tháng và thường mua thêm cho cả nhà.",
      "lamAu": "Khách mạn tính hết thuốc mà không ai nhắc thì mua ở quầy tiện đường hơn. Mất một khách mạn tính là mất doanh thu đều đặn nhiều năm mà bạn không bao giờ biết vì sao.",
      "aiLam": [
        {
          "role": "Người bán hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Ghi lại lúc bán. 30 giây, làm ngay sau khi giao thuốc.",
          "desc": "",
          "step": "Bước 1 đến 4"
        },
        {
          "role": "Người phụ trách nhắc",
          "desc": "",
          "step": ""
        },
        {
          "role": "Một người cố định. Xem danh sách mỗi sáng và nhắn.",
          "desc": "",
          "step": "Bước 5 đến 9"
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Xem tỷ lệ khách quay lại mỗi tháng.",
          "desc": "",
          "step": "Bước 10"
        }
      ],
      "phases": [
        {
          "title": "GHI LẠI LÚC BÁN",
          "subtitle": "30 giây, làm ngay sau khi giao thuốc",
          "steps": [
            {
              "num": "1",
              "action": "Xác định khách này có thuộc nhóm đáng theo dõi không: mua thuốc mạn tính, thuốc theo liệu trình dài, thực phẩm bổ sung theo đợt, hoặc người cao tuổi mua nhiều loại.",
              "why": "Ghi hết mọi khách thì danh sách phình ra và không ai dùng được. Chỉ ghi nhóm sẽ quay lại đều.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Xin số Zalo bằng một câu tự nhiên, gắn với lợi ích của khách.",
              "why": "Xin số trơn thì nhiều người ngại. Gắn với lợi ích thì gần như ai cũng cho.",
              "tip": "Câu hiệu quả nhất: &quot;Dạ thuốc này của mình khoảng 28 ngày là hết. Anh chị cho em số Zalo để gần hết em nhắn nhắc, khỏi bị gián đoạn ạ.&quot; Nói lý do là để không gián đoạn thuốc, không phải để chăm sóc khách hàng."
            },
            {
              "num": "3",
              "action": "Ghi bốn thứ: tên gọi khách và số Zalo · thuốc gì và liều mỗi ngày · ngày mua và đủ dùng bao nhiêu ngày · ngày cần nhắc.",
              "why": "Ngày cần nhắc là ngày dự kiến hết trừ đi ba ngày. Tính luôn lúc ghi thì sau này chỉ việc nhìn danh sách.",
              "tip": "Cho phép ghi biệt danh thay vì tên thật nếu khách ngại. Mục đích là nhắc được, không phải lập hồ sơ."
            },
            {
              "num": "4",
              "action": "Nói rõ với khách là bạn sẽ nhắn nhắc, và họ có thể bảo dừng bất cứ lúc nào.",
              "why": "Nói trước thì tin nhắn của bạn là thứ được mong đợi. Không nói trước thì nó là tin nhắn lạ.",
              "tip": ""
            }
          ]
        },
        {
          "title": "NHẮC KHÁCH",
          "subtitle": "mỗi sáng 5 phút cho cả danh sách",
          "steps": [
            {
              "num": "5",
              "action": "Mỗi sáng mở danh sách, xem hôm nay cần nhắc những ai.",
              "why": "Làm cố định vào buổi sáng thì thành thói quen. Để lúc rảnh thì không bao giờ rảnh.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Nhắn Zalo với khách bình thường. Gọi điện với khách cao tuổi hoặc khách không dùng Zalo.",
              "why": "Người cao tuổi thường không đọc tin nhắn hoặc đọc mà không biết trả lời. Một cuộc gọi 30 giây hiệu quả hơn nhiều.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Nhắn vào buổi sáng hoặc đầu giờ chiều. Không nhắn tối muộn, không nhắn sáng sớm.",
              "why": "",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Một tin nhắn thôi. Không trả lời thì thôi, không nhắc lại lần hai.",
              "why": "Nhắc lần hai là chuyển từ chăm sóc sang làm phiền. Ranh giới rất mỏng và vượt qua rồi thì không quay lại được.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Ghi lại khách nào đã quay lại, khách nào không.",
              "why": "Đây là số liệu duy nhất cho biết việc này có tác dụng hay không.",
              "tip": ""
            }
          ]
        },
        {
          "title": "RANH GIỚI KHI NHẮN TIN — RẤT QUAN TRỌNG",
          "subtitle": "",
          "steps": []
        }
      ],
      "loiHayGap": [
        {
          "loi": "Nhắc đúng ngày hết thuốc",
          "viSao": "Nghĩ rằng đúng ngày là chuẩn nhất",
          "caiGia": "Đúng ngày hết là lúc họ đã mua ở quầy khác trên đường đi. Muộn mất ba ngày."
        },
        {
          "loi": "Ghi hết mọi khách vào danh sách",
          "viSao": "Nghĩ càng nhiều càng tốt",
          "caiGia": "Danh sách phình ra, mỗi sáng nhìn thấy nản, ba tuần sau bỏ luôn."
        },
        {
          "loi": "Nhắn tin có kèm quảng cáo sản phẩm",
          "viSao": "Đang nhắn rồi thì tiện giới thiệu luôn",
          "caiGia": "Khách chặn tin. Mất luôn cả kênh nhắc thuốc chứ không chỉ mất một lần bán."
        }
      ]
    }
  },
  {
    "code": "QT-19",
    "title": "BÁN QUA ZALO VÀ GIAO THUỐC TẬN NƠI",
    "flowId": "flow-khach",
    "flowName": "Dòng Khách",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Cả quầy",
    "summary": "Thông tin bắt buộc hỏi trước khi nhận đơn · trường hợp từ chối giao · đóng gói và giữ nhiệt · bàn giao và xác nhận · giới hạn với thuốc kê đơn.",
    "keyDetail": "Chụp ảnh gói hàng trước khi giao cho shipper. Khiếu nại thiếu hàng khi giao tận nơi là chuyện xảy ra đều đặn.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — BÁN QUA ZALO VÀ GIAO THUỐC TẬN NƠI",
      "subtitle": "Dán cạnh chỗ đóng gói. Mỗi đơn giao đi tick một lượt.",
      "sections": [
        {
          "title": "HỎI ĐỦ 5 THỨ TRƯỚC KHI NHẬN ĐƠN",
          "items": [
            "Dùng cho ai — người lớn, trẻ mấy tuổi mấy cân, có thai không",
            "Triệu chứng gì, bị mấy ngày",
            "Đang dùng thuốc gì khác không",
            "Có dị ứng thuốc nào không",
            "Địa chỉ và số điện thoại người nhận"
          ]
        },
        {
          "title": "TỪ CHỐI GIAO TRONG 5 TRƯỜNG HỢP",
          "items": [
            "Thuốc kê đơn mà chưa có đơn hợp lệ, chưa xác thực được",
            "Khách mô tả có dấu hiệu nặng — phải khuyên đi khám",
            "Không xác định được ai là người dùng",
            "Đơn có thuốc phải kiểm soát đặc biệt",
            "Địa chỉ hoặc thông tin người nhận không rõ ràng"
          ]
        },
        {
          "title": "ĐÓNG GÓI",
          "items": [
            "Kiểm lại lần cuối theo tin nhắn: đúng thuốc · đúng số lượng",
            "Ghi cách dùng lên hộp hoặc kèm giấy hướng dẫn",
            "Hàng lạnh: túi giữ nhiệt + đá gel, ghi rõ bên ngoài",
            "CHỤP ẢNH GÓI HÀNG TRƯỚC KHI GIAO CHO SHIPPER",
            "Bọc kín, không để nhìn thấy tên thuốc từ bên ngoài"
          ]
        },
        {
          "title": "GIAO VÀ XÁC NHẬN",
          "items": [
            "Nhắn khách: đã gửi, dự kiến mấy giờ tới, mã vận đơn",
            "Khách nhận xong: nhắn hỏi đã nhận đủ chưa",
            "Nhắn dặn lại cách dùng một lần nữa qua Zalo",
            "Lưu lại toàn bộ đoạn tin nhắn của đơn đó"
          ]
        }
      ]
    },
    "sop": {
      "title": "BÁN QUA ZALO VÀ GIAO THUỐC TẬN NƠI",
      "duration": "Từ lúc khách nhắn tới lúc xác nhận đã giao. Đơn đơn giản 15 phút, đơn cần tư vấn có thể lâu hơn.",
      "lamDung": "Phục vụ được khách ở xa và khách không đi lại được. Có ảnh gói hàng nên khiếu nại thiếu hàng xử lý trong hai phút.",
      "lamAu": "Giao thuốc mà không hỏi đủ là tư vấn mù. Không chụp ảnh gói hàng thì mọi khiếu nại thiếu hàng đều thành lời khai của hai bên chống nhau.",
      "aiLam": [
        {
          "role": "Người có chuyên môn dược",
          "desc": "",
          "step": ""
        },
        {
          "role": "Khai thác thông tin, chọn thuốc, quyết định giao hay từ chối.",
          "desc": "",
          "step": "Bước 1 đến 7"
        },
        {
          "role": "Người đóng gói",
          "desc": "",
          "step": ""
        },
        {
          "role": "Kiểm lại, đóng gói, chụp ảnh. Có thể là nhân viên không chuyên môn.",
          "desc": "",
          "step": "Bước 8 đến 11"
        },
        {
          "role": "Người theo dõi đơn",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nhắn xác nhận và theo dõi tới khi khách nhận.",
          "desc": "",
          "step": "Bước 12 đến 14"
        },
        {
          "role": "Nguyên tắc nền: bán qua tin nhắn khó hơn bán tại quầy chứ không dễ hơn, vì bạn không nhìn thấy người bệnh. Mọi thứ bạn quan sát được tại quầy — sắc mặt, cách thở, dáng đi — đều mất. Bù lại bằng cách hỏi kỹ hơn, không phải hỏi ít hơn.",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "KHI KHÁCH NHẮN TỚI",
          "subtitle": "đừng báo giá ngay",
          "steps": [
            {
              "num": "1",
              "action": "Chào và hỏi thông tin trước khi báo giá hay báo có hàng.",
              "why": "Báo giá ngay là biến cuộc trò chuyện thành giao dịch mua bán và bỏ qua toàn bộ phần tư vấn. Sau đó rất khó quay lại hỏi.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Hỏi đủ năm thứ: dùng cho ai · triệu chứng và mấy ngày · đang dùng thuốc gì khác · có dị ứng không · địa chỉ và số điện thoại người nhận.",
              "why": "Bốn câu đầu là sáu bước tư vấn rút gọn cho môi trường tin nhắn. Câu thứ năm là để giao được hàng.",
              "tip": "Gửi cả năm câu trong một tin nhắn, đánh số. Khách trả lời một lượt, nhanh hơn hỏi từng câu."
            },
            {
              "num": "3",
              "action": "Nếu khách mô tả có dấu hiệu nặng: khuyên đi khám ngay, không nhận đơn.",
              "why": "Qua tin nhắn thì bạn càng ít căn cứ để đánh giá. Ngưỡng khuyên đi khám phải thấp hơn so với khi khách đứng trước mặt.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Nếu là thuốc kê đơn: yêu cầu đơn và xác thực theo QT-14. Chưa xác thực được thì không giao.",
              "why": "Giao thuốc kê đơn không có đơn hợp lệ là vi phạm, và giao đi thì còn khó giải thích hơn bán tại quầy.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Nếu không xác định được ai là người dùng: không nhận đơn.",
              "why": "Người nhắn không phải người dùng là chuyện bình thường, nhưng phải biết người dùng là ai. Không biết thì không tư vấn được.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Chốt đơn bằng tin nhắn ghi rõ: tên thuốc, hàm lượng, số lượng, giá, phí giao, tổng tiền.",
              "why": "Ghi rõ trong tin nhắn thì lúc giao không có tranh cãi về số tiền.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Dặn cách dùng ngay trong tin nhắn, trước khi giao.",
              "why": "Dặn lúc giao thì shipper không dặn hộ được. Dặn qua tin nhắn thì khách còn đọc lại được nhiều lần.",
              "tip": ""
            }
          ]
        },
        {
          "title": "ĐÓNG GÓI",
          "subtitle": "5 phút, đừng bỏ bước chụp ảnh",
          "steps": [
            {
              "num": "8",
              "action": "Kiểm lại lần cuối theo đúng tin nhắn chốt đơn: đúng thuốc, đúng hàm lượng, đúng số lượng.",
              "why": "",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Ghi cách dùng lên hộp, hoặc kèm một tờ giấy hướng dẫn nếu đơn có nhiều loại.",
              "why": "Khách đọc tin nhắn một lần rồi quên. Tờ giấy trong gói là thứ còn lại.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Hàng cần bảo quản lạnh: dùng túi giữ nhiệt và đá gel, ghi rõ bên ngoài là hàng cần bảo quản mát.",
              "why": "Không ghi bên ngoài thì shipper để gói hàng trên yên xe giữa trưa.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "CHỤP ẢNH GÓI HÀNG ĐÃ ĐÓNG, và ảnh toàn bộ số thuốc bày ra trước khi bỏ vào gói.",
              "why": "Khiếu nại thiếu hàng khi giao tận nơi là chuyện xảy ra đều đặn. Hai tấm ảnh này giải quyết trong hai phút thay vì tranh cãi cả buổi.",
              "tip": "Chụp ảnh số thuốc bày ra cạnh tờ chốt đơn, giống như cách kiểm nhận hàng ở QT-01."
            },
            {
              "num": "12",
              "action": "Bọc kín, không để nhìn thấy tên thuốc từ bên ngoài.",
              "why": "Gói hàng đi qua tay nhiều người và về tới nhà thì cả nhà nhìn thấy. Đây là chuyện riêng tư của khách.",
              "tip": ""
            }
          ]
        },
        {
          "title": "GIAO VÀ XÁC NHẬN",
          "subtitle": "đừng để đơn trôi mất sau khi giao cho shipper",
          "steps": [
            {
              "num": "13",
              "action": "Nhắn khách: đã gửi, dự kiến mấy giờ tới, mã vận đơn nếu có.",
              "why": "",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Sau khi khách nhận, nhắn hỏi đã nhận đủ chưa và có gì thắc mắc không.",
              "why": "Hỏi trước thì khách nói ngay khi có vấn đề. Không hỏi thì họ tự bực và bạn biết sau ba ngày qua một bài đăng.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Dặn lại cách dùng một lần nữa qua Zalo, ngắn gọn.",
              "why": "Lần dặn thứ hai này là lúc khách thật sự đọc, vì lúc này họ đang cầm thuốc trong tay.",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Lưu lại toàn bộ đoạn tin nhắn của đơn đó.",
              "why": "Đoạn tin nhắn chính là hồ sơ của đơn hàng này: đã hỏi gì, khách trả lời gì, đã dặn gì.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Báo giá ngay khi khách vừa nhắn",
          "viSao": "Khách hỏi giá thì trả lời giá, thấy tự nhiên",
          "caiGia": "Bỏ qua toàn bộ phần khai thác. Sau đó rất khó quay lại hỏi mà không bị thấy là phiền."
        },
        {
          "loi": "Không chụp ảnh gói hàng",
          "viSao": "Đang vội, và nghĩ chắc không có chuyện gì",
          "caiGia": "Khiếu nại thiếu hàng thành lời khai hai bên chống nhau. Thường phải chịu thiệt cho xong."
        },
        {
          "loi": "Hàng lạnh không ghi bên ngoài gói",
          "viSao": "Nghĩ shipper sẽ tự biết",
          "caiGia": "Gói hàng nằm trên yên xe giữa trưa. Thuốc hỏng mà không ai biết."
        }
      ]
    }
  },
  {
    "code": "QT-20",
    "title": "TUYỂN NHÂN VIÊN QUẦY",
    "flowId": "flow-nguoi",
    "flowName": "Dòng Người",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 5,
    "targetRole": "Chủ · quản lý",
    "summary": "Viết tin tuyển thế nào để lọc bớt · 8 câu hỏi phỏng vấn tình huống có thang chấm · thử việc kiểu gì trong 3 ngày đầu · dấu hiệu nên dừng sớm.",
    "keyDetail": "Cho ứng viên đứng cạnh quầy 30 phút quan sát rồi hỏi họ thấy gì. Câu trả lời nói lên nhiều hơn cả buổi phỏng vấn.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — TUYỂN NHÂN VIÊN QUẦY",
      "subtitle": "Bảng này dành cho chủ và quản lý. Cất trong cặp hồ sơ tuyển dụng.",
      "sections": [
        {
          "title": "VIẾT TIN TUYỂN ĐỂ LỌC BỚT, KHÔNG PHẢI ĐỂ THU HÚT",
          "items": [
            "Ghi rõ giờ làm, ca làm, có làm cuối tuần không — ghi thật",
            "Ghi rõ việc phải làm, kể cả việc chân tay",
            "Ghi khoảng lương thật, đừng ghi cao rồi thương lượng xuống",
            "Yêu cầu nhắn tin thay vì gọi — lọc được người cẩu thả ngay từ đầu"
          ]
        },
        {
          "title": "PHỎNG VẤN — HỎI TÌNH HUỐNG, KHÔNG HỎI LÝ THUYẾT",
          "items": [
            "Không hỏi: điểm mạnh điểm yếu của em là gì",
            "Hỏi: khách đòi mua thuốc kê đơn không có đơn, em làm gì",
            "Hỏi: em bán nhầm thuốc cho khách, phát hiện sau 1 tiếng, em làm gì",
            "Hỏi: khách quen xin nợ 200 nghìn, chủ không có mặt, em làm gì",
            "Nghe cách họ NGHĨ, đừng chấm câu trả lời đúng sai"
          ]
        },
        {
          "title": "BÀI KIỂM TRA THẬT — 30 PHÚT ĐỨNG QUẦY",
          "items": [
            "Cho đứng cạnh quầy quan sát 30 phút",
            "Sau đó hỏi: em thấy gì, em có thắc mắc gì",
            "Câu trả lời nói lên nhiều hơn cả buổi phỏng vấn",
            "Người quan sát được chi tiết là người sẽ làm được việc"
          ]
        },
        {
          "title": "DẤU HIỆU NÊN DỪNG SỚM",
          "items": [
            "Nói xấu chỗ làm cũ ngay trong buổi phỏng vấn",
            "Trả lời tình huống bằng cách đổ lỗi cho người khác",
            "Không hỏi lại câu nào về công việc",
            "Đến muộn mà không báo trước",
            "Nói sẽ làm mọi thứ, không có gì mình không làm được"
          ]
        }
      ]
    },
    "sop": {
      "title": "TUYỂN NHÂN VIÊN QUẦY",
      "duration": "Từ lúc đăng tin đến lúc quyết định nhận. Mất khoảng 2 tuần. Tuyển sai một người tốn nhiều hơn để trống vị trí ba tháng.",
      "lamDung": "Tuyển được người phù hợp với thực tế công việc nên họ ở lại lâu. Lọc sớm nên không mất ba tháng đào tạo rồi người ta nghỉ.",
      "lamAu": "Tuyển sai một người mất tiền lương ba tháng, mất công đào tạo, mất khách vì tư vấn kém, và mất luôn thời gian tuyển lại từ đầu.",
      "aiLam": [
        {
          "role": "Chủ cơ sở",
          "desc": "",
          "step": "Quyết định cuối cùng. Phỏng vấn vòng chính."
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Lọc hồ sơ, hẹn lịch, cho thử việc quan sát.",
          "desc": "",
          "step": "Bước 3 đến 9"
        }
      ],
      "phases": [
        {
          "title": "VIẾT TIN TUYỂN",
          "subtitle": "30 phút, viết một lần dùng nhiều lần",
          "steps": [
            {
              "num": "1",
              "action": "Viết tin tuyển để lọc bớt, không phải để thu hút thật nhiều người ứng tuyển.",
              "why": "Nhiều hồ sơ không phải là tốt. Ba mươi hồ sơ không phù hợp tốn thời gian hơn năm hồ sơ phù hợp.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Ghi thật về giờ làm, ca làm, có làm cuối tuần và ngày lễ không.",
              "why": "Đây là lý do nghỉ việc phổ biến nhất trong ba tháng đầu. Ghi thật thì người không chấp nhận được sẽ tự loại mình từ đầu.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Ghi rõ toàn bộ việc phải làm, kể cả việc chân tay: bê hàng, lau kệ, dọn thùng carton.",
              "why": "Nhiều người hình dung công việc quầy thuốc chỉ là đứng tư vấn. Ghi rõ thì không có cú sốc ở tuần đầu.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Ghi khoảng lương thật. Đừng ghi mức cao rồi thương lượng xuống khi phỏng vấn.",
              "why": "Thương lượng xuống là bắt đầu quan hệ bằng một cảm giác bị lừa. Người đó sẽ đi ngay khi có chỗ khác.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Yêu cầu ứng viên nhắn tin giới thiệu ngắn thay vì gọi điện.",
              "why": "Tin nhắn cho thấy cách họ diễn đạt và mức độ cẩn thận. Đây là bộ lọc rẻ nhất và hiệu quả nhất.",
              "tip": "Yêu cầu nhắn đủ ba thông tin: tên, đã làm quầy thuốc chưa và bao lâu, có làm được ca tối không. Ai nhắn thiếu là đã cho bạn một thông tin."
            }
          ]
        },
        {
          "title": "PHỎNG VẤN",
          "subtitle": "30 phút, hỏi tình huống",
          "steps": [
            {
              "num": "6",
              "action": "Bỏ hoàn toàn các câu hỏi lý thuyết kiểu điểm mạnh điểm yếu, mục tiêu năm năm tới.",
              "why": "Ai cũng đã chuẩn bị sẵn câu trả lời cho những câu này. Chúng không cho biết gì về việc người đó sẽ làm gì khi đứng quầy.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Hỏi tám câu tình huống trong bảng dưới. Nghe CÁCH HỌ NGHĨ, không chấm đúng sai.",
              "why": "Người chưa làm nghề sẽ trả lời sai về nghiệp vụ nhưng đúng về thái độ — đó là người đào tạo được. Người trả lời đúng nghiệp vụ nhưng thái độ đổ lỗi thì không sửa được.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Hỏi lý do rời chỗ làm cũ, và nghe cách họ nói về chỗ cũ.",
              "why": "Người nói xấu chỗ cũ trong buổi phỏng vấn sẽ nói xấu chỗ bạn ở buổi phỏng vấn tiếp theo.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Cuối buổi, hỏi họ có câu hỏi gì không.",
              "why": "Không hỏi lại câu nào là dấu hiệu không thật sự quan tâm tới công việc này. Người quan tâm luôn có ít nhất một câu.",
              "tip": ""
            }
          ]
        },
        {
          "title": "THỬ VIỆC BA NGÀY ĐẦU",
          "subtitle": "đây là phần cho biết nhiều nhất",
          "steps": [
            {
              "num": "10",
              "action": "Ngày đầu: cho đứng cạnh quầy quan sát 30 phút, không làm gì cả. Sau đó hỏi em thấy gì, em có thắc mắc gì.",
              "why": "Câu trả lời cho bài này nói lên nhiều hơn cả buổi phỏng vấn. Người nhận ra được chi tiết — khách hay hỏi gì, nhân viên xử lý thế nào, chỗ nào lộn xộn — là người sẽ học nghề nhanh.",
              "tip": "Đây là bài kiểm tra rẻ nhất và chính xác nhất mà một quầy có thể làm. Không tốn gì ngoài 30 phút."
            },
            {
              "num": "11",
              "action": "Ngày một và hai: chỉ cho làm việc không cần chuyên môn — sắp hàng, lau kệ, đóng gói, kiểm hạn dùng.",
              "why": "Vừa an toàn, vừa cho thấy họ có ngại việc chân tay không. Người ngại bê hàng sẽ không hợp với quầy thuốc.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Ngày ba: cho đứng nghe tư vấn, chưa cho tự tư vấn.",
              "why": "Nghe đủ nhiều lượt tư vấn thật là cách học nhanh nhất. Cho tự tư vấn sớm là mạo hiểm với khách.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Cuối ba ngày, quyết định tiếp hay dừng. Dừng thì nói thẳng và trả công đủ ba ngày.",
              "why": "Kéo dài thử việc khi đã biết không hợp là lãng phí thời gian của cả hai bên, và người đó sẽ kể lại cho người khác.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Ghi lương cao trong tin rồi thương lượng xuống",
          "viSao": "Muốn có nhiều người ứng tuyển",
          "caiGia": "Bắt đầu quan hệ bằng cảm giác bị lừa. Người đó đi ngay khi có chỗ khác."
        },
        {
          "loi": "Tuyển vội vì đang thiếu người",
          "viSao": "Quầy đang thiếu, ai cũng được, đào tạo sau",
          "caiGia": "Tuyển sai tốn hơn nhiều so với để trống thêm hai tuần và tuyển đúng."
        },
        {
          "loi": "Hỏi lý thuyết thay vì hỏi tình huống",
          "viSao": "Câu hỏi lý thuyết dễ hỏi và ai cũng hỏi vậy",
          "caiGia": "Không biết gì về người đó cho tới khi họ đứng quầy thật."
        }
      ]
    }
  },
  {
    "code": "QT-21",
    "title": "KÈM NHÂN VIÊN MỚI 30 NGÀY ĐẦU",
    "flowId": "flow-nguoi",
    "flowName": "Dòng Người",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 5,
    "targetRole": "Quản lý",
    "summary": "Tuần 1 làm gì, tuần 2 làm gì · việc chưa được giao cho người mới · bài kiểm cuối mỗi tuần · tiêu chí đạt.",
    "keyDetail": "Tuần đầu không cho bán, chỉ cho xếp hàng và quan sát. Nghe đủ 200 lượt tư vấn trước khi tự tư vấn lần đầu.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — KÈM NHÂN VIÊN MỚI 30 NGÀY ĐẦU",
      "subtitle": "Dán trong khu nhân viên. Người kèm và người mới cùng nhìn vào bảng này.",
      "sections": [
        {
          "title": "TUẦN 1 — CHƯA CHO BÁN",
          "items": [
            "Học vị trí hàng: nhóm nào ở đâu, vì sao xếp thế",
            "Học tên hàng: 50 mã bán chạy nhất",
            "Làm việc chân tay: sắp hàng, kiểm hạn, đóng gói",
            "ĐỨNG NGHE tư vấn — mục tiêu 200 lượt trong tuần",
            "Cuối tuần: chỉ được vị trí 30 nhóm hàng bất kỳ"
          ]
        },
        {
          "title": "TUẦN 2 — BÁN HÀNG ĐƠN GIẢN CÓ NGƯỜI KÈM",
          "items": [
            "Bán hàng khách chủ động hỏi đúng tên",
            "Học sáu bước tư vấn, đọc thuộc thứ tự",
            "Thao tác phần mềm: bán, in hoá đơn, tra tồn",
            "Vẫn có người đứng cạnh mọi lúc",
            "Cuối tuần: thuộc sáu bước và biết vì sao hỏi ai dùng trước"
          ]
        },
        {
          "title": "TUẦN 3 — TỰ TƯ VẤN NHÓM DỄ, CÓ NGƯỜI GIÁM SÁT",
          "items": [
            "Tư vấn nhóm thông thường: cảm, ho, tiêu hoá nhẹ, vết thương ngoài da",
            "Học 12 tình huống khó — đọc và diễn thử",
            "Học các dấu hiệu phải khuyên đi khám",
            "Cuối tuần: xử lý được 5 tình huống giả định"
          ]
        },
        {
          "title": "TUẦN 4 — LÀM GẦN NHƯ ĐỘC LẬP",
          "items": [
            "Tự tư vấn, người kèm chỉ quan sát từ xa",
            "Học quy trình mở ca, đóng ca, bàn giao ca",
            "Học ranh giới: việc gì tuyệt đối không được làm",
            "Cuối tuần: đánh giá tổng, quyết định cho đứng quầy độc lập hay chưa"
          ]
        },
        {
          "title": "CHƯA ĐƯỢC GIAO CHO NGƯỜI MỚI TRONG 30 NGÀY",
          "items": [
            "Bán thuốc kê đơn",
            "Xử lý khiếu nại về thuốc",
            "Nhận hàng và kiểm nhập một mình",
            "Kiểm quỹ, đối chiếu tiền một mình",
            "Tiếp đoàn kiểm tra"
          ]
        }
      ]
    },
    "sop": {
      "title": "KÈM NHÂN VIÊN MỚI 30 NGÀY ĐẦU",
      "duration": "Bốn tuần, mỗi tuần một mục tiêu rõ ràng và một bài kiểm cuối tuần. Làm đủ thì tháng thứ hai họ đứng quầy được một mình.",
      "lamDung": "Sau một tháng họ đứng quầy được thật chứ không chỉ có mặt. Người kèm biết rõ phải dạy gì tuần nào nên không dạy lung tung.",
      "lamAu": "Thả người mới ra quầy sớm là mạo hiểm với khách và làm chính người đó mất tự tin. Không có lộ trình thì tháng thứ ba vẫn phải hỏi từng câu.",
      "aiLam": [
        {
          "role": "Người kèm",
          "desc": "",
          "step": ""
        },
        {
          "role": "Một người cố định trong suốt 30 ngày, không luân phiên.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Kiểm tra cuối mỗi tuần. Quyết định cho qua tuần tiếp hay không.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Bài kiểm cuối tuần",
          "desc": "",
          "step": ""
        },
        {
          "role": "Người mới",
          "desc": "",
          "step": ""
        },
        {
          "role": "Ghi lại mỗi ngày một điều học được. Chủ động hỏi.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nguyên tắc lớn nhất: tuần đầu KHÔNG cho bán. Nghe đủ khoảng 200 lượt tư vấn thật trước khi tự tư vấn lần đầu. Đây là điều mà gần như mọi quầy đều bỏ qua vì đang thiếu người, và cũng là nguyên nhân chính khiến nhân viên mới tư vấn kém suốt nhiều tháng sau đó.",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "TUẦN 1 — HỌC BẰNG MẮT VÀ BẰNG TAY",
          "subtitle": "chưa cho bán, không ngoại lệ",
          "steps": [
            {
              "num": "1",
              "action": "Ngày đầu: dẫn đi một vòng quầy, giải thích vì sao hàng xếp như vậy, nhóm nào ở đâu.",
              "why": "Hiểu logic sắp xếp thì nhớ vị trí nhanh gấp nhiều lần so với học thuộc từng chỗ.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Giao danh sách 50 mã bán chạy nhất. Mỗi ngày học 10 mã: tên, công dụng chính, vị trí trên kệ.",
              "why": "Năm mươi mã chiếm phần lớn số lượt bán. Thuộc 50 mã này là đã làm được việc, thuộc 500 mã thì mất nửa năm.",
              "tip": "Kiểm bằng cách chỉ đại một mã và hỏi để đâu, chứ đừng hỏi lý thuyết."
            },
            {
              "num": "3",
              "action": "Giao việc chân tay: sắp hàng, lau kệ, kiểm hạn dùng, đóng gói, dọn thùng.",
              "why": "Vừa quen mặt hàng vừa quen tay. Kiểm hạn dùng là việc dạy nhanh nhất cách đọc thông tin trên hộp.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Cho đứng cạnh nghe tư vấn. Mục tiêu 200 lượt trong tuần.",
              "why": "Đây là phần quan trọng nhất của cả tháng. Nghe đủ nhiều thì các mẫu hình lặp lại tự hiện ra mà không cần ai giảng.",
              "tip": "Bảo họ ghi lại mỗi ngày ba câu hỏi khách hay hỏi và cách nhân viên trả lời. Cuối tuần đọc lại là có ngay giáo trình của chính quầy mình."
            },
            {
              "num": "5",
              "action": "Cuối tuần 1 — bài kiểm: chỉ đúng vị trí 30 nhóm hàng bất kỳ, gọi đúng tên 50 mã.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "TUẦN 2 — BÁN CÓ NGƯỜI ĐỨNG CẠNH",
          "subtitle": "chưa tự tư vấn",
          "steps": [
            {
              "num": "6",
              "action": "Cho bán những trường hợp khách chủ động hỏi đúng tên và đúng loại.",
              "why": "Đây là nhóm không cần tư vấn nên an toàn, mà vẫn cho họ quen với việc giao tiếp và thao tác.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Dạy sáu bước tư vấn theo QT-13. Bắt đọc thuộc thứ tự và giải thích được vì sao hỏi ai dùng trước.",
              "why": "Thuộc thứ tự chưa đủ. Phải hiểu vì sao, vì lúc thực tế họ sẽ bị khách kéo lệch khỏi thứ tự.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Dạy thao tác phần mềm: bán hàng, in hoá đơn, tra tồn, tra giá.",
              "why": "Lóng ngóng phần mềm trước mặt khách làm mất tự tin nhiều hơn cả tư vấn sai.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Người kèm đứng cạnh mọi lúc, không rời đi.",
              "why": "Tuần 2 mà để họ một mình là quay lại đúng cái sai đang cần tránh.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Cuối tuần 2 — bài kiểm: đọc thuộc sáu bước và giải thích vì sao bước 1 phải là hỏi ai dùng.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "TUẦN 3 — TỰ TƯ VẤN NHÓM DỄ",
          "subtitle": "có người giám sát từ gần",
          "steps": [
            {
              "num": "11",
              "action": "Cho tự tư vấn nhóm thông thường: cảm, ho, tiêu hoá nhẹ, vết thương ngoài da, muỗi đốt.",
              "why": "Chọn nhóm mà sai thì hậu quả nhẹ và dễ sửa. Không cho tư vấn nhóm trẻ nhỏ, phụ nữ có thai, người bệnh mạn tính.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Sau mỗi ca tư vấn, người kèm góp ý riêng ngay, không góp ý trước mặt khách.",
              "why": "Góp ý trước mặt khách làm hỏng cả lòng tin của khách lẫn sự tự tin của người mới.",
              "tip": "Góp ý theo công thức: em làm tốt chỗ này, lần sau thêm chỗ này. Luôn nói điểm được trước."
            },
            {
              "num": "13",
              "action": "Học 12 tình huống khó theo QT-16. Diễn thử ít nhất năm tình huống.",
              "why": "Đọc không đủ. Phải nói ra miệng một lần thì lúc gặp thật mới bật ra được.",
              "tip": "Người kèm đóng vai khách khó. Diễn nghiêm túc, đừng cười."
            },
            {
              "num": "14",
              "action": "Học thuộc danh sách dấu hiệu phải khuyên đi khám ngay.",
              "why": "Đây là danh sách quan trọng nhất về mặt an toàn trong cả tháng đào tạo.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Cuối tuần 3 — bài kiểm: xử lý 5 tình huống giả định, có nêu được dấu hiệu phải khuyên đi khám.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "TUẦN 4 — GẦN NHƯ ĐỘC LẬP",
          "subtitle": "người kèm quan sát từ xa",
          "steps": [
            {
              "num": "16",
              "action": "Cho tự tư vấn, người kèm ở trong quầy nhưng không đứng cạnh.",
              "why": "Có người đứng cạnh thì họ luôn quay sang hỏi. Đứng xa thì họ buộc phải tự quyết, và đó là bước cuối cùng của việc học nghề.",
              "tip": ""
            },
            {
              "num": "17",
              "action": "Dạy quy trình mở ca, đóng ca, bàn giao ca. Cho làm cùng người khác một lượt.",
              "why": "",
              "tip": ""
            },
            {
              "num": "18",
              "action": "Dạy rõ ranh giới: việc gì tuyệt đối không được làm, và khi nào phải gọi người khác.",
              "why": "Biết giới hạn của mình là dấu hiệu quan trọng nhất của một người đã sẵn sàng đứng quầy.",
              "tip": ""
            },
            {
              "num": "19",
              "action": "Cuối tuần 4 — đánh giá tổng: quản lý quan sát một buổi và quyết định cho đứng quầy độc lập hay kéo dài thêm.",
              "why": "Kéo dài thêm hai tuần không phải là thất bại. Cho đứng độc lập khi chưa sẵn sàng mới là thất bại.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Cho bán ngay từ ngày đầu",
          "viSao": "Quầy đang thiếu người, có thêm một tay là đỡ ngay",
          "caiGia": "Tư vấn kém suốt nhiều tháng vì chưa từng nghe đủ. Và mạo hiểm với khách."
        },
        {
          "loi": "Đổi người kèm liên tục",
          "viSao": "Ai rảnh thì kèm",
          "caiGia": "Mỗi người dạy một kiểu, người mới bối rối và không biết theo ai."
        },
        {
          "loi": "Góp ý trước mặt khách",
          "viSao": "Thấy sai thì sửa ngay cho khách khỏi bị tư vấn nhầm",
          "caiGia": "Khách mất lòng tin vào cả quầy, người mới mất tự tin. Nên can thiệp bằng cách tiếp lời chứ không phải sửa lưng."
        }
      ]
    }
  },
  {
    "code": "QT-22",
    "title": "GIAO VIỆC VÀ KIỂM VIỆC KHI CHỦ KHÔNG CÓ MẶT",
    "flowId": "flow-nguoi",
    "flowName": "Dòng Người",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Chủ",
    "summary": "Việc nào giao được việc nào không · giao qua kênh nào · cách kiểm mà không phải đứng canh · nhịp báo cáo trong ngày.",
    "keyDetail": "Giao việc bằng tin nhắn có hạn chót cụ thể, không giao miệng. Việc giao miệng là việc không ai nhớ và không ai chịu trách nhiệm.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — GIAO VIỆC VÀ KIỂM VIỆC KHI CHỦ KHÔNG CÓ MẶT",
      "subtitle": "Bảng này dành cho chủ và quản lý. Không dán công khai — cất trong cặp hồ sơ quản lý.",
      "sections": [
        {
          "title": "NGUYÊN TẮC GIAO VIỆC",
          "items": [
            "Giao bằng TIN NHẮN, không giao miệng",
            "Mỗi việc phải có: làm gì · ai làm · xong lúc nào · báo lại thế nào",
            "Không giao việc mơ hồ kiểu &apos;để ý cái này giúp anh&apos;",
            "Giao cho MỘT người, không giao cho cả nhóm"
          ]
        },
        {
          "title": "3 NHỊP BÁO CÁO TRONG NGÀY",
          "items": [
            "Sáng: ảnh quầy đã mở + đã làm xong checklist mở ca",
            "Trưa hoặc giữa ca: chỉ báo khi có bất thường",
            "Tối: tổng kết ca — doanh số · lệch tiền · sự cố · việc dở"
          ]
        },
        {
          "title": "KIỂM VIỆC MÀ KHÔNG PHẢI ĐỨNG CANH",
          "items": [
            "Xem số liệu phần mềm từ xa mỗi ngày 5 phút",
            "Đọc biên bản bàn giao ca — đọc thật, và cho họ biết mình đọc",
            "Ghé quầy vào giờ ngẫu nhiên, không báo trước",
            "Mỗi tuần một buổi có mặt đủ lâu để quan sát"
          ]
        },
        {
          "title": "VIỆC KHÔNG BAO GIỜ GIAO ĐI",
          "items": [
            "Quyết định giá và mức giảm giá",
            "Duyệt cho nợ",
            "Quyết định nhận hay không nhận một deal lớn",
            "Tuyển và cho nghỉ nhân sự",
            "Tiếp đoàn kiểm tra ở phần quyết định"
          ]
        }
      ]
    },
    "sop": {
      "title": "GIAO VIỆC VÀ KIỂM VIỆC KHI CHỦ KHÔNG CÓ MẶT",
      "duration": "Dành cho chủ quầy không đứng bán cả ngày. Mỗi ngày mất 10 phút, thay cho việc đứng canh cả buổi.",
      "lamDung": "Quầy chạy được khi bạn không có mặt. Bạn biết chuyện gì đang xảy ra mà không phải đứng canh. Nhân viên biết rõ mình được quyết gì.",
      "lamAu": "Giao việc miệng là việc không ai nhớ và không ai chịu trách nhiệm. Không có nhịp báo cáo thì bạn chỉ biết chuyện sau khi nó đã thành chuyện lớn.",
      "aiLam": [
        {
          "role": "Chủ cơ sở",
          "desc": "",
          "step": ""
        },
        {
          "role": "Giao việc, đặt nhịp báo cáo, kiểm việc.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nhận việc, phân xuống, báo cáo đúng nhịp.",
          "desc": "",
          "step": "Bước 5 đến 12"
        }
      ],
      "phases": [
        {
          "title": "GIAO VIỆC ĐÚNG CÁCH",
          "subtitle": "5 phút cho mỗi việc, làm một lần thay vì nhắc ba lần",
          "steps": [
            {
              "num": "1",
              "action": "Giao bằng tin nhắn, không giao miệng. Kể cả khi bạn đang đứng ngay đó.",
              "why": "Việc giao miệng là việc không ai nhớ và không ai chịu trách nhiệm. Đây là nguyên nhân phổ biến nhất của câu em tưởng anh nói là...",
              "tip": "Đang đứng đó vẫn nhắn. Mất 20 giây và loại bỏ hoàn toàn mọi tranh cãi về sau."
            },
            {
              "num": "2",
              "action": "Mỗi việc giao phải có đủ bốn thứ: làm gì cụ thể · ai làm · xong lúc nào · báo lại bằng cách nào.",
              "why": "Thiếu hạn chót thì việc không bao giờ xong. Thiếu cách báo lại thì bạn phải đi hỏi.",
              "tip": "Mẫu: &quot;Em A rà lại hạn dùng kệ số 3, xong trước 5h chiều, chụp ảnh danh sách gửi nhóm nhé.&quot;"
            },
            {
              "num": "3",
              "action": "Giao cho một người cụ thể, không giao cho cả nhóm.",
              "why": "Việc giao cho cả nhóm là việc không ai làm, vì ai cũng nghĩ người khác đang làm.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Không giao việc mơ hồ kiểu để ý cái này giúp anh, xem giúp anh chỗ kia.",
              "why": "Câu mơ hồ thì mỗi người hiểu một kiểu, và khi không xong thì không ai sai cả.",
              "tip": ""
            }
          ]
        },
        {
          "title": "BA NHỊP BÁO CÁO TRONG NGÀY",
          "subtitle": "tổng cộng chưa tới 10 phút của bạn",
          "steps": [
            {
              "num": "5",
              "action": "Nhịp sáng: ảnh quầy đã mở cửa và xác nhận đã làm xong checklist mở ca.",
              "why": "Một tấm ảnh cho biết quầy mở đúng giờ, đèn bật, hàng gọn. Nhiều thứ trong một tấm ảnh.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Nhịp giữa ca: chỉ báo khi có bất thường. Không có gì thì không cần nhắn.",
              "why": "Bắt báo cáo giữa ca khi không có gì là tạo ra tin nhắn rác và làm mọi người bỏ đọc nhóm.",
              "tip": ""
            },
            {
              "num": "7",
              "action": "Nhịp tối: tổng kết ca theo mẫu cố định — doanh số, có lệch tiền không, sự cố gì, việc gì còn dở.",
              "why": "Mẫu cố định thì đọc lướt 15 giây là nắm được, và so sánh giữa các ngày được ngay.",
              "tip": "Dán mẫu tin nhắn tổng kết ngay quầy để ai trực cũng nhắn giống nhau."
            },
            {
              "num": "8",
              "action": "Đọc và phản hồi ngắn, kể cả khi không có gì đặc biệt.",
              "why": "Nhân viên báo cáo mà không bao giờ được phản hồi sẽ ngừng báo cáo sau khoảng ba tuần.",
              "tip": "Một chữ ok cũng được. Quan trọng là họ biết có người đọc."
            }
          ]
        },
        {
          "title": "KIỂM VIỆC MÀ KHÔNG PHẢI ĐỨNG CANH",
          "subtitle": "mỗi ngày 5 phút, mỗi tuần một buổi",
          "steps": [
            {
              "num": "9",
              "action": "Xem số liệu trên phần mềm từ xa mỗi ngày: doanh số, số hoá đơn, hoá đơn huỷ, giảm giá thủ công.",
              "why": "Bốn con số này cho biết phần lớn những gì cần biết mà không cần có mặt.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Đọc biên bản bàn giao ca, và cho nhân viên biết là bạn có đọc.",
              "why": "Biết có người đọc thì viết nghiêm túc. Biết không ai đọc thì ba tuần sau bắt đầu điền cho có.",
              "tip": "Thỉnh thoảng nhắn hỏi về một chi tiết trong biên bản. Một câu hỏi là đủ để cả tháng sau ai cũng viết cẩn thận."
            },
            {
              "num": "11",
              "action": "Ghé quầy vào giờ ngẫu nhiên, không báo trước, nhưng đừng ghé với thái độ đi kiểm tra.",
              "why": "Ghé đúng giờ cố định thì chỉ nhìn thấy một phiên bản đã chuẩn bị của quầy. Ghé ngẫu nhiên với thái độ bình thường thì thấy quầy thật.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Mỗi tuần có mặt một buổi đủ lâu để quan sát cách nhân viên tư vấn khách.",
              "why": "Số liệu không cho biết chất lượng tư vấn. Chỉ có ngồi nghe mới biết.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Giao việc miệng vì đang đứng ngay đó",
          "viSao": "Nói nhanh hơn nhắn",
          "caiGia": "Không ai nhớ chính xác, và khi không xong thì không truy được."
        },
        {
          "loi": "Bắt báo cáo dày nhưng không bao giờ phản hồi",
          "viSao": "Bận, và thấy không có gì để nói",
          "caiGia": "Sau ba tuần nhân viên ngừng báo cáo hoặc báo cáo cho có."
        },
        {
          "loi": "Ghé quầy đúng giờ cố định",
          "viSao": "Tiện đường, quen giờ",
          "caiGia": "Chỉ nhìn thấy phiên bản đã chuẩn bị. Không bao giờ thấy quầy thật."
        }
      ]
    }
  },
  {
    "code": "QT-23",
    "title": "NHÂN VIÊN LÀM SAI VÀ NHÂN VIÊN NGHỈ VIỆC",
    "flowId": "flow-nguoi",
    "flowName": "Dòng Người",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Chủ · quản lý",
    "summary": "Ba mức xử lý theo mức độ · nói chuyện thế nào để họ sửa chứ không nghỉ · bàn giao khi nghỉ việc · thu hồi quyền truy cập.",
    "keyDetail": "Khi nhân viên xin nghỉ, việc đầu tiên là đổi mật khẩu phần mềm và thu chìa khoá, làm trước cả khi bàn giao xong.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — NHÂN VIÊN LÀM SAI VÀ NHÂN VIÊN NGHỈ VIỆC",
      "subtitle": "Bảng này dành cho chủ và quản lý. Cất trong cặp hồ sơ nhân sự, không dán công khai.",
      "sections": [
        {
          "title": "BA MỨC XỬ LÝ KHI LÀM SAI",
          "items": [
            "MỨC 1 — nhắc riêng: sai lần đầu, không cố ý, hậu quả nhỏ",
            "MỨC 2 — nhắc có ghi lại: lặp lại, hoặc hậu quả đáng kể",
            "MỨC 3 — xử lý theo quy định: cố ý, gian dối, gây hậu quả nặng",
            "Không bỏ qua mức 1 rồi nhảy thẳng lên mức 3"
          ]
        },
        {
          "title": "NÓI CHUYỆN THẾ NÀO ĐỂ HỌ SỬA CHỨ KHÔNG NGHỈ",
          "items": [
            "Nói riêng, không nói trước mặt người khác",
            "Nói về VIỆC, không nói về NGƯỜI",
            "Sai: &quot;Em cẩu thả quá&quot; · Đúng: &quot;Bill này thiếu 2 dòng chưa tick&quot;",
            "Hỏi trước khi kết luận: có gì khiến em không làm được bước đó không",
            "Kết bằng một điều cụ thể phải làm khác đi lần sau"
          ]
        },
        {
          "title": "KHI NHÂN VIÊN XIN NGHỈ — LÀM NGAY",
          "items": [
            "Đổi mật khẩu phần mềm — làm TRƯỚC cả khi bàn giao xong",
            "Thu chìa khoá, thu quyền vào nhóm Zalo nội bộ",
            "Chốt công nợ nếu có: tiền tạm ứng, nợ khách do họ duyệt",
            "Kiểm kê nhóm hàng họ phụ trách trước ngày cuối"
          ]
        },
        {
          "title": "BÀN GIAO KHI NGHỈ",
          "items": [
            "Danh sách khách quen họ đang chăm và tình trạng từng người",
            "Việc đang dở dang chưa xong",
            "Mật khẩu, tài khoản, số liên hệ nhà cung cấp họ giữ",
            "Chìa khoá, đồng phục, thiết bị",
            "Ký biên bản bàn giao, hai bên giữ mỗi bên một bản"
          ]
        }
      ]
    },
    "sop": {
      "title": "NHÂN VIÊN LÀM SAI VÀ NHÂN VIÊN NGHỈ VIỆC",
      "duration": "Hai tình huống khác nhau nhưng cùng một nguyên tắc: xử lý việc chứ không xử lý người, và làm dứt khoát.",
      "lamDung": "Người làm sai vì chưa biết thì sửa được và ở lại. Người nghỉ việc ra đi gọn gàng, không để lại lỗ hổng nào.",
      "lamAu": "Nói trước mặt người khác là mất người ngay cả khi họ chỉ sai nhỏ. Không thu quyền truy cập khi nghỉ là để lại một cánh cửa mở vô thời hạn.",
      "aiLam": [
        {
          "role": "Chủ cơ sở",
          "desc": "",
          "step": ""
        },
        {
          "role": "Xử lý mức 2 và mức 3. Quyết định khi cho nghỉ.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "",
          "step": ""
        },
        {
          "role": "Xử lý mức 1. Chuẩn bị hồ sơ cho mức 2, 3.",
          "desc": "",
          "step": "Bước 1 đến 6"
        },
        {
          "role": "Nguyên tắc xuyên suốt: phần lớn cái gọi là nhân viên làm sai thực chất là lỗi quy trình — không ai chỉ rõ phải làm thế nào, hoặc cách làm đúng bất tiện tới mức không ai làm nổi. Trước khi xử lý người, hãy tự hỏi quy trình có chỗ nào khiến việc làm đúng khó hơn làm sai không.",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "KHI PHÁT HIỆN LÀM SAI",
          "subtitle": "đừng phản ứng ngay lúc đang bực",
          "steps": [
            {
              "num": "1",
              "action": "Xác minh trước khi nói. Nghe từ người khác thì kiểm lại, đừng xử lý theo lời kể.",
              "why": "Xử lý dựa trên lời kể chưa xác minh là cách nhanh nhất mất một người tốt và giữ lại một người giỏi kể chuyện.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Phân mức: sai lần đầu không cố ý hậu quả nhỏ là mức 1 · lặp lại hoặc hậu quả đáng kể là mức 2 · cố ý, gian dối, gây hậu quả nặng là mức 3.",
              "why": "Xử lý quá tay với lỗi nhỏ làm mất người. Xử lý quá nhẹ với lỗi nặng làm cả quầy hiểu rằng quy định không có giá trị.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Nói riêng, không bao giờ nói trước mặt người khác hay trong nhóm chung.",
              "why": "Nói trước mặt người khác thì dù bạn đúng đến đâu, người nghe cũng chỉ nhớ cảm giác bị làm nhục. Họ sẽ nghỉ, và thường nghỉ trong im lặng.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Nói về VIỆC, không nói về NGƯỜI.",
              "why": "Nói về việc thì sửa được. Nói về người thì thành một lời phán xét về tính cách, và không ai sửa được tính cách trong một buổi nói chuyện.",
              "tip": "So sánh: &quot;Em cẩu thả quá&quot; và &quot;Bill hôm qua có hai dòng chưa tick mà đã nhập máy&quot;. Câu sau sửa được, câu trước thì không."
            },
            {
              "num": "5",
              "action": "Hỏi trước khi kết luận: có gì khiến em không làm được bước đó không.",
              "why": "Rất nhiều lần câu trả lời cho thấy quy trình có vấn đề chứ không phải người có vấn đề. Và ngay cả khi không, việc được hỏi làm người ta hợp tác thay vì phòng thủ.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Kết thúc bằng một điều cụ thể phải làm khác đi lần sau, không kết bằng một lời nhắc chung chung.",
              "why": "Lần sau em cẩn thận hơn nhé là câu không dẫn tới thay đổi nào. Từ mai em tick từng dòng rồi mới nhập máy là câu có thể kiểm được.",
              "tip": ""
            }
          ]
        },
        {
          "title": "MỨC 2 VÀ MỨC 3",
          "subtitle": "có ghi lại",
          "steps": [
            {
              "num": "7",
              "action": "Mức 2: nói chuyện như trên nhưng ghi lại thành văn bản, hai bên cùng biết nội dung đã trao đổi.",
              "why": "Ghi lại không phải để làm khó ai, mà để nếu lặp lại lần nữa thì cả hai bên đều nhớ đã trao đổi những gì.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Mức 3: xử lý theo quy định của cơ sở và quy định pháp luật lao động. Chuẩn bị đầy đủ căn cứ trước khi nói chuyện.",
              "why": "Xử lý mức nặng mà không có căn cứ rõ ràng là chỗ dễ phát sinh tranh chấp nhất.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Dù ở mức nào, sau khi xử lý người thì phải sửa luôn lỗ hổng quy trình đã cho phép lỗi xảy ra.",
              "why": "Xử lý người mà để nguyên lỗ hổng thì người tiếp theo cũng sẽ mắc đúng lỗi đó.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI NHÂN VIÊN XIN NGHỈ",
          "subtitle": "làm ngay trong ngày họ báo",
          "steps": [
            {
              "num": "10",
              "action": "Đổi mật khẩu phần mềm và thu quyền truy cập ngay trong ngày họ báo nghỉ, trước cả khi bàn giao xong.",
              "why": "Đây không phải vì nghi ngờ ai. Đây là việc chuẩn mực, làm với mọi người, và nên nói rõ như vậy để không ai thấy bị xúc phạm.",
              "tip": "Nói thẳng và nhẹ: bên anh có quy định đổi mật khẩu khi có thay đổi nhân sự, em thông cảm nhé."
            },
            {
              "num": "11",
              "action": "Thu chìa khoá, thu quyền vào nhóm Zalo nội bộ, thu thiết bị và đồng phục.",
              "why": "",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Chốt công nợ: tiền tạm ứng, các khoản khách nợ do họ duyệt, hàng họ giữ.",
              "why": "Để sau ngày cuối cùng thì rất khó liên hệ và rất dễ thành tranh chấp.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Kiểm kê nhóm hàng họ phụ trách trước ngày làm việc cuối.",
              "why": "Kiểm sau khi họ đã nghỉ thì chênh lệch không truy được vào đâu, và không công bằng với chính họ.",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Yêu cầu bàn giao đầy đủ: danh sách khách quen đang chăm và tình trạng từng người · việc đang dở · mật khẩu và tài khoản họ giữ · số liên hệ nhà cung cấp.",
              "why": "Danh sách khách quen là thứ hay bị quên nhất và cũng là thứ mất đi thì đau nhất.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Ký biên bản bàn giao, hai bên giữ mỗi bên một bản.",
              "why": "",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Nói chuyện một lần cuối, hỏi thật lý do nghỉ.",
              "why": "Người sắp đi là người nói thật nhất về quầy của bạn. Đây là nguồn thông tin quý mà rất ít chủ quầy khai thác.",
              "tip": "Hỏi mở: có điều gì nếu làm khác đi thì em đã ở lại không. Và im lặng nghe, đừng phản biện."
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Nhắc lỗi ngay trước mặt khách hoặc đồng nghiệp",
          "viSao": "Đang bực, và thấy tiện thì nói luôn",
          "caiGia": "Người đó nghỉ trong im lặng vài tuần sau. Bạn không bao giờ biết đó là lý do."
        },
        {
          "loi": "Xử lý người mà không sửa quy trình",
          "viSao": "Xử lý người thì nhanh, sửa quy trình thì mất công",
          "caiGia": "Người tiếp theo mắc đúng lỗi đó. Vấn đề lặp lại mãi."
        },
        {
          "loi": "Không thu quyền truy cập khi nhân viên nghỉ",
          "viSao": "Ngại, sợ người ta nghĩ mình nghi ngờ",
          "caiGia": "Để lại một cánh cửa mở vô thời hạn vào phần mềm và dữ liệu của quầy."
        }
      ]
    }
  },
  {
    "code": "QT-24",
    "title": "ĐOÀN KIỂM TRA VÀO ĐỘT XUẤT",
    "flowId": "flow-tinhhuong",
    "flowName": "Tình Huống Bất Ngờ",
    "phase": 1,
    "phaseName": "Đợt 1 - Bản chạy thử",
    "pages": 5,
    "targetRole": "Cả quầy",
    "summary": "Việc làm trong 5 phút đầu · ai đứng ra tiếp · giấy tờ đưa ra theo thứ tự nào · được và không được nói gì · ký biên bản thế nào.",
    "keyDetail": "Xin xem giấy giới thiệu và ghi lại tên, đơn vị trước khi bắt đầu. Làm lịch sự nhưng phải làm - đây là quyền của cơ sở.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — ĐOÀN KIỂM TRA VÀO ĐỘT XUẤT",
      "subtitle": "Dán ở nơi cả quầy nhìn thấy. Đây là bảng mà mọi nhân viên phải đọc trong tuần đầu đi làm.",
      "sections": [
        {
          "title": "5 PHÚT ĐẦU — LÀM ĐÚNG THỨ TỰ NÀY",
          "items": [
            "1. Chào bình thường, mời vào, mời ngồi. Không tỏ ra hoảng",
            "2. XIN XEM GIẤY GIỚI THIỆU hoặc quyết định kiểm tra — đây là quyền của cơ sở",
            "3. Ghi lại: tên · chức vụ · đơn vị · số quyết định · nội dung kiểm tra",
            "4. GỌI NGAY chủ cơ sở và người phụ trách chuyên môn",
            "5. Một người tiếp, những người khác tiếp tục làm việc bình thường"
          ]
        },
        {
          "title": "TRONG LÚC CHỜ NGƯỜI PHỤ TRÁCH TỚI",
          "items": [
            "Mời nước, thái độ hợp tác và bình tĩnh",
            "Nói: &quot;Dạ anh chị chờ em chút, chị dược sĩ phụ trách đang trên đường tới ạ&quot;",
            "Trả lời những gì mình biết chắc, không đoán",
            "Không biết thì nói: &quot;Dạ cái này em chưa nắm, chờ chị phụ trách trả lời ạ&quot;",
            "Không tự ý đưa hồ sơ khi chưa có người phụ trách"
          ]
        },
        {
          "title": "ĐƯA HỒ SƠ THEO THỨ TỰ NÀY",
          "items": [
            "1. Giấy chứng nhận đủ điều kiện kinh doanh dược",
            "2. Chứng chỉ hành nghề của người phụ trách chuyên môn",
            "3. Hồ sơ nhân sự",
            "4. Sổ sách theo dõi: nhiệt ẩm · nhập hàng · bán thuốc kê đơn",
            "5. Hoá đơn chứng từ nhập hàng",
            "6. Bộ quy trình của cơ sở",
            "Đưa đúng thứ hỏi, không đưa cả cặp hồ sơ ra"
          ]
        },
        {
          "title": "TUYỆT ĐỐI KHÔNG",
          "items": [
            "Không cãi tay đôi, không to tiếng, không mỉa mai",
            "Không giấu, không cất vội, không nói dối",
            "Không tự ý đưa tiền hay quà dưới bất kỳ hình thức nào",
            "Không ký vào biên bản khi chưa đọc kỹ",
            "Không ký khi nội dung ghi chưa đúng thực tế"
          ]
        },
        {
          "title": "KHI KÝ BIÊN BẢN",
          "items": [
            "ĐỌC KỸ TOÀN BỘ trước khi ký, không ký vội",
            "Nội dung nào chưa đúng thì đề nghị sửa ngay tại chỗ",
            "Không sửa được thì GHI Ý KIẾN CỦA MÌNH vào biên bản rồi mới ký",
            "Xin một bản của biên bản",
            "Chụp ảnh lại toàn bộ biên bản trước khi họ mang đi"
          ]
        }
      ]
    },
    "sop": {
      "title": "ĐOÀN KIỂM TRA VÀO ĐỘT XUẤT",
      "duration": "30 phút đầu quyết định phần lớn kết quả. Đọc trước và diễn thử một lần, vì lúc gặp thật thì không ai kịp đọc.",
      "lamDung": "Buổi kiểm tra diễn ra đúng trình tự, không phát sinh căng thẳng. Những gì cơ sở làm đúng đều được ghi nhận đúng.",
      "lamAu": "Hoảng loạn, giấu giếm hoặc cãi lại làm buổi kiểm tra chuyển hướng xấu ngay cả khi cơ sở không sai gì nhiều. Ký vào biên bản ghi sai thực tế là tự nhận điều mình không làm.",
      "aiLam": [
        {
          "role": "Người trực quầy lúc đó",
          "desc": "5 phút đầu. Sau đó tiếp tục làm việc bình thường.",
          "step": "Bước 1 đến 6"
        },
        {
          "role": "Người phụ trách chuyên môn",
          "desc": "",
          "step": ""
        },
        {
          "role": "Tiếp chính về nội dung chuyên môn.",
          "desc": "",
          "step": "Bước 7 trở đi"
        },
        {
          "role": "Chủ cơ sở",
          "desc": "Được gọi ngay. Quyết định ở phần ký biên bản.",
          "step": ""
        },
        {
          "role": "Phần ký biên bản",
          "desc": "Đọc trước, đừng đọc lúc gặp: quy trình này chỉ có tác dụng nếu đã đọc từ trước. Nên diễn thử một lần với cả quầy, mất 15 phút, và nó thay đổi hoàn toàn cách mọi người phản ứng khi việc xảy ra thật.",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "5 PHÚT ĐẦU",
          "subtitle": "làm đúng thứ tự, đừng bỏ bước 2",
          "steps": [
            {
              "num": "1",
              "action": "Chào bình thường, mời vào, mời ngồi. Giữ giọng và nhịp nói như với khách bình thường.",
              "why": "Phản ứng hoảng hốt là thứ đầu tiên người kiểm tra nhìn thấy, và nó tạo ấn tượng ngay từ đầu rằng ở đây có gì đó không ổn.",
              "tip": "Nếu tay run thì cứ để tay xuống bàn. Đừng cố che, càng che càng lộ."
            },
            {
              "num": "2",
              "action": "Xin xem giấy giới thiệu hoặc quyết định kiểm tra, một cách lịch sự nhưng phải xin.",
              "why": "Đây là quyền của cơ sở và là việc bình thường trong mọi cuộc kiểm tra. Không xin thì bạn không biết ai đang kiểm và kiểm nội dung gì.",
              "tip": "Câu chuẩn: &quot;Dạ cho em xin phép được xem giấy giới thiệu của đoàn ạ.&quot; Lịch sự, ngắn, và không ai từ chối được."
            },
            {
              "num": "3",
              "action": "Ghi lại: tên, chức vụ, đơn vị công tác, số quyết định, nội dung kiểm tra.",
              "why": "Ghi lại là cách nhắc chính mình rằng đây là một buổi làm việc có trình tự, không phải một sự kiện bất ngờ.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Gọi ngay chủ cơ sở và người phụ trách chuyên môn.",
              "why": "Gọi ngay, không đợi xem họ hỏi gì rồi mới gọi. Người phụ trách cần thời gian di chuyển.",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Một người tiếp đoàn. Những người khác tiếp tục làm việc bình thường, không xúm lại.",
              "why": "Cả quầy xúm lại vừa gây cảm giác căng thẳng, vừa để quầy không có người phục vụ khách.",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Trong lúc chờ: mời nước, giữ thái độ hợp tác. Trả lời những gì mình biết chắc, không đoán.",
              "why": "Đoán một câu sai còn tệ hơn nói không biết. Câu an toàn nhất: &quot;Dạ cái này em chưa nắm, chờ chị phụ trách trả lời ạ.&quot;",
              "tip": ""
            }
          ]
        },
        {
          "title": "LÀM VIỆC VỚI ĐOÀN",
          "subtitle": "người phụ trách tiếp chính",
          "steps": [
            {
              "num": "7",
              "action": "Đưa hồ sơ theo đúng thứ tự được hỏi. Đưa đúng thứ hỏi, không đẩy cả cặp hồ sơ ra bàn.",
              "why": "Đẩy cả cặp ra là mở rộng phạm vi kiểm tra một cách không cần thiết, và làm mất kiểm soát về việc gì đang được xem.",
              "tip": "Sắp hồ sơ theo đúng thứ tự ở bảng dán tường và để trong một cặp riêng. Lấy ra trong 10 giây thay vì lục 5 phút."
            },
            {
              "num": "8",
              "action": "Trả lời trung thực. Chỗ nào cơ sở đang thiếu hoặc đang làm chưa đúng thì nhận, kèm hướng khắc phục.",
              "why": "Nhận và nêu hướng khắc phục là thái độ được ghi nhận. Giấu rồi bị phát hiện là chuyển từ một thiếu sót thành hai vấn đề.",
              "tip": "Mẫu câu: &quot;Dạ chỗ này bên em đang còn thiếu, bên em sẽ hoàn thiện trong [thời gian] ạ.&quot;"
            },
            {
              "num": "9",
              "action": "Không cãi tay đôi, không to tiếng, không mỉa mai, kể cả khi cho rằng mình đúng.",
              "why": "Nếu không đồng ý thì có chỗ để ghi ý kiến vào biên bản. Đó mới là nơi ý kiến của bạn có giá trị, không phải trong một cuộc tranh luận.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Không giấu, không cất vội đồ đạc, không nói dối.",
              "why": "Hành động cất giấu bị nhìn thấy là thứ chuyển buổi kiểm tra sang một hướng hoàn toàn khác.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Không đưa tiền hay quà dưới bất kỳ hình thức nào.",
              "why": "Đây là ranh giới tuyệt đối, không có ngoại lệ và không có cách diễn giải nào khác.",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHI KÝ BIÊN BẢN",
          "subtitle": "đây là bước quan trọng nhất của cả buổi",
          "steps": [
            {
              "num": "12",
              "action": "Đọc kỹ toàn bộ biên bản trước khi ký. Đọc chậm, đọc hết, kể cả khi có người đang chờ.",
              "why": "Biên bản là thứ duy nhất còn lại sau khi mọi người ra về. Mọi điều bạn nói trong buổi hôm đó đều không có giá trị bằng những dòng ghi trong biên bản.",
              "tip": "Cứ nói thẳng: dạ cho em đọc kỹ một chút ạ. Không ai từ chối, và đây là việc hoàn toàn bình thường."
            },
            {
              "num": "13",
              "action": "Nội dung nào ghi chưa đúng thực tế thì đề nghị sửa ngay tại chỗ.",
              "why": "",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Không sửa được thì ghi ý kiến của mình vào phần dành cho ý kiến của cơ sở, rồi mới ký.",
              "why": "Ký mà không ghi ý kiến là mặc nhiên đồng ý với toàn bộ nội dung. Đây là chỗ nhiều cơ sở mất quyền của mình vì không biết.",
              "tip": "Ghi ngắn gọn và nêu sự việc, không tranh luận: cơ sở có thực hiện việc X vào ngày Y, đề nghị xem xét."
            },
            {
              "num": "15",
              "action": "Xin một bản của biên bản, và chụp ảnh lại toàn bộ trước khi họ mang đi.",
              "why": "Có ảnh thì đối chiếu được về sau. Không có gì thì chỉ còn trí nhớ.",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Sau khi đoàn về: họp lại cả quầy, ghi lại toàn bộ diễn biến và các điểm được nhắc.",
              "why": "Ghi ngay khi còn nhớ rõ. Ba ngày sau thì mỗi người nhớ một kiểu.",
              "tip": ""
            },
            {
              "num": "17",
              "action": "Lập kế hoạch khắc phục có thời hạn cho từng điểm được nhắc, và làm thật.",
              "why": "Điểm được nhắc mà lần sau vẫn còn nguyên là tình huống xấu hơn nhiều so với lần đầu bị nhắc.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Hoảng, cất giấu, gọi nhau nhốn nháo",
          "viSao": "Phản xạ tự nhiên khi bất ngờ",
          "caiGia": "Chuyển buổi kiểm tra sang hướng xấu ngay từ phút đầu, kể cả khi cơ sở không sai gì nhiều."
        },
        {
          "loi": "Ký biên bản mà không đọc kỹ",
          "viSao": "Ngại để mọi người chờ, và nghĩ ký cho xong",
          "caiGia": "Tự nhận những điều không đúng thực tế. Sau đó không sửa lại được."
        },
        {
          "loi": "Không ghi ý kiến khi không đồng ý",
          "viSao": "Không biết là mình có quyền ghi",
          "caiGia": "Mặc nhiên đồng ý toàn bộ. Mất hoàn toàn tiếng nói của cơ sở."
        }
      ]
    }
  },
  {
    "code": "QT-25",
    "title": "MẤT ĐIỆN, HỎNG TỦ LẠNH, NGẬP NƯỚC",
    "flowId": "flow-tinhhuong",
    "flowName": "Tình Huống Bất Ngờ",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Cả quầy",
    "summary": "Việc trong 15 phút đầu cho từng loại sự cố · cách đánh giá hàng sau sự cố · khi nào phải bỏ · ghi nhận để khiếu nại bảo hiểm.",
    "keyDetail": "Mất điện thì tuyệt đối không mở tủ lạnh. Tủ đóng kín giữ được nhiệt vài giờ; mở ra một lần là mất hết.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — MẤT ĐIỆN, HỎNG TỦ LẠNH, NGẬP NƯỚC",
      "subtitle": "Dán cạnh tủ lạnh và cạnh tủ điện. Đây là bảng cứu được nhiều tiền nhất trong cả bộ.",
      "sections": [
        {
          "title": "MẤT ĐIỆN — 15 PHÚT ĐẦU",
          "items": [
            "TUYỆT ĐỐI KHÔNG MỞ TỦ LẠNH — dán băng dính lên cửa tủ",
            "Ghi lại giờ bắt đầu mất điện",
            "Gọi hỏi điện lực hoặc hàng xóm: mất bao lâu, cả khu hay riêng mình",
            "Nếu riêng mình: kiểm cầu dao, gọi thợ ngay",
            "Ghi nhiệt độ tủ lạnh lúc trước khi mất điện (lấy số đo gần nhất)"
          ]
        },
        {
          "title": "MẤT ĐIỆN — SAU 2 GIỜ",
          "items": [
            "Chuẩn bị phương án: máy phát · thùng đá · gửi nhờ tủ lạnh nơi khác",
            "Chỉ mở tủ khi đã có chỗ chuyển sang, mở một lần và chuyển hết",
            "Ghi lại toàn bộ mốc thời gian: mất lúc mấy giờ, chuyển lúc mấy giờ",
            "Ghi nhiệt độ ngay khi mở tủ"
          ]
        },
        {
          "title": "HỎNG TỦ LẠNH",
          "items": [
            "Chuyển toàn bộ hàng sang chỗ lạnh khác NGAY, không chờ thợ",
            "Thùng xốp + đá gel là phương án tạm chấp nhận được vài giờ",
            "Ghi nhiệt độ lúc phát hiện và ước lượng đã hỏng bao lâu",
            "Người có chuyên môn dược đánh giá từng mặt hàng sau đó"
          ]
        },
        {
          "title": "NGẬP NƯỚC",
          "items": [
            "Ngắt điện khu vực bị ngập TRƯỚC KHI làm bất cứ việc gì",
            "Chuyển hàng lên cao, ưu tiên hàng giá trị cao và hàng dễ hỏng ẩm",
            "Chụp ảnh toàn cảnh trước khi dọn — cần cho bảo hiểm và cho đối chiếu",
            "Hàng đã ngấm nước: KHÔNG bán, kể cả khi nhìn còn nguyên"
          ]
        },
        {
          "title": "SAU MỌI SỰ CỐ",
          "items": [
            "Người có chuyên môn dược đánh giá từng nhóm hàng",
            "Nhóm nghi ngờ: biệt trữ, dán giấy đỏ, không bán",
            "Ghi đầy đủ mốc thời gian và nhiệt độ vào sổ",
            "Liên hệ nhà cung cấp hỏi ngưỡng chịu đựng của từng mặt hàng"
          ]
        }
      ]
    },
    "sop": {
      "title": "MẤT ĐIỆN, HỎNG TỦ LẠNH, NGẬP NƯỚC",
      "duration": "Mỗi sự cố có việc phải làm trong 15 phút đầu và việc làm trong 24 giờ sau. Đọc trước vì lúc xảy ra thì không kịp tra.",
      "lamDung": "Cứu được phần lớn hàng bảo quản lạnh nhờ không mở tủ. Có mốc thời gian và nhiệt độ đầy đủ nên đánh giá được chính xác cái gì còn dùng được.",
      "lamAu": "Mở tủ lạnh một lần trong lúc mất điện là mất toàn bộ lượng lạnh tích trong tủ. Không ghi mốc thời gian thì sau đó không ai đánh giá được gì và phải bỏ hết cho chắc.",
      "aiLam": [
        {
          "role": "Người trực quầy",
          "desc": "15 phút đầu của mọi sự cố.",
          "step": ""
        },
        {
          "role": "Phần xử lý ngay",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "Được gọi ngay. Quyết định phương án chuyển hàng.",
          "step": ""
        },
        {
          "role": "Phần sau 2 giờ",
          "desc": "",
          "step": ""
        },
        {
          "role": "Người có chuyên môn dược",
          "desc": "Đánh giá hàng sau sự cố. Quyết định giữ hay bỏ.",
          "step": ""
        },
        {
          "role": "Phần đánh giá",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "MẤT ĐIỆN",
          "subtitle": "việc quan trọng nhất là việc KHÔNG làm",
          "steps": [
            {
              "num": "1",
              "action": "KHÔNG MỞ TỦ LẠNH. Dán một mẩu băng dính lên cửa tủ để không ai mở theo phản xạ.",
              "why": "Tủ lạnh đóng kín giữ được nhiệt độ trong vài giờ. Mở ra một lần là mất phần lớn lượng lạnh tích trong đó, và không lấy lại được cho tới khi có điện.",
              "tip": "Đây là điều quan trọng nhất trong cả quy trình này. Phản xạ tự nhiên của mọi người là mở tủ ra xem, và đúng cái phản xạ đó phá hỏng mọi thứ."
            },
            {
              "num": "2",
              "action": "Ghi lại giờ bắt đầu mất điện, và ghi nhiệt độ tủ lạnh theo số đo gần nhất trước đó.",
              "why": "Hai con số này là căn cứ duy nhất để sau đó đánh giá hàng còn dùng được hay không. Không có thì phải bỏ hết cho chắc.",
              "tip": ""
            },
            {
              "num": "3",
              "action": "Gọi hỏi điện lực hoặc hỏi hàng xóm: mất cả khu hay riêng quầy mình, dự kiến bao lâu.",
              "why": "Biết dự kiến bao lâu thì quyết định được có cần chuyển hàng hay không. Mất 30 phút thì không cần làm gì.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Nếu chỉ riêng quầy mình mất điện: kiểm cầu dao, gọi thợ ngay.",
              "why": "",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Sau 2 giờ mà chưa có điện: chuẩn bị phương án chuyển hàng — máy phát, thùng xốp và đá, hoặc gửi nhờ tủ lạnh của cơ sở gần đó.",
              "why": "Chuẩn bị xong hết rồi mới mở tủ. Mở tủ khi chưa có chỗ chuyển sang là mất lạnh mà không được gì.",
              "tip": "Nhà thuốc gần nhất thường sẵn sàng cho gửi nhờ, và bạn cũng sẽ làm vậy cho họ. Có sẵn một số điện thoại như vậy là đáng giá."
            },
            {
              "num": "6",
              "action": "Khi chuyển: mở tủ một lần duy nhất, chuyển hết trong một lượt. Ghi nhiệt độ ngay lúc mở.",
              "why": "Mở ra mở vào nhiều lần là làm hỏng phần còn lại. Nhiệt độ lúc mở là con số quan trọng nhất cho việc đánh giá sau đó.",
              "tip": ""
            }
          ]
        },
        {
          "title": "HỎNG TỦ LẠNH",
          "subtitle": "chuyển hàng trước, gọi thợ sau",
          "steps": [
            {
              "num": "7",
              "action": "Chuyển toàn bộ hàng sang chỗ lạnh khác ngay lập tức, không chờ thợ tới xem.",
              "why": "Chờ thợ mất vài tiếng, và trong vài tiếng đó hàng đang hỏng dần. Thứ tự đúng là cứu hàng trước, sửa tủ sau.",
              "tip": ""
            },
            {
              "num": "8",
              "action": "Phương án tạm: thùng xốp cùng đá gel, hoặc gửi nhờ cơ sở gần đó. Không dùng đá viên tiếp xúc trực tiếp với hộp thuốc.",
              "why": "Đá viên tan ra làm ướt hộp và làm hỏng bao bì, mà nhiệt độ lại không ổn định.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Ghi nhiệt độ lúc phát hiện và ước lượng tủ đã hỏng bao lâu.",
              "why": "Ước lượng dựa vào lần ghi sổ nhiệt độ gần nhất còn bình thường. Đây là lúc sổ nhiệt độ ghi đều đặn phát huy giá trị.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Gọi thợ. Trong lúc chờ, kiểm cả nguồn điện và ổ cắm — nhiều trường hợp là do ổ cắm chứ không phải tủ.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "NGẬP NƯỚC",
          "subtitle": "an toàn điện trước tiên",
          "steps": [
            {
              "num": "11",
              "action": "Ngắt điện khu vực bị ngập trước khi làm bất cứ việc gì khác.",
              "why": "Đây là việc về an toàn con người, đứng trước mọi việc về hàng hoá.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Chuyển hàng lên cao, ưu tiên hàng giá trị cao và hàng dễ hỏng khi ẩm.",
              "why": "",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Chụp ảnh toàn cảnh trước khi bắt đầu dọn.",
              "why": "Cần cho việc đối chiếu bảo hiểm nếu có, và cần cho việc đánh giá thiệt hại sau đó. Dọn xong rồi thì không ai hình dung được mức độ.",
              "tip": ""
            },
            {
              "num": "14",
              "action": "Hàng đã ngấm nước hoặc có thể đã ngấm: không bán, kể cả khi nhìn bên ngoài còn nguyên.",
              "why": "Bao bì thấm nước thì không còn bảo đảm được điều kiện bên trong, dù nhìn không thấy gì.",
              "tip": ""
            },
            {
              "num": "15",
              "action": "Sau khi rút nước: kiểm toàn bộ khu vực, lau khô, chạy máy hút ẩm nếu có, đo lại độ ẩm trước khi xếp hàng trở lại.",
              "why": "Xếp hàng vào khu vực còn ẩm là kéo dài thiệt hại thêm nhiều ngày sau khi nước đã rút.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SAU MỌI SỰ CỐ — ĐÁNH GIÁ HÀNG",
          "subtitle": "người có chuyên môn dược làm",
          "steps": [
            {
              "num": "16",
              "action": "Tập hợp đầy đủ số liệu: sự cố bắt đầu lúc nào, kéo dài bao lâu, nhiệt độ cao nhất ghi nhận được.",
              "why": "",
              "tip": ""
            },
            {
              "num": "17",
              "action": "Liên hệ nhà cung cấp hoặc nhà sản xuất hỏi ngưỡng chịu đựng của từng mặt hàng cụ thể.",
              "why": "Mỗi sản phẩm có ngưỡng khác nhau. Hỏi thay vì đoán, và giữ lại câu trả lời bằng văn bản.",
              "tip": "Câu hỏi cụ thể: sản phẩm X ở nhiệt độ Y trong Z giờ thì còn dùng được không ạ."
            },
            {
              "num": "18",
              "action": "Nhóm không còn bảo đảm: biệt trữ theo QT-06, dán giấy đỏ, xử lý huỷ hoặc đề nghị nhà cung cấp.",
              "why": "",
              "tip": ""
            },
            {
              "num": "19",
              "action": "Ghi toàn bộ diễn biến vào sổ theo dõi, kèm ảnh và các mốc thời gian.",
              "why": "Đây là hồ sơ bạn cần khi làm việc với nhà cung cấp, với bảo hiểm, và khi có kiểm tra sau này.",
              "tip": ""
            },
            {
              "num": "20",
              "action": "Rà lại phương án phòng ngừa: có cần máy phát nhỏ không, có cần thiết bị cảnh báo nhiệt độ không, có số điện thoại cơ sở gửi nhờ chưa.",
              "why": "Sự cố đã xảy ra một lần thì sẽ xảy ra lần nữa. Đây là lúc duy nhất mọi người đủ động lực để đầu tư phòng ngừa.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Mở tủ lạnh ra xem trong lúc mất điện",
          "viSao": "Phản xạ tự nhiên, ai cũng muốn kiểm tra",
          "caiGia": "Mất phần lớn lượng lạnh tích trong tủ. Rút ngắn thời gian cứu được từ vài giờ xuống còn chưa tới một giờ."
        },
        {
          "loi": "Không ghi mốc thời gian và nhiệt độ",
          "viSao": "Đang cuống, không nghĩ tới việc ghi chép",
          "caiGia": "Sau đó không đánh giá được gì và phải bỏ toàn bộ cho chắc, kể cả phần vẫn còn dùng được."
        },
        {
          "loi": "Chờ thợ tới rồi mới tính chuyện chuyển hàng",
          "viSao": "Nghĩ rằng thợ sẽ sửa nhanh",
          "caiGia": "Vài tiếng chờ đợi là vài tiếng hàng hỏng dần. Cứu hàng luôn đứng trước sửa tủ."
        }
      ]
    }
  },
  {
    "code": "QT-26",
    "title": "MẤT TRỘM, KHÁCH GÂY RỐI, BỊ QUAY VIDEO",
    "flowId": "flow-tinhhuong",
    "flowName": "Tình Huống Bất Ngờ",
    "phase": 3,
    "phaseName": "Đợt 3 - Mở rộng toàn diện",
    "pages": 4,
    "targetRole": "Cả quầy",
    "summary": "Ưu tiên an toàn con người trước tài sản · việc làm ngay và việc làm trong 24 giờ · trích xuất camera · khi nào báo công an.",
    "keyDetail": "Không đuổi theo, không giằng co. Ghi nhớ đặc điểm và giữ nguyên hiện trường quan trọng hơn nhiều so với giữ lại món hàng.",
    "wallChecklist": {
      "title": "BẢNG DÁN TƯỜNG — MẤT TRỘM, KHÁCH GÂY RỐI, BỊ QUAY VIDEO",
      "subtitle": "Dán ở nơi cả quầy nhìn thấy. Đọc trước, vì lúc xảy ra thì phản xạ quyết định tất cả.",
      "sections": [
        {
          "title": "NGUYÊN TẮC SỐ MỘT",
          "items": [
            "AN TOÀN CON NGƯỜI TRƯỚC, TÀI SẢN SAU",
            "KHÔNG đuổi theo · KHÔNG giằng co · KHÔNG chặn đường",
            "Không có món hàng nào đáng đổi bằng một chấn thương",
            "Ghi nhớ đặc điểm quan trọng hơn giữ lại món hàng"
          ]
        },
        {
          "title": "MẤT TRỘM — LÀM NGAY",
          "items": [
            "Không đuổi theo. Quan sát và ghi nhớ: dáng người, quần áo, xe, biển số",
            "Ghi lại giờ chính xác — để trích camera",
            "Kiểm ngay mất gì, mất bao nhiêu",
            "Báo quản lý và chủ cơ sở ngay",
            "Trích xuất camera trong ngày, đừng để dữ liệu bị ghi đè"
          ]
        },
        {
          "title": "KHÁCH GÂY RỐI",
          "items": [
            "Đứng lùi lại, giữ quầy làm khoảng cách giữa mình và khách",
            "Không tranh luận, không chạm vào người khách",
            "Hạ giọng, nói chậm — người kia sẽ hạ theo sau vài câu",
            "Gọi người hỗ trợ: quản lý, hàng xóm, hoặc cơ quan chức năng",
            "Nếu chỉ có một mình: ưu tiên rời khỏi vị trí nguy hiểm"
          ]
        },
        {
          "title": "BỊ QUAY VIDEO",
          "items": [
            "KHÔNG che ống kính · KHÔNG giật máy · KHÔNG quay lưng bỏ đi",
            "Đứng thẳng, nói năng bình thường như đang được ghi hình",
            "Hỏi lịch sự: &quot;Dạ anh chị quay có việc gì em hỗ trợ được không ạ&quot;",
            "Báo quản lý ngay sau đó, chụp lại nếu họ đăng lên mạng",
            "Mọi phản ứng che giấu đều thành bằng chứng chống lại quầy"
          ]
        },
        {
          "title": "SAU MỌI VIỆC",
          "items": [
            "Ghi lại toàn bộ diễn biến khi còn nhớ rõ",
            "Trích và lưu camera đoạn liên quan",
            "Rà lại lỗ hổng: chỗ khuất camera, hàng để hớ hênh, quầy trống người",
            "Nếu cần báo cơ quan chức năng thì giữ nguyên hiện trường"
          ]
        }
      ]
    },
    "sop": {
      "title": "MẤT TRỘM, KHÁCH GÂY RỐI, BỊ QUAY VIDEO",
      "duration": "Ba tình huống có chung một nguyên tắc: an toàn con người đứng trước tài sản. Không có món hàng nào đáng để đánh đổi.",
      "lamDung": "Không ai bị thương. Có đủ đặc điểm và dữ liệu camera nên xử lý được về sau. Quầy không bị biến thành nội dung xấu trên mạng.",
      "lamAu": "Đuổi theo hoặc giằng co là cách một vụ mất vài trăm nghìn trở thành một vụ chấn thương. Che ống kính là tự tạo ra bằng chứng bất lợi cho chính mình.",
      "aiLam": [
        {
          "role": "Người trực quầy",
          "desc": "",
          "step": ""
        },
        {
          "role": "Toàn bộ phần xử lý ngay tại chỗ.",
          "desc": "",
          "step": ""
        },
        {
          "role": "Phần xử lý",
          "desc": "",
          "step": ""
        },
        {
          "role": "Quản lý cửa hàng",
          "desc": "Được gọi ngay. Trích camera, quyết định có báo cơ quan chức năng không.",
          "step": ""
        },
        {
          "role": "Phần sau đó",
          "desc": "",
          "step": ""
        },
        {
          "role": "Chủ cơ sở",
          "desc": "",
          "step": "Quyết định khi có thiệt hại lớn hoặc khi việc lan lên mạng."
        },
        {
          "role": "Khi được báo",
          "desc": "",
          "step": ""
        },
        {
          "role": "Nguyên tắc bao trùm cả ba tình huống: an toàn con người đứng trước tài sản, không có ngoại lệ. Một nhân viên bị thương vì đuổi theo kẻ lấy trộm là tổn thất lớn hơn nhiều lần giá trị món hàng, và là điều người chủ sẽ ân hận rất lâu.",
          "desc": "",
          "step": ""
        }
      ],
      "phases": [
        {
          "title": "MẤT TRỘM",
          "subtitle": "không đuổi theo, quan sát thay vì hành động",
          "steps": [
            {
              "num": "1",
              "action": "Không đuổi theo, không chặn đường, không giằng co.",
              "why": "Người bị dồn vào đường cùng sẽ phản ứng, và bạn không biết họ mang gì trong người. Món hàng không đáng.",
              "tip": ""
            },
            {
              "num": "2",
              "action": "Quan sát và ghi nhớ ngay: dáng người, chiều cao ước lượng, quần áo màu gì, đội mũ gì, đi bộ hay đi xe, loại xe và biển số nếu thấy được.",
              "why": "Đặc điểm nhớ được trong ba mươi giây đầu là thứ có giá trị nhất. Sau mười phút thì trí nhớ bắt đầu tự bịa thêm.",
              "tip": "Ghi ra giấy ngay lập tức, đừng tin vào trí nhớ. Ghi cả những chi tiết thấy vụn vặt."
            },
            {
              "num": "3",
              "action": "Ghi lại giờ chính xác của sự việc.",
              "why": "Đây là thứ để trích camera. Không có giờ thì phải tua cả ngày.",
              "tip": ""
            },
            {
              "num": "4",
              "action": "Kiểm ngay mất gì và mất bao nhiêu.",
              "why": "",
              "tip": ""
            },
            {
              "num": "5",
              "action": "Báo quản lý và chủ cơ sở ngay.",
              "why": "",
              "tip": ""
            },
            {
              "num": "6",
              "action": "Trích xuất đoạn camera liên quan trong ngày, lưu ra chỗ khác.",
              "why": "Camera thường ghi đè sau một số ngày nhất định. Để đến tuần sau mới trích thì dữ liệu đã mất.",
              "tip": "Trích rộng hơn khoảng thời gian bạn nghĩ: lấy từ 15 phút trước tới 15 phút sau."
            },
            {
              "num": "7",
              "action": "Nếu giá trị lớn hoặc có dấu hiệu tái diễn: báo cơ quan chức năng, giữ nguyên hiện trường.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "KHÁCH GÂY RỐI, KHÁCH SAY",
          "subtitle": "giữ khoảng cách và gọi người",
          "steps": [
            {
              "num": "8",
              "action": "Đứng lùi lại, giữ quầy làm khoảng cách vật lý giữa mình và khách.",
              "why": "Quầy là vật cản tự nhiên có sẵn. Đứng sau quầy an toàn hơn nhiều so với bước ra ngoài để nói chuyện.",
              "tip": ""
            },
            {
              "num": "9",
              "action": "Không tranh luận, không chạm vào người khách dù chỉ để can.",
              "why": "Chạm vào người là chuyển tình huống sang một mức hoàn toàn khác, kể cả khi ý định của bạn là tốt.",
              "tip": ""
            },
            {
              "num": "10",
              "action": "Hạ giọng và nói chậm lại.",
              "why": "Người đang kích động sẽ hạ giọng theo người đối diện sau khoảng ba câu. Đây là công cụ hiệu quả nhất và không tốn gì.",
              "tip": ""
            },
            {
              "num": "11",
              "action": "Gọi người hỗ trợ ngay: quản lý, hàng xóm, hoặc cơ quan chức năng nếu có nguy cơ mất an toàn.",
              "why": "Đừng cố tự xử lý một mình để chứng minh mình xử lý được. Gọi sớm luôn tốt hơn gọi muộn.",
              "tip": ""
            },
            {
              "num": "12",
              "action": "Nếu chỉ có một mình và tình huống có nguy cơ: ưu tiên rời khỏi vị trí nguy hiểm, khoá quầy nếu kịp.",
              "why": "Hàng hoá mất đi thì mua lại được.",
              "tip": ""
            },
            {
              "num": "13",
              "action": "Sau khi ổn định: ghi lại diễn biến, trích camera, báo chủ cơ sở.",
              "why": "",
              "tip": ""
            }
          ]
        },
        {
          "title": "BỊ QUAY VIDEO TRONG QUẦY",
          "subtitle": "phản ứng quyết định tất cả",
          "steps": [
            {
              "num": "14",
              "action": "Không che ống kính, không giật máy, không quay lưng bỏ đi.",
              "why": "Mọi hành động che giấu đều trở thành đoạn được cắt ra và đăng lên. Một quầy không làm gì sai mà che ống kính sẽ trông đúng như một quầy đang giấu điều gì đó.",
              "tip": "Đây là chi tiết quan trọng nhất của cả phần này. Phản xạ tự nhiên là che, và đúng cái phản xạ đó gây hại."
            },
            {
              "num": "15",
              "action": "Đứng thẳng, tiếp tục nói năng và làm việc bình thường như đang được ghi hình.",
              "why": "Nếu bạn vẫn làm đúng thì đoạn video đó thực ra có lợi cho bạn.",
              "tip": ""
            },
            {
              "num": "16",
              "action": "Hỏi một câu lịch sự: &quot;Dạ anh chị quay có việc gì em hỗ trợ được không ạ.&quot;",
              "why": "Câu này vừa lịch sự vừa cho thấy bạn không né tránh. Rất nhiều trường hợp người quay dừng lại ngay sau câu này.",
              "tip": ""
            },
            {
              "num": "17",
              "action": "Nếu họ đang phản ánh một vấn đề: chuyển sang QT-16 hoặc QT-17, xử lý nội dung thật thay vì xử lý cái điện thoại.",
              "why": "Người quay video hầu như luôn đang bực về một chuyện gì đó. Giải quyết chuyện đó thì đoạn video mất lý do tồn tại.",
              "tip": ""
            },
            {
              "num": "18",
              "action": "Báo quản lý ngay sau đó. Nếu video được đăng lên mạng: chụp lại toàn bộ, không tranh luận công khai.",
              "why": "Tranh luận dưới bài đăng luôn làm bài đăng lan xa hơn. Liên hệ riêng luôn hiệu quả hơn.",
              "tip": ""
            }
          ]
        },
        {
          "title": "SAU MỌI VIỆC",
          "subtitle": "làm trong ngày",
          "steps": [
            {
              "num": "19",
              "action": "Ghi lại toàn bộ diễn biến khi còn nhớ rõ, càng chi tiết càng tốt.",
              "why": "",
              "tip": ""
            },
            {
              "num": "20",
              "action": "Rà lại lỗ hổng: có chỗ nào khuất camera không, hàng giá trị cao có để hớ hênh không, có lúc nào quầy chỉ có một người không.",
              "why": "Sự việc đã xảy ra là lúc duy nhất mọi người đủ động lực để sửa những chỗ vẫn biết là hở nhưng chưa làm gì.",
              "tip": ""
            },
            {
              "num": "21",
              "action": "Nếu là mất trộm lặp lại: xem lại cách bố trí hàng và vị trí đứng của nhân viên.",
              "why": "Hàng nhỏ giá trị cao để ngay lối ra là mời gọi. Chuyển vào trong tầm mắt là biện pháp rẻ nhất.",
              "tip": ""
            }
          ]
        }
      ],
      "loiHayGap": [
        {
          "loi": "Đuổi theo người lấy trộm",
          "viSao": "Phản xạ tức thời, và tiếc của",
          "caiGia": "Một vụ mất vài trăm nghìn có thể thành một vụ chấn thương. Đây là cái giá không bao giờ đáng."
        },
        {
          "loi": "Che ống kính khi bị quay",
          "viSao": "Phản xạ tự vệ tự nhiên",
          "caiGia": "Tự tạo ra đúng đoạn video bất lợi nhất cho mình. Quầy không sai cũng trông như đang giấu."
        },
        {
          "loi": "Để đến hôm sau mới trích camera",
          "viSao": "Việc đã qua rồi, mai làm cũng được",
          "caiGia": "Camera ghi đè, dữ liệu mất. Đây là lỗi không sửa lại được."
        }
      ]
    }
  }
];
